/**
 * Game Scene: the core gameplay scene.
 *
 * Responsibilities:
 *  - Generate/restore the maze
 *  - Render walls and exit marker
 *  - Manage the player entity and cursor-key input
 *  - Manage fog of war (visibility mask around the player)
 *  - Coordinate HealthSystem and FoodSpawner
 *  - Handle level completion and game saving
 *  - Launch the HUD scene as a parallel overlay
 */

import Phaser from 'phaser';
import {
  SCENE_GAME,
  SCENE_HUD,
  SCENE_GAME_OVER,
  SCENE_TITLE,
  TEX_WALL,
  TEX_WALL_BRICK,
  TEX_WALL_STONE,
  TEX_WALL_WOOD,
  TEX_WALL_METAL,
  TEX_WALL_LATTICE,
  TEX_WALL_RUINS,
  TEX_WALL_MARBLE,
  TEX_WALL_ADOBE,
  TEX_EXIT,
  TEX_GLOW,
  TEX_DUST,
  TEX_PLAYER,
  TEX_SMOKE,
  TEX_LAVA_RIM,
  TEX_POISON_RIM,
  TEX_SPIKE_RIM,
  TEX_BLOOD,
  TEX_LOOT_HEART,
  TEX_LOOT_STAR,
  TEX_LOOT_AGMUTH,
  TEX_LOOT_COIN_BAG,
  TEX_COMPASS,
  TEX_MANUSCRIPT,
  TEX_FOOD_CHICKEN,
  TEX_FOOD_TUNA,
  TEX_COIN,
  TEX_KEY_METALLIC,
  TEX_KEY_COPPER,
  TEX_KEY_GOLD,
  TEX_CHEST_METALLIC,
  TEX_THREIA_TOTEM,
  TEX_HAMMER_OF_THREIA,
  TEX_WALL_DEBRIS,
  TEX_WATER,
  TEX_LAVA,
  TEX_POISON,
  TEX_SPIKE,
  TEX_BAT_WINGS_UP,
  TEX_GOLDEN_EXIT,
  TEX_GOLDEN_EXIT_SPENT,
  SHOP_ITEM_TEXTURES,
  Direction,
  Events,
  CellType,
  KeyTier,
  KEY_TIER_ORDER,
  KEY_CAN_OPEN,
  ChestTier,
  ChestLoot,
  SHOP_ITEM_NAMES,
  SHOP_ITEM_DESCRIPTIONS,
  SHOP_ITEM_CATEGORIES,
  SHOP_ITEM_DURABILITY,
  ItemCategory,
  GameMode,
  LevelEvent,
  LEVEL_EVENT_NAMES,
  LEVEL_EVENT_DESCRIPTIONS,
} from '../utils/constants.js';
import {
  TILE_SIZE,
  GAME_WIDTH,
  GAME_HEIGHT,
  MAX_HEALTH,
  INITIAL_LIVES,
  FOOD_HEAL_AMOUNT,
  FOG_REVEAL_RADIUS,
  FOG_VISITED_ALPHA,
  COLOR_BLACK,
  COLOR_WHITE,
  HUD_HEIGHT,
  BORDER_MARGIN,
  COINS_PER_EXTRA_LIFE,
  AUDIO_GAME_BG,
  AUDIO_GAME_BG_COUNT,
  AUDIO_LEVEL_COMPLETE,
  AUDIO_LEVEL_NEW,
  WATER_DRAIN_MULTIPLIER,
  WATER_IDLE_DRAIN_INTERVAL,
  WATER_IDLE_DRAIN_AMOUNT,
  AUDIO_HEART_LOSS,
  AUDIO_WATER_WALK,
  AUDIO_GAME_END,
  LAVA_DRAIN_MULTIPLIER,
  LAVA_IDLE_DRAIN_INTERVAL,
  LAVA_IDLE_DRAIN_AMOUNT,
  PLAYER_SIZE,
  PLAYER_MOVE_DURATION,
  REST_IDLE_DELAY,
  REST_HEAL_AMOUNT,
  REST_HEAL_INTERVAL,
  REST_Z_MAX_VISIBLE,
  REST_Z_SPAWN_INTERVAL,
  REST_SCORE_COST_PER_HEAL,
  POISON_DURATION,
  POISON_DRAIN_INTERVAL,
  POISON_DRAIN_AMOUNT,
  POISON_SPEED_MULTIPLIER,
  SPIKE_WOUND_DURATION,
  SPIKE_DRAIN_INTERVAL,
  SPIKE_DRAIN_AMOUNT,
  SPIKE_SPEED_MULTIPLIER,
  AUDIO_MENU_CLICK,
  AUDIO_ESC_PRESSED,
  AUDIO_MENU_SELECT,
  AUDIO_RECOVER_LIFE,
  AUDIO_LAVA_WALK,
  AUDIO_SPIKE_WALK,
  AUDIO_POISON_WALK,
  ZOOM_OUT_DURATION,
  ZOOM_IN_DURATION,
  ZOOM_OUT_FOG_VISITED_ALPHA,
  KEY_CHEST_MIN_LEVEL,
  MAX_PLAYER_KEYS,
  CHEST_LOOT_METALLIC,
  CHEST_LOOT_COPPER,
  CHEST_LOOT_GOLD,
  CHEST_COIN_BAG_AMOUNT,
  AGMUTH_MAX_PER_GAME,
  CHEST_OPEN_ZOOM_AMOUNT,
  AUDIO_HEART_COLLECT,
  AUDIO_STAR_COLLECT,
  AUDIO_AGMUTH_COLLECT,
  AUDIO_AGMUTH_USED,
  AUDIO_COIN_BAG_COLLECT,
  COMPASS_SPAWN_CHANCE,
  THREIA_TOTEM_SPAWN_CHANCE,
  THREIAS_GUIDE_REVEAL_CHANCE,
  AGMUTH_PENDANT_BONUS,
  KEY_PENDANT_SPAWN_BONUS,
  TOTEM_VORACIOUS_ATTRACT_RADIUS,
  TOTEM_VORACIOUS_ATTRACT_SPEED,
  SHOP_ITEM_THREIAS_GUIDE,
  SHOP_ITEM_LAVA_RING,
  SHOP_ITEM_POISON_RING,
  SHOP_ITEM_WATER_RING,
  SHOP_ITEM_AGMUTH_PENDANT,
  SHOP_ITEM_KEY_PENDANT,
  SHOP_ITEM_HEART_PENDANT,
  HEART_PENDANT_DRAIN_REDUCTION,
  SHOP_ITEM_ELEMENTAL_RING,
  SHOP_ITEM_TOTEM_VORACIOUS,
  AUDIO_ITEM_BREAK,
  AUDIO_CHEST_LOCKED,
  AUDIO_ZOOM_OUT,
  MANUSCRIPT_SPAWN_CHANCE,
  AUDIO_MANUSCRIPT_COLLECT,
  HAMMER_SPAWN_CHANCE,
  HAMMER_PICKUP_CHARGES,
  AUDIO_HAMMER_COLLECT,
  AUDIO_HAMMER_HIT,
  AUDIO_HAMMER_BREAK,
  AUDIO_WALL_REGEN,
  COLOR_HAMMER_BLUE,
  COLOR_HAMMER_GOLD,
  SHOP_ITEM_HAMMER_OF_THREIA,
  SHOP_ITEM_KEY_METALLIC,
  SHOP_ITEM_KEY_COPPER,
  SHOP_ITEM_KEY_GOLD,
  WALL_THEME_COUNT,
  AUDIO_TEAR_OF_THREIA_USE,
  AUDIO_HEART_POTION_USE,
  COLOR_TEAR_THREIA_GOLD,
  COLOR_POTION_GREEN,
  COLOR_NARRATOR_MONK,
  COLOR_NARRATOR_MONK_NAME,
  COLOR_NARRATOR_VORATH,
  COLOR_NARRATOR_VORATH_NAME,
  COLOR_NARRATOR_VORATH_BG,
  AUDIO_NARRATOR_MONK,
  AUDIO_NARRATOR_VORATH,
  TREASURE_ROOM_LEVEL_INTERVAL,
  TREASURE_ROOM_SPAWN_CHANCE,
  TREASURE_ROOM_FIRST_LEVEL,
  TREASURE_ROOM_TIMER_SECONDS,
  TREASURE_ROOM_WARNING_SECONDS,
  TREASURE_ROOM_COIN_COUNT,
  TREASURE_ROOM_COIN_BAG_COUNT,
  TREASURE_ROOM_MAZE_COLS,
  TREASURE_ROOM_MAZE_ROWS,
  TREASURE_ROOM_MUSIC_COUNT,
  COLOR_GOLDEN_EXIT,
  COLOR_GOLDEN_EXIT_DARK,
  GOLDEN_EXIT_GLOW_PULSE_SPEED,
  GOLDEN_EXIT_SPENT_ALPHA,
  AUDIO_TREASURE_BG,
  AUDIO_TREASURE_TICK,
  AUDIO_TREASURE_ENTER,
  AUDIO_TREASURE_EXIT,
  EXPLORER_FREE_CONTINUES,
  EXPLORER_SCORE_PENALTY,
  EXPLORER_COIN_PENALTY,
  EXPLORER_MIN_COIN_COST,
  LEVEL_EVENT_FOG_RADIUS_BONUS,
  LEVEL_EVENT_FOOD_LIFETIME_MULT,
  LEVEL_EVENT_COIN_MULTIPLIER,
  COLOR_LEVEL_EVENT,
  LEVEL_EVENT_ANNOUNCE_DURATION,
  DIFFICULTY_HARD,
  ACHIEVEMENT_EXPLORER_FOG_PCT,
  ACHIEVEMENT_GHOST_USES,
  ACHIEVEMENT_PRAY_BLESSINGS,
  ACHIEVEMENT_SURVIVOR_LEVEL,
  ACHIEVEMENT_SCHOLAR_MANUSCRIPTS,
  ACHIEVEMENT_HOARDER_COINS,
  ACHIEVEMENT_DESTROYER_WALLS,
  ACHIEVEMENT_TREASURE_HUNTER_CHESTS,
  SHADOW_WALKER_COIN_STEAL,
  SHADOW_WALKER_DAMAGE,
  BAT_NEST_TRAVERSE_DAMAGE_MULT,
  BAT_NEST_TRAVERSE_COIN_MULT,
  TRAIL_LENGTH,
  TRAIL_DOT_SIZE,
  TRAIL_BASE_ALPHA,
  TRAIL_FADE_STEP,
  TRAIL_COLOR,
} from '../config/game-config.js';
import MazeGenerator from '../systems/MazeGenerator.js';
import HealthSystem from '../systems/HealthSystem.js';
import FoodSpawner from '../systems/FoodSpawner.js';
import CoinSpawner from '../systems/CoinSpawner.js';
import ScoreSystem from '../systems/ScoreSystem.js';
import WaterSystem from '../systems/WaterSystem.js';
import LavaSystem from '../systems/LavaSystem.js';
import PoisonSystem from '../systems/PoisonSystem.js';
import SpikeSystem from '../systems/SpikeSystem.js';
import KeySpawner from '../systems/KeySpawner.js';
import ChestSpawner from '../systems/ChestSpawner.js';
import Player from '../entities/Player.js';
import CoinItem from '../entities/CoinItem.js';
import CompassItem from '../entities/CompassItem.js';
import ThreiaTotemItem from '../entities/ThreiaTotemItem.js';
import ManuscriptItem from '../entities/ManuscriptItem.js';
import HammerItem from '../entities/HammerItem.js';
import ThreiaSystem from '../systems/ThreiaSystem.js';
import BatSystem from '../systems/BatSystem.js';
import HammerSystem from '../systems/HammerSystem.js';
import NarratorSystem from '../systems/NarratorSystem.js';
import FinalBattleSystem from '../systems/FinalBattleSystem.js';
import LevelEventSystem from '../systems/LevelEventSystem.js';
import AmbientSoundSystem from '../systems/AmbientSoundSystem.js';
import MovingWallSystem from '../systems/MovingWallSystem.js';
import SecretPassageSystem from '../systems/SecretPassageSystem.js';
import ShadowWalkerSystem from '../systems/ShadowWalkerSystem.js';
import BatNestSystem from '../systems/BatNestSystem.js';
import { saveGame, loadGame, loadMusicPref, saveMusicPref, hasTutorialBeenSeen, saveTutorialSeen, hasTreasureRoomBeenSeen, saveTreasureRoomSeen } from '../utils/save-manager.js';
import { isItemActive, addToWallet, getWallet, degradeItem, isElementalRingActive, getActiveItemsList, getItemCount, consumeItem } from '../systems/ShopManager.js';
import { getMusicVol, getSfxVol, applyMasterMute, refreshConfig, getDifficultyMultiplier, getDifficulty } from '../utils/audio-config.js';
import { pickUncollectedManuscript, collectManuscript } from '../utils/manuscript-manager.js';
import { unlockAchievement, isAchievementUnlocked } from '../utils/achievement-manager.js';
import { getCollectedCount as getManuscriptCount } from '../utils/manuscript-manager.js';

export default class GameScene extends Phaser.Scene {
  constructor() {
    super({ key: SCENE_GAME });
  }

  // ---------------------------------------------------------------
  // Phaser lifecycle
  // ---------------------------------------------------------------

  /**
   * @param {{ continuing: boolean, gameMode?: string }} data
   */
  init(data) {
    this.continuing = data.continuing || false;
    /** @type {string} Game mode for this session (classic or explorer). */
    this._initGameMode = data.gameMode || null; // stored here, applied in _startNewGame / _restoreFromSave
  }

  create() {
    this.cameras.main.setBackgroundColor('#000000');

    // Refresh audio/difficulty config
    refreshConfig();
    applyMasterMute(this.sound);

    /** @type {Phaser.Events.EventEmitter} Shared event bus for inter-system communication. */
    this.eventBus = new Phaser.Events.EventEmitter();

    // Music preference (persisted across sessions)
    this.musicEnabled = loadMusicPref();

    // Background music track rotation — changes every 2 levels
    /** @type {number} Current track number (1–AUDIO_GAME_BG_COUNT). */
    this._gameMusicTrack = Math.floor(Math.random() * AUDIO_GAME_BG_COUNT) + 1;
    /** @type {number} Level at which the track was last selected. */
    this._gameMusicLastChangeLevel = 1;

    // Transition lock — blocks player input during level transitions/announcements
    this._transitionLock = false;

    // Pause state
    this._isPaused = false;
    this._pauseOverlay = null;
    this._pauseMenuTexts = [];
    this._pauseSelectorArrow = null;
    this._pauseSelectedIndex = 0;
    this._pauseKeyListeners = [];
    this._pauseStartTime = 0;       // Date.now() when pause began
    this._totalPausedMs = 0;         // Accumulated pause time for score timer offset

    // Info modal state (I key)
    /** @type {boolean} Whether the info modal is currently visible. */
    this._isInfoOpen = false;
    /** @type {Phaser.GameObjects.Rectangle|null} Info modal dark overlay. */
    this._infoOverlay = null;
    /** @type {Phaser.GameObjects.GameObject[]} All info modal UI elements for cleanup. */
    this._infoElements = [];
    /** @type {Function[]} Info modal keyboard listener removers. */
    this._infoKeyListeners = [];
    /** @type {number} Current scroll offset for info modal content. */
    this._infoScrollY = 0;
    /** @type {number} Maximum scroll offset for info modal content. */
    this._infoMaxScrollY = 0;
    /** @type {Phaser.GameObjects.Container|null} Scrollable content container. */
    this._infoContainer = null;
    /** @type {Phaser.Display.Masks.GeometryMask|null} Mask for scroll area. */
    this._infoMask = null;
    /** @type {Phaser.GameObjects.Graphics|null} Mask shape graphics. */
    this._infoMaskGraphics = null;

    // Water walk sound state
    this._waterWalkSound = null;

    // Hazard idle drain state — passive damage while standing still on water/lava
    /** @type {boolean} Whether the player is currently standing on a water tile. */
    this._onWaterTile = false;
    /** @type {Phaser.Time.TimerEvent|null} Repeating timer for idle water drain. */
    this._waterIdleDrainTimer = null;
    /** @type {boolean} Whether the player is currently standing on a lava tile. */
    this._onLavaTile = false;
    /** @type {Phaser.Time.TimerEvent|null} Repeating timer for idle lava drain. */
    this._lavaIdleDrainTimer = null;

    // Ring glow tracking — used to emit events only on transitions
    /** @private */ this._waterRingGlowActive = false;
    /** @private */ this._lavaRingGlowActive = false;
    /** @private */ this._poisonRingGlowActive = false;
    /** @private */ this._elementalRingGlowActive = false;

    // Lava walk sound state
    this._lavaWalkSound = null;

    // Spike walk sound state
    this._spikeWalkSound = null;

    // Poison walk sound state
    this._poisonWalkSound = null;

    // Lava visual effects — burning rim sprite and smoke emitter
    /** @type {Phaser.GameObjects.Sprite|null} */
    this._lavaRimSprite = null;
    /** @type {Phaser.Tweens.Tween|null} */
    this._lavaRimTween = null;
    /** @type {Phaser.GameObjects.Particles.ParticleEmitter|null} */
    this._lavaSmokeEmitter = null;

    // Idle rest / sleep state
    this._lastMoveTime = Date.now();
    this._isResting = false;
    /** @type {Phaser.Time.TimerEvent|null} */
    this._restHealTimer = null;
    /** @type {Phaser.Time.TimerEvent|null} */
    this._restZTimer = null;
    /** @type {Phaser.GameObjects.Text[]} Active floating Z letters. */
    this._restZTexts = [];
    /** @type {Phaser.Sound.BaseSound|null} Looping rest heal sound. */
    this._restHealSound = null;
    /** @type {Phaser.Time.TimerEvent|null} Timer for spaced rest sound playback. */
    this._restSoundTimer = null;

    // Poison status effect state
    /** @type {boolean} Whether the player is currently poisoned. */
    this._isPoisoned = false;
    /** @type {Phaser.Time.TimerEvent|null} Repeating timer for passive poison drain. */
    this._poisonDrainTimer = null;
    /** @type {Phaser.Time.TimerEvent|null} Delayed call to end poison effect. */
    this._poisonEndTimer = null;

    // Poison visual effects — green rim sprite
    /** @type {Phaser.GameObjects.Sprite|null} */
    this._poisonRimSprite = null;
    /** @type {Phaser.Tweens.Tween|null} */
    this._poisonRimTween = null;

    // Wounded status effect state (spike hazard)
    /** @type {boolean} Whether the player is currently wounded. */
    this._isWounded = false;
    /** @type {Phaser.Time.TimerEvent|null} Repeating timer for passive wounded drain. */
    this._woundedDrainTimer = null;
    /** @type {Phaser.Time.TimerEvent|null} Delayed call to end wounded effect. */
    this._woundedEndTimer = null;

    // Wounded visual effects — blood-red rim sprite and blood trail emitter
    /** @type {Phaser.GameObjects.Sprite|null} */
    this._spikeRimSprite = null;
    /** @type {Phaser.Tweens.Tween|null} */
    this._spikeRimTween = null;
    /** @type {Phaser.GameObjects.Particles.ParticleEmitter|null} */
    this._bloodTrailEmitter = null;

    // Zoom-out map view state (Z key)
    /** @type {boolean} Whether the camera is currently zoomed out. */
    this._isZoomedOut = false;
    /** @type {boolean} Whether a zoom transition tween is currently running. */
    this._isZoomTransitioning = false;
    /** @type {Phaser.Tweens.Tween|null} Active zoom tween (in or out). */
    this._zoomTween = null;
    /** @type {Phaser.Input.Keyboard.Key|null} Z key object for polling. */
    this._zoomKey = null;

    // Keys & Chests state
    /** @type {string[]} Array of KeyTier values the player currently holds. */
    this._playerKeys = [];
    /** @type {number} Number of Agmuth items collected this playthrough. */
    this._agmuthCount = 0;
    /** @type {string} Direction the player is currently facing ('up','down','left','right'). */
    this._playerFacing = Direction.DOWN;
    /** @type {boolean} Whether a chest open is currently in progress (prevents double-open). */
    this._chestOpenInProgress = false;
    /** @type {Phaser.Input.Keyboard.Key|null} O key for opening chests. */
    this._openKey = null;

    // Agmuth Ghost Mode state
    /** @type {boolean} Whether Agmuth ghost mode is currently active. */
    this._isGhostMode = false;
    /** @type {Phaser.Input.Keyboard.Key|null} A key for activating Agmuth. */
    this._agmuthKey = null;

    // Compass state
    /** @type {boolean} Whether the player has collected a compass this level. */
    this._hasCompass = false;
    /** @type {CompassItem|null} The compass floor item (if spawned, not yet collected). */
    this._compassItem = null;
    /** @type {Phaser.Physics.Arcade.Group|null} Physics group for the compass item. */
    this._compassGroup = null;
    /** @type {{ item: CompassItem|null }|null} Shared reference for spawner exclusion. */
    this._compassRef = null;

    // Threia Totem & Blessing state
    /** @type {boolean} Whether the player currently holds a Threia Totem. */
    this._hasThreiaTotem = false;
    /** @type {boolean} Whether the blessing has been used on this level. */
    this._threiaBlessingUsed = false;
    /** @type {ThreiaTotemItem|null} The totem floor item (if spawned, not yet collected). */
    this._threiaTotemItem = null;
    /** @type {Phaser.Physics.Arcade.Group|null} Physics group for the totem item. */
    this._threiaTotemGroup = null;
    /** @type {{ item: ThreiaTotemItem|null }|null} Shared reference for spawner exclusion. */
    this._threiaTotemRef = null;
    /** @type {ThreiaSystem|null} The Threia prayer/blessing system. */
    this._threiaSystem = null;

    // Bat system state
    /** @type {BatSystem|null} Ambient bat spawn/flight/attack system. */
    this._batSystem = null;

    // Manuscript state
    /** @type {ManuscriptItem|null} The manuscript floor item (if spawned, not yet collected). */
    this._manuscriptItem = null;
    /** @type {Phaser.Physics.Arcade.Group|null} Physics group for the manuscript item. */
    this._manuscriptGroup = null;
    /** @type {number|null} The manuscript ID spawned this level (null if none). */
    this._manuscriptId = null;

    // Hammer of Threia state
    /** @type {number} Current hammer charges (pickup + shop stacked). */
    this._hammerCharges = 0;
    /** @type {HammerItem|null} The hammer floor item (if spawned, not yet collected). */
    this._hammerItem = null;
    /** @type {Phaser.Physics.Arcade.Group|null} Physics group for the hammer item. */
    this._hammerGroup = null;
    /** @type {HammerSystem|null} The hammer wall-breaking system. */
    this._hammerSystem = null;

    // Vorath's Treasure Room state
    /** @type {boolean} Whether a golden exit exists on the current level. */
    this._hasGoldenExit = false;
    /** @type {{col:number,row:number}|null} Grid position of the golden exit. */
    this._goldenExitPos = null;
    /** @type {Phaser.GameObjects.Sprite|null} The golden exit marker sprite. */
    this._goldenExitMarker = null;
    /** @type {Phaser.Tweens.Tween|null} Golden exit glow tween. */
    this._goldenExitGlowTween = null;
    /** @type {boolean} Whether the golden exit has been used this run (spent). */
    this._goldenExitSpent = false;
    /** @type {boolean} Whether the player is currently in the treasure room. */
    this._inTreasureRoom = false;
    /** @type {number} Remaining seconds on the treasure room timer. */
    this._treasureTimeRemaining = 0;
    /** @type {Phaser.Time.TimerEvent|null} Treasure room countdown timer event. */
    this._treasureTimerEvent = null;
    /** @type {number} Coins collected inside the treasure room (tracked separately). */
    this._treasureCoinsCollected = 0;
    /** @type {Phaser.GameObjects.Text|null} Countdown timer text in treasure room. */
    this._treasureTimerText = null;
    /** @type {Phaser.Sound.BaseSound|null} Treasure room background music. */
    this._treasureMusic = null;
    // Saved state for returning from treasure room
    /** @type {object|null} Snapshot of the normal level state before entering treasure room. */
    this._preTreasureState = null;

    // Final Battle state
    /** @type {FinalBattleSystem|null} The endgame Vorath battle system. */
    this._finalBattleSystem = null;
    /** @type {boolean} Whether the final battle game over is definitive (no continue). */
    this._isFinalBattleGameOver = false;

    // New gameplay systems (Maze Variety + Enemies & Threats)
    /** @type {MovingWallSystem|null} Walls that open/close periodically (level 8+). */
    this._movingWallSystem = null;
    /** @type {SecretPassageSystem|null} Hidden walls opened by pushing 3 times (level 3+). */
    this._secretPassageSystem = null;
    /** @type {ShadowWalkerSystem|null} Patrol enemies that steal coins + deal damage (level 7+). */
    this._shadowWalkerSystem = null;
    /** @type {BatNestSystem|null} Destructible nests that spawn bats on proximity (level 4+). */
    this._batNestSystem = null;

    // State
    this.level = 1;
    this.score = 0;
    this.coinsCollected = 0;

    // Restore or start fresh
    if (this.continuing) {
      this._restoreFromSave();
    } else {
      this._startNewGame();
    }

    // Build visuals
    this._buildMaze();
    this._createPlayer();
    this._createExitMarker();
    this._initWaterSystem();
    this._initLavaSystem();
    this._initPoisonSystem();
    this._initSpikeSystem();
    this._initFog();
    this._revealFog(this.player.gridCol, this.player.gridRow);

    // Systems
    this._initHealthSystem();
    this._initFoodSpawner();
    this._initCoinSpawner();
    this._initChestSpawner();
    this._initKeySpawner();
    this._initManuscript();
    this._initCompass();
    this._initThreiaTotem();
    this._initHammer();
    this._crossLinkSpawners();
    this._initScoreSystem();

    // Input
    this._setupInput();

    // Events
    this._setupEvents();

    // Direction trail graphics (subtle floor dots showing last N positions)
    this._trailGraphics = this.add.graphics();
    this._trailGraphics.setDepth(1); // Below walls (depth 2)
    this._drawTrail();

    // Threia prayer/blessing system
    this._threiaSystem = new ThreiaSystem(this, this.eventBus, this.healthSystem, this.player);
    this._threiaSystem.setHasTotem(this._hasThreiaTotem);
    this._threiaSystem.setBlessingUsed(this._threiaBlessingUsed);

    // Bat system — ambient bats that spawn as player reveals new fog cells
    this._batSystem = new BatSystem(
      this,
      this.eventBus,
      this.player,
      this.visited,
      this.mazeGridWidth,
      this.mazeGridHeight,
      this.level,
      () => this._hasThreiaTotem,
      this.healthSystem,
    );

    // Hammer system — B-key combo to break walls
    this._hammerSystem = new HammerSystem(this, this.eventBus, this.player);
    this._hammerSystem.setCharges(this._hammerCharges);

    // Final Battle system — endgame sequence when all 4 Agmuth relics are collected
    this._finalBattleSystem = new FinalBattleSystem({
      scene: this,
      eventBus: this.eventBus,
      healthSystem: this.healthSystem,
    });

    // Restore broken walls from save (must happen after HammerSystem is created)
    if (this._savedBrokenWalls && this._savedBrokenWalls.length > 0) {
      this._hammerSystem.restoreBrokenWalls(this._savedBrokenWalls);
      this._savedBrokenWalls = null;
    }

    // Apply level event effects to spawners (for restored games with active events)
    this._applyLevelEventEffects();

    // Ambient sound system — contextual environmental audio
    this._ambientSoundSystem = new AmbientSoundSystem({ scene: this, level: this.level });
    this._ambientSoundSystem.start();

    // --- New gameplay systems (Maze Variety + Enemies & Threats) ---

    // Moving walls: walls that open/close periodically (level 8+)
    this._movingWallSystem = new MovingWallSystem(
      this, this.eventBus, this.mazeGrid, this.level,
      this.startPos, this.exitPos, this.player, this.mazeSeed,
    );

    // Secret passages: hidden walls opened by pushing 3 times (level 3+)
    this._secretPassageSystem = new SecretPassageSystem(
      this, this.eventBus, this.mazeGrid, this.level,
      this.startPos, this.exitPos, this.mazeSeed,
    );

    // Shadow Walkers: patrol enemies that steal coins + deal damage (level 7+)
    this._shadowWalkerSystem = new ShadowWalkerSystem(
      this, this.eventBus, this.mazeGrid, this.level,
      this.player, this.startPos, this.healthSystem,
      () => this._isGhostMode,
    );

    // Bat nests: destructible nests that spawn bats on proximity (level 4+)
    this._batNestSystem = new BatNestSystem(
      this, this.eventBus, this.mazeGrid, this.player,
      this.visited, this.level, this.startPos, this.exitPos,
      this.healthSystem, () => this._hasThreiaTotem,
    );

    // Restore new system states from save (must happen after construction)
    if (this.continuing && this._savedNewSystems) {
      if (this._savedNewSystems.movingWalls) {
        this._movingWallSystem.restore(this._savedNewSystems.movingWalls);
      }
      if (this._savedNewSystems.secretPassages) {
        this._secretPassageSystem.restore(this._savedNewSystems.secretPassages);
      }
      if (this._savedNewSystems.batNests) {
        this._batNestSystem.restore(this._savedNewSystems.batNests);
      }
      this._savedNewSystems = null;
    }

    // Launch HUD overlay
    this.scene.launch(SCENE_HUD, {
      eventBus: this.eventBus,
      health: this.healthSystem.health,
      maxHealth: this.healthSystem.maxHealth,
      lives: this.healthSystem.lives,
      score: this.score,
      coins: this.coinsCollected,
      level: this.level,
      musicEnabled: this.musicEnabled,
      playerKeys: this._playerKeys,
      agmuthCount: this._agmuthCount,
      hasCompass: this._hasCompass,
      hasThreiaTotem: this._hasThreiaTotem,
      hammerCharges: this._hammerCharges,
      exitPos: this.exitPos,
      minimapVisible: this._minimapVisible,
    });

    // Start game background music
    this._startGameMusic();

    // Center camera on maze
    this._setupCamera();

    // Threia's Guide: chance to reveal the entire maze this level
    this._tryThreiasGuideReveal();

    // Golden exit / Vorath's Treasure Room — spawn check for the initial level
    // (_buildNextLevel handles this for subsequent levels, but level 1 and
    // restored saves need it here)
    if (this._hasGoldenExit && this._goldenExitPos) {
      // Restored from save — recreate the visual marker
      this._createGoldenExitMarker(this._goldenExitPos);
    } else {
      this._trySpawnGoldenExit();
    }

    // Show level announcement with disintegration effect
    this._showLevelAnnouncement(this.level);

    // Register shutdown with Phaser's scene lifecycle as a safety net.
    // GameScene already calls _cleanup() manually before transitions, but this
    // ensures cleanup runs even if the scene is stopped externally.
    this.events.on('shutdown', this._onShutdown, this);
  }

  update() {
    // Guard: player is null during the level-transition delay
    if (!this.player) return;

    // Skip gameplay updates when paused or info modal is open
    if (this._isPaused || this._isInfoOpen) return;

    // Zoom-out map view: hold Z to zoom out, release to zoom back in.
    // Blocked during transitions (level announcements, game over) and final battle.
    const isFinalBattleActive = this._finalBattleSystem && this._finalBattleSystem.isActive;
    if (this._zoomKey && !this._transitionLock && !isFinalBattleActive) {
      if (this._zoomKey.isDown && !this._isZoomedOut && !this._isZoomTransitioning) {
        this._startZoomOut();
      } else if (!this._zoomKey.isDown && this._isZoomedOut) {
        this._stopZoomOut();
      }
    }

    // Block input during level transitions/announcements AND while zoomed out
    if (!this._transitionLock && !this._isZoomedOut && !this._isZoomTransitioning) {
      this._handleInput();
    }

    // Let the player manage sound + particle state each frame
    this.player.updatePlayer();

    // Update ghost mode halo position each frame
    if (this.player && this.player.isGhostMode) {
      this.player.updateGhostHalo();
    }

    // Update Threia prayer glow position each frame
    if (this._threiaSystem) {
      this._threiaSystem.update();
    }

    // Keep the lava rim sprite centred on the player each frame
    if (this._lavaRimSprite && this.player) {
      this._lavaRimSprite.setPosition(this.player.x, this.player.y);
    }

    // Keep the poison rim sprite centred on the player each frame
    if (this._poisonRimSprite && this.player) {
      this._poisonRimSprite.setPosition(this.player.x, this.player.y);
    }

    // Keep the spike/wounded rim sprite centred on the player each frame
    if (this._spikeRimSprite && this.player) {
      this._spikeRimSprite.setPosition(this.player.x, this.player.y);
    }

    // Toggle blood trail emitter based on player movement while wounded
    if (this._bloodTrailEmitter && this.player) {
      this._bloodTrailEmitter.setPosition(this.player.x, this.player.y);
      if (this.player.isMoving && this._isWounded) {
        this._bloodTrailEmitter.emitting = true;
      } else {
        this._bloodTrailEmitter.emitting = false;
      }
    }

    // Stop water walk sound when the player is no longer moving
    // (idle drain handles damage while standing still)
    if (!this.player.isMoving && this._waterWalkSound) {
      this._stopWaterWalkSound();
    }

    // Stop lava walk sound when the player is no longer moving
    // (but keep visual effects active while standing on lava)
    if (!this.player.isMoving && this._lavaWalkSound) {
      this._stopLavaWalkSound();
    }

    // Stop spike walk sound when the player is no longer moving
    if (!this.player.isMoving && this._spikeWalkSound) {
      this._stopSpikeWalkSound();
    }

    // Stop poison walk sound when the player is no longer moving
    if (!this.player.isMoving && this._poisonWalkSound) {
      this._stopPoisonWalkSound();
    }

    if (this.foodSpawner) {
      this.foodSpawner.update();
    }
    if (this.coinSpawner) {
      this.coinSpawner.update();
    }
    if (this.keySpawner) {
      this.keySpawner.update();
    }

    // Totem of the Voracious: attract nearby food items toward the player
    this._updateVoraciousAttraction();

    // Update bat system (flight, attack, despawn)
    if (this._batSystem) {
      this._batSystem.update(0, this.game.loop.delta);
    }

    // Update shadow walker system (patrol, chase, despawn)
    if (this._shadowWalkerSystem) {
      this._shadowWalkerSystem.update(0, this.game.loop.delta);
    }

    // Update bat nest system (proximity checks, spawned bat animation)
    if (this._batNestSystem) {
      this._batNestSystem.update(0, this.game.loop.delta);
    }

    // Update final battle dark spheres (collision with walls and player)
    if (this._finalBattleSystem && this._finalBattleSystem.isActive) {
      this._finalBattleSystem.updateSpheres();
    }

    // Idle rest detection: if player hasn't moved for REST_IDLE_DELAY,
    // and is not already resting, health is below max, has score to spend,
    // and is NOT standing on a hazard tile (water, lava, or poisoned).
    if (
      !this._isResting
      && !this._transitionLock
      && !this.player.isMoving
      && !this._onWaterTile
      && !this._onLavaTile
      && !this._isPoisoned
      && !this._isWounded
      && this.healthSystem
      && this.healthSystem.health < this.healthSystem.maxHealth
      && this.scoreSystem
      && this.scoreSystem.getScore() > 0
      && (Date.now() - this._lastMoveTime) >= REST_IDLE_DELAY
    ) {
      this._startResting();
    }

    // If already resting and health is now full, stop resting
    if (
      this._isResting
      && this.healthSystem
      && this.healthSystem.health >= this.healthSystem.maxHealth
    ) {
      this._stopResting();
    }

    // If already resting and score ran out, stop resting
    if (
      this._isResting
      && this.scoreSystem
      && this.scoreSystem.getScore() <= 0
    ) {
      this._stopResting();
    }
  }

  // ---------------------------------------------------------------
  // Initialization helpers
  // ---------------------------------------------------------------

  /** @private Start a brand new game at level 1. */
  _startNewGame() {
    this.level = 1;
    this.score = 0;
    this.coinsCollected = 0;
    this._savedHealth = MAX_HEALTH;
    this._savedLives = INITIAL_LIVES;
    this._playerKeys = [];

    // Deliver purchased shop keys to player inventory (consumed on delivery)
    const shopKeyItems = [
      { itemId: SHOP_ITEM_KEY_METALLIC, tier: KeyTier.METALLIC },
      { itemId: SHOP_ITEM_KEY_COPPER, tier: KeyTier.COPPER },
      { itemId: SHOP_ITEM_KEY_GOLD, tier: KeyTier.GOLD },
    ];
    for (const { itemId, tier } of shopKeyItems) {
      const qty = getItemCount(itemId);
      for (let i = 0; i < qty; i++) {
        if (this._playerKeys.length < MAX_PLAYER_KEYS) {
          this._playerKeys.push(tier);
          consumeItem(itemId);
        }
      }
    }

    this._agmuthCount = 0;
    this._isGhostMode = false;
    this._hasCompass = false;
    this._hasThreiaTotem = false;
    this._threiaBlessingUsed = false;
    this._prevWallThemeIdx = undefined; // Reset wall theme history for new game

    // Game mode (classic or explorer) — set from init data or default to classic
    this._gameMode = this._initGameMode || GameMode.CLASSIC;
    this._explorerContinuesUsed = 0;

    // Reset golden exit / treasure room state for new game
    this._hasGoldenExit = false;
    this._goldenExitPos = null;
    this._goldenExitSpent = false;
    this._inTreasureRoom = false;

    // Reset final battle state for new game
    this._isFinalBattleGameOver = false;

    // Track total play time for records
    this._gameStartTime = Date.now();
    this._gameElapsedBeforeSave = 0;

    // Per-run achievement tracking counters
    this._achGhostUses = 0;        // Agmuth ghost mode uses this run
    this._achWallsBroken = 0;      // Walls broken this run
    this._achChestsOpened = 0;     // Chests opened this run
    this._achBlessingsTotal = 0;   // Threia blessings (loaded from localStorage if continuing)

    // Mini-map visibility state (toggled via TAB key)
    this._minimapVisible = false;

    // Direction trail (last N player positions for orientation)
    this._playerTrail = [];

    // Initialize narrator system (fresh for new game)
    this._narratorSystem = new NarratorSystem();

    // Initialize level event system (fresh for new game)
    this._levelEventSystem = new LevelEventSystem({ scene: this, eventBus: this.eventBus });

    const dims = MazeGenerator.dimensionsForLevel(this.level);
    const gen = new MazeGenerator(dims.cols, dims.rows);
    const result = gen.generate();

    this.mazeGrid = result.grid;
    this.mazeGridWidth = result.gridWidth;
    this.mazeGridHeight = result.gridHeight;
    this.mazeSeed = result.seed;
    this.startPos = result.start;
    this.exitPos = result.exit;
    this.playerStartCol = result.start.col;
    this.playerStartRow = result.start.row;

    this._initVisited();
  }

  /** @private Restore state from a saved game. */
  _restoreFromSave() {
    const save = loadGame();
    if (!save) {
      // Fallback to new game if save is corrupted
      this._startNewGame();
      return;
    }

    this.level = save.level || 1;
    this.score = save.score || 0;
    this.coinsCollected = save.coinsCollected || 0;
    this._savedHealth = save.health || MAX_HEALTH;
    this._savedLives = save.lives || INITIAL_LIVES;
    this._playerKeys = save.playerKeys || [];
    this._agmuthCount = save.agmuthCount || 0;
    this._isGhostMode = false; // Ghost mode is never saved — always starts inactive
    this._hasCompass = save.hasCompass || false;
    this._hasThreiaTotem = save.hasThreiaTotem || false;
    this._threiaBlessingUsed = save.threiaBlessingUsed || false;
    this._savedChests = save.chests || null;
    this._prevWallThemeIdx = undefined; // Reset wall theme history on restore
    this._hammerCharges = save.hammerCharges || 0;
    this._savedBrokenWalls = save.brokenWalls || null;

    // Restore game mode — prefer save data (for Continue), fallback to init data or classic
    this._gameMode = save.gameMode || this._initGameMode || GameMode.CLASSIC;
    this._explorerContinuesUsed = save.explorerContinuesUsed || 0;

    // Restore golden exit state
    this._hasGoldenExit = save.hasGoldenExit || false;
    this._goldenExitPos = save.goldenExitPos || null;
    this._goldenExitSpent = save.goldenExitSpent || false;
    this._inTreasureRoom = false; // Never saved — always starts outside

    // Restore narrator system state
    this._narratorSystem = new NarratorSystem({
      monkIndex: save.narratorMonkIndex || 0,
      vorathIndex: save.narratorVorathIndex || 0,
      monkNextLevel: save.narratorMonkNextLevel || 0,
      vorathNextLevel: save.narratorVorathNextLevel || 0,
    });

    // Restore level event system
    this._levelEventSystem = new LevelEventSystem({ scene: this, eventBus: this.eventBus });
    this._levelEventSystem.setEvent(save.levelEvent || LevelEvent.NONE);

    // Restore play-time tracking for records
    this._gameStartTime = Date.now();
    this._gameElapsedBeforeSave = save.gameElapsedTime || 0;

    // Restore per-run achievement tracking counters
    this._achGhostUses = save.achGhostUses || 0;
    this._achWallsBroken = save.achWallsBroken || 0;
    this._achChestsOpened = save.achChestsOpened || 0;
    this._achBlessingsTotal = save.achBlessingsTotal || 0;

    // Mini-map visibility state (toggled via TAB key)
    this._minimapVisible = save.minimapVisible || false;

    // Direction trail (restored from save or empty for fresh level)
    this._playerTrail = save.playerTrail || [];

    // Stash new system save data — systems are constructed later in create()
    // and restored from this temporary field.
    this._savedNewSystems = {
      movingWalls: save.movingWalls || null,
      secretPassages: save.secretPassages || null,
      batNests: save.batNests || null,
    };

    // Regenerate the same maze from seed
    const dims = MazeGenerator.dimensionsForLevel(this.level);
    const gen = new MazeGenerator(dims.cols, dims.rows, save.mazeSeed);
    const result = gen.generate();

    this.mazeGrid = result.grid;
    this.mazeGridWidth = result.gridWidth;
    this.mazeGridHeight = result.gridHeight;
    this.mazeSeed = result.seed;
    this.startPos = result.start;
    this.exitPos = result.exit;
    this.playerStartCol = save.playerCol ?? result.start.col;
    this.playerStartRow = save.playerRow ?? result.start.row;

    // Restore visited map
    if (save.visited) {
      this.visited = save.visited;
    } else {
      this._initVisited();
    }
  }

  /** @private Initialize the visited cells array (for fog of war). */
  _initVisited() {
    this.visited = [];
    for (let r = 0; r < this.mazeGridHeight; r++) {
      this.visited.push(new Array(this.mazeGridWidth).fill(0));
    }
  }

  // ---------------------------------------------------------------
  // Maze rendering
  // ---------------------------------------------------------------

  /** @private Draw wall tiles as a static tilemap layer. */
  _buildMaze() {
    // Group for wall sprites (static for performance)
    this.wallGroup = this.physics.add.staticGroup();

    // Select wall texture based on level theme (cycles through 4 themes)
    const wallTexKey = this._getWallTextureForLevel(this.level);
    /** @type {string} Cached wall texture key for the current level. */
    this._currentWallTexture = wallTexKey;

    for (let row = 0; row < this.mazeGridHeight; row++) {
      for (let col = 0; col < this.mazeGridWidth; col++) {
        if (this.mazeGrid[row][col] === CellType.WALL) {
          const x = col * TILE_SIZE + TILE_SIZE / 2;
          const y = row * TILE_SIZE + TILE_SIZE / 2;
          const wall = this.wallGroup.create(x, y, wallTexKey);
          wall.setOrigin(0.5);
          wall.refreshBody();
        }
      }
    }
  }

  /**
   * Return the wall texture key for a given level.
   * Theme is selected pseudo-randomly using the maze seed so that:
   *   - The choice is deterministic (reproducible for save/restore).
   *   - The order feels unpredictable to the player.
   *   - The same theme never appears on two consecutive levels.
   * @private
   * @param {number} level - Current game level (1-based).
   * @returns {string} Texture key to use for walls.
   */
  _getWallTextureForLevel(level) {
    const WALL_TEXTURES = [
      TEX_WALL, TEX_WALL_BRICK, TEX_WALL_STONE, TEX_WALL_WOOD,
      TEX_WALL_METAL, TEX_WALL_LATTICE, TEX_WALL_RUINS, TEX_WALL_MARBLE,
      TEX_WALL_ADOBE,
    ];

    // Simple hash: use mazeSeed (available before _buildMaze) to pick index.
    // mazeSeed is already a different random number for every level.
    const seed = this.mazeSeed || 0;
    let idx = Math.abs(seed) % WALL_THEME_COUNT;

    // Avoid repeating the same theme as the previous level.
    if (this._prevWallThemeIdx !== undefined && idx === this._prevWallThemeIdx) {
      idx = (idx + 1) % WALL_THEME_COUNT;
    }
    this._prevWallThemeIdx = idx;

    return WALL_TEXTURES[idx];
  }

  /** @private Place the exit marker sprite. */
  _createExitMarker() {
    const x = this.exitPos.col * TILE_SIZE + TILE_SIZE / 2;
    const y = this.exitPos.row * TILE_SIZE + TILE_SIZE / 2;

    this.exitMarker = this.add.sprite(x, y, TEX_EXIT);
    this.exitMarker.setOrigin(0.5);
    this.exitMarker.setDepth(5); // Above hazard floor tiles (depth 2)

    // Pulsing animation for the exit
    this.tweens.add({
      targets: this.exitMarker,
      alpha: 0.4,
      duration: 800,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    // Golden exit: either restore from save or try to spawn for first time
    if (this._hasGoldenExit && this._goldenExitPos) {
      this._createGoldenExitMarker(this._goldenExitPos);
    }
  }

  // ---------------------------------------------------------------
  // Vorath's Treasure Room
  // ---------------------------------------------------------------

  /**
   * Check whether a golden exit portal should appear this level.
   * Called after building each new level (inside _buildNextLevel).
   * The portal spawns every TREASURE_ROOM_LEVEL_INTERVAL levels
   * (3, 6, 9 …) with TREASURE_ROOM_SPAWN_CHANCE probability,
   * starting from TREASURE_ROOM_FIRST_LEVEL. If the golden exit is
   * already "spent" for this run, the portal still shows but in its
   * deteriorated form and is non-functional.
   * @private
   */
  _trySpawnGoldenExit() {
    // Only eligible at intervals of TREASURE_ROOM_LEVEL_INTERVAL, starting
    // from TREASURE_ROOM_FIRST_LEVEL
    if (this.level < TREASURE_ROOM_FIRST_LEVEL) return;
    if (this.level % TREASURE_ROOM_LEVEL_INTERVAL !== 0) return;

    // Random chance
    if (Math.random() > TREASURE_ROOM_SPAWN_CHANCE) return;

    // Pick a walkable PATH cell for the golden exit, different from
    // normal exit and start positions.
    // IMPORTANT: only consider logical cells (both col and row are odd in the
    // expanded grid) so the portal never appears in a narrow corridor passage
    // between two walls — only in actual "room" cells.
    const pathCells = MazeGenerator.getPathCells(this.mazeGrid);
    const forbidden = new Set([
      `${this.startPos.col},${this.startPos.row}`,
      `${this.exitPos.col},${this.exitPos.row}`,
    ]);

    const candidates = pathCells.filter(
      (c) =>
        c.col % 2 === 1 &&
        c.row % 2 === 1 &&
        !forbidden.has(`${c.col},${c.row}`),
    );
    if (candidates.length === 0) return;

    // Pick a cell at least some distance from the exit (prefer variety)
    const exitCol = this.exitPos.col;
    const exitRow = this.exitPos.row;
    // Sort by Manhattan distance to exit descending, then pick from the
    // top quarter so the golden exit tends to be far from the normal exit
    candidates.sort((a, b) => {
      const da = Math.abs(a.col - exitCol) + Math.abs(a.row - exitRow);
      const db = Math.abs(b.col - exitCol) + Math.abs(b.row - exitRow);
      return db - da;
    });
    const topN = Math.max(1, Math.floor(candidates.length * 0.25));
    const chosen = candidates[Math.floor(Math.random() * topN)];

    this._hasGoldenExit = true;
    this._goldenExitPos = { col: chosen.col, row: chosen.row };
    this._createGoldenExitMarker(this._goldenExitPos);
  }

  /**
   * Create the golden exit marker sprite at the given grid position.
   * If the golden exit has been "spent" this run, uses the deteriorated
   * texture and no glow.
   * @private
   * @param {{ col: number, row: number }} pos
   */
  _createGoldenExitMarker(pos) {
    const x = pos.col * TILE_SIZE + TILE_SIZE / 2;
    const y = pos.row * TILE_SIZE + TILE_SIZE / 2;

    const texKey = this._goldenExitSpent
      ? TEX_GOLDEN_EXIT_SPENT
      : TEX_GOLDEN_EXIT;

    this._goldenExitMarker = this.add.sprite(x, y, texKey);
    this._goldenExitMarker.setOrigin(0.5);
    this._goldenExitMarker.setDepth(5);

    if (this._goldenExitSpent) {
      this._goldenExitMarker.setAlpha(GOLDEN_EXIT_SPENT_ALPHA);
    } else {
      // Pulsing golden glow animation
      this._goldenExitGlowTween = this.tweens.add({
        targets: this._goldenExitMarker,
        alpha: 0.4,
        duration: GOLDEN_EXIT_GLOW_PULSE_SPEED,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    }
  }

  /**
   * Destroy the golden exit marker sprite and its glow tween.
   * @private
   */
  _destroyGoldenExitMarker() {
    if (this._goldenExitGlowTween) {
      this._goldenExitGlowTween.stop();
      this._goldenExitGlowTween = null;
    }
    if (this._goldenExitMarker) {
      this._goldenExitMarker.destroy();
      this._goldenExitMarker = null;
    }
  }

  /**
   * Enter Vorath's Treasure Room.
   * Snapshots the current level state, plays an entrance effect, shows
   * the first-time lore modal (once ever), then builds the bonus maze.
   * @private
   */
  _enterTreasureRoom() {
    // Prevent re-entry
    if (this._inTreasureRoom) return;

    // Lock transitions
    this._transitionLock = true;

    // Snapshot the full current level so we can restore after
    this._snapshotLevelState();

    // Play entrance sound
    try {
      this.sound.play(AUDIO_TREASURE_ENTER, { volume: getSfxVol(0.65) });
    } catch (_) {}

    // Stop current game music
    this._stopGameMusic();

    // Golden flash transition effect
    const cam = this.cameras.main;
    const centerX = cam.scrollX + cam.width / 2;
    const centerY = cam.scrollY + cam.height / 2;
    const flash = this.add.rectangle(
      centerX, centerY,
      cam.width * 2, cam.height * 2,
      0xffd700, 0,
    );
    flash.setDepth(350);

    this.tweens.add({
      targets: flash,
      alpha: 1,
      duration: 500,
      ease: 'Quad.easeIn',
      onComplete: () => {
        // Check if this is the first time ever seeing treasure room
        if (!hasTreasureRoomBeenSeen()) {
          // Show lore modal first, then build treasure room
          this._showTreasureRoomIntroModal(() => {
            flash.destroy();
            this._buildTreasureRoom();
          });
        } else {
          flash.destroy();
          this._buildTreasureRoom();
        }
      },
    });
  }

  /**
   * Capture all mutable state for the current normal level so the
   * treasure room can be built in-place and then restored afterwards.
   * @private
   */
  _snapshotLevelState() {
    const pos = this.player.getGridPosition();
    this._preTreasureState = {
      level: this.level,
      mazeSeed: this.mazeSeed,
      mazeGrid: this.mazeGrid,
      mazeGridWidth: this.mazeGridWidth,
      mazeGridHeight: this.mazeGridHeight,
      startPos: { ...this.startPos },
      exitPos: { ...this.exitPos },
      playerCol: pos.col,
      playerRow: pos.row,
      health: this.healthSystem.health,
      lives: this.healthSystem.lives,
      score: this.scoreSystem ? this.scoreSystem.getScore() : 0,
      coinsCollected: this.coinsCollected,
      visited: this.visited ? this.visited.map((r) => [...r]) : null,
      playerKeys: [...this._playerKeys],
      agmuthCount: this._agmuthCount,
      hasCompass: this._hasCompass,
      hasThreiaTotem: this._hasThreiaTotem,
      threiaBlessingUsed: this._threiaBlessingUsed,
      hammerCharges: this._hammerCharges,
      chests: this.chestSpawner ? this.chestSpawner.serialize() : [],
      brokenWalls: this._hammerSystem
        ? this._hammerSystem.getBrokenWallsForSave()
        : [],
      narratorState: this._narratorSystem
        ? this._narratorSystem.serialize()
        : {},
      hasGoldenExit: this._hasGoldenExit,
      goldenExitPos: this._goldenExitPos
        ? { ...this._goldenExitPos }
        : null,
    };
  }

  /**
   * Show the one-time lore introduction modal for Vorath's Treasure Room.
   * Uses the Vorath visual style (dark red overlay, red text, italic).
   * @private
   * @param {Function} callback - Called after player dismisses the modal
   */
  _showTreasureRoomIntroModal(callback) {
    saveTreasureRoomSeen();

    const cam = this.cameras.main;
    const centerX = cam.scrollX + cam.width / 2;
    const centerY = cam.scrollY + cam.height / 2;

    const elements = [];

    // Dark red overlay
    const overlay = this.add.rectangle(
      centerX, centerY,
      cam.width * 2, cam.height * 2,
      COLOR_NARRATOR_VORATH_BG, 0.92,
    );
    overlay.setDepth(400);
    overlay.setAlpha(0);
    elements.push(overlay);

    // Title
    const title = this.add.text(centerX, centerY - 100, "Vorath's Vault", {
      fontFamily: '"Courier New", Courier, monospace',
      fontSize: '28px',
      fontStyle: 'bold italic',
      color: Phaser.Display.Color.IntegerToColor(COLOR_NARRATOR_VORATH_NAME)
        .rgba,
    });
    title.setOrigin(0.5);
    title.setDepth(401);
    title.setAlpha(0);
    elements.push(title);

    // Lore body text
    const loreText = [
      'You have found a crack in the labyrinth —',
      'a golden portal shimmering with stolen light.',
      '',
      'Beyond it lies Vorath\'s hoard: a vault filled',
      'with coins stolen from the bodies of those',
      'who perished in these corridors, collected',
      'by his minions over countless ages.',
      '',
      'Enter quickly. Gather what you can.',
      'When time runs out, Vorath will cast you back.',
    ].join('\n');

    const body = this.add.text(centerX, centerY + 20, loreText, {
      fontFamily: '"Courier New", Courier, monospace',
      fontSize: '14px',
      fontStyle: 'italic',
      color: Phaser.Display.Color.IntegerToColor(COLOR_NARRATOR_VORATH).rgba,
      align: 'center',
      lineSpacing: 6,
      wordWrap: { width: 500 },
    });
    body.setOrigin(0.5);
    body.setDepth(401);
    body.setAlpha(0);
    elements.push(body);

    // Dismiss hint
    const hint = this.add.text(
      centerX,
      centerY + 160,
      '[Press ENTER or SPACE to enter the vault]',
      {
        fontFamily: '"Courier New", Courier, monospace',
        fontSize: '12px',
        color: '#888888',
      },
    );
    hint.setOrigin(0.5);
    hint.setDepth(401);
    hint.setAlpha(0);
    elements.push(hint);

    // Fade in all elements
    for (const el of elements) {
      this.tweens.add({
        targets: el,
        alpha: el === overlay ? 0.92 : 1,
        duration: 400,
        ease: 'Quad.easeOut',
      });
    }

    // Delayed key binding to prevent accidental dismiss
    this.time.delayedCall(600, () => {
      if (!this.scene) return;
      const dismiss = () => {
        this.input.keyboard.off('keydown-ENTER', dismiss);
        this.input.keyboard.off('keydown-SPACE', dismiss);
        this.input.keyboard.off('keydown-ESC', dismiss);

        // Fade out and destroy
        for (const el of elements) {
          this.tweens.add({
            targets: el,
            alpha: 0,
            duration: 300,
            ease: 'Quad.easeIn',
            onComplete: () => el.destroy(),
          });
        }

        this.time.delayedCall(350, () => {
          if (callback) callback();
        });
      };
      this.input.keyboard.on('keydown-ENTER', dismiss);
      this.input.keyboard.on('keydown-SPACE', dismiss);
      this.input.keyboard.on('keydown-ESC', dismiss);
    });
  }

  /**
   * Build the treasure room: a small maze filled with coins and coin bags.
   * Tears down the current level, generates a new 15×13 maze, places only
   * coins and coin bags (no food, hazards, keys, chests, bats, etc.),
   * starts treasure music and the countdown timer.
   * @private
   */
  _buildTreasureRoom() {
    // Mark that we're in the treasure room
    this._inTreasureRoom = true;
    this._treasureCoinsCollected = 0;

    // Full cleanup of the current level visuals and systems
    this._cleanup();

    // Generate a small maze (level-1 size)
    const gen = new MazeGenerator(
      TREASURE_ROOM_MAZE_COLS,
      TREASURE_ROOM_MAZE_ROWS,
    );
    const result = gen.generate();

    this.mazeGrid = result.grid;
    this.mazeGridWidth = result.gridWidth;
    this.mazeGridHeight = result.gridHeight;
    this.mazeSeed = result.seed;
    this.startPos = result.start;
    this.exitPos = result.exit; // Not used, but keep consistent

    this._initVisited();

    // Build maze walls (no exit marker in treasure room)
    this._buildMaze();

    // Create the player at the start position
    this.player = new Player(
      this,
      result.start.col,
      result.start.row,
      this.mazeGrid,
      this.eventBus,
    );

    // No hazard systems in the treasure room
    this.waterSystem = null;
    this.lavaSystem = null;
    this.poisonSystem = null;
    this.spikeSystem = null;

    // Init fog as normal
    this._initFog();
    this._revealFog(this.player.gridCol, this.player.gridRow);

    // Create loot group for coin bags BEFORE filling so _spawnTreasureCoinBag
    // adds bags to this group (not a throwaway one).
    this.lootGroup = this.physics.add.group();

    // Place coins and coin bags throughout the maze
    this._fillTreasureCoins(result.grid, result.start);

    // Set up coin overlap (coins are in coinGroup, created inside _fillTreasureCoins)
    this.physics.add.overlap(
      this.player,
      this.coinGroup,
      this._onCoinCollected,
      null,
      this,
    );

    // Set up loot overlap for coin bags (already in this.lootGroup)
    this.physics.add.overlap(
      this.player,
      this.lootGroup,
      this._onTreasureLootCollected,
      null,
      this,
    );

    // Set up camera
    this._setupCamera();

    // Re-register movement keyboard listeners (removed by _cleanup)
    this._resubscribeKeyboardListeners();

    // Start treasure music
    this._startTreasureMusic();

    // Reset idle timer
    this._lastMoveTime = Date.now();

    // Show "VORATH'S VAULT" announcement then start timer
    this._showTreasureAnnouncement();
  }

  /**
   * Fill the treasure room maze with coins and coin bags.
   * Uses Fisher-Yates shuffle on path cells to distribute items evenly.
   * @private
   * @param {number[][]} grid - The maze grid
   * @param {{ col: number, row: number }} start - Player start position
   */
  _fillTreasureCoins(grid, start) {
    const pathCells = MazeGenerator.getPathCells(grid);
    const startKey = `${start.col},${start.row}`;

    // Exclude the start cell
    const available = pathCells.filter(
      (c) => `${c.col},${c.row}` !== startKey,
    );

    // Fisher-Yates shuffle
    for (let i = available.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [available[i], available[j]] = [available[j], available[i]];
    }

    // First TREASURE_ROOM_COIN_BAG_COUNT cells get coin bags
    const bagCount = Math.min(
      TREASURE_ROOM_COIN_BAG_COUNT,
      available.length,
    );
    for (let i = 0; i < bagCount; i++) {
      const cell = available[i];
      const x = cell.col * TILE_SIZE + TILE_SIZE / 2;
      const y = cell.row * TILE_SIZE + TILE_SIZE / 2;
      this._spawnTreasureCoinBag(x, y);
    }

    // Remaining cells get individual coins (up to TREASURE_ROOM_COIN_COUNT)
    this.coinGroup = this.physics.add.group();
    const coinCount = Math.min(
      TREASURE_ROOM_COIN_COUNT,
      available.length - bagCount,
    );
    for (let i = 0; i < coinCount; i++) {
      const cell = available[bagCount + i];
      const x = cell.col * TILE_SIZE + TILE_SIZE / 2;
      const y = cell.row * TILE_SIZE + TILE_SIZE / 2;
      // Very long lifetime so coins don't despawn during the 45s timer
      const coin = new CoinItem(this, x, y, 600000);
      this.coinGroup.add(coin);
    }
  }

  /**
   * Spawn a coin bag loot item in the treasure room.
   * @private
   * @param {number} x - World X position
   * @param {number} y - World Y position
   */
  _spawnTreasureCoinBag(x, y) {
    const bag = this.add.sprite(x, y, TEX_LOOT_COIN_BAG);
    bag.setOrigin(0.5);
    bag.setDepth(10);
    bag.setData('lootType', ChestLoot.COIN_BAG);

    // Enable physics body for overlap detection
    this.physics.world.enable(bag);
    bag.body.setImmovable(true);

    // Pop-in animation
    bag.setScale(0);
    this.tweens.add({
      targets: bag,
      scaleX: 1,
      scaleY: 1,
      duration: 300,
      ease: 'Back.easeOut',
    });

    // Idle pulsing glow
    this.tweens.add({
      targets: bag,
      alpha: 0.6,
      duration: 1000,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    // Add to loot group (created in _buildTreasureRoom before this call)
    this.lootGroup.add(bag);
  }

  /**
   * Handle collecting a loot item in the treasure room (coin bags).
   * @private
   * @param {Player} player
   * @param {Phaser.GameObjects.Sprite} lootItem
   */
  _onTreasureLootCollected(player, lootItem) {
    if (!lootItem.active) return;

    // Immediately deactivate to prevent double-collect
    lootItem.active = false;

    const lootType = lootItem.getData('lootType');
    if (lootType === ChestLoot.COIN_BAG) {
      const x = lootItem.x;
      const y = lootItem.y;
      this._collectLootCoinBag(lootItem, x, y);
    }
  }

  /**
   * Start one of the treasure room music tracks (random selection).
   * @private
   */
  _startTreasureMusic() {
    this._stopTreasureMusic();
    const trackNum = Math.floor(Math.random() * TREASURE_ROOM_MUSIC_COUNT) + 1;
    const key = `${AUDIO_TREASURE_BG}-${trackNum}`;
    try {
      this._treasureMusic = this.sound.add(key, {
        volume: getMusicVol(0.45),
        loop: true,
      });
      this._treasureMusic.play();
    } catch (_) {
      this._treasureMusic = null;
    }
  }

  /**
   * Stop and destroy the treasure room music.
   * @private
   */
  _stopTreasureMusic() {
    if (this._treasureMusic) {
      try { this._treasureMusic.stop(); } catch (_) {}
      try { this._treasureMusic.destroy(); } catch (_) {}
      this._treasureMusic = null;
    }
  }

  /**
   * Start the treasure room countdown timer.
   * Creates a camera-fixed timer text and a repeating 1-second event.
   * @private
   */
  _startTreasureTimer() {
    this._treasureTimeRemaining = TREASURE_ROOM_TIMER_SECONDS;

    // Camera-fixed timer text at top-center of the viewport
    const cam = this.cameras.main;
    const textX = cam.scrollX + cam.width / 2;
    const textY = cam.scrollY + 20;

    this._treasureTimerText = this.add.text(
      textX,
      textY,
      this._formatTreasureTime(this._treasureTimeRemaining),
      {
        fontFamily: '"Courier New", Courier, monospace',
        fontSize: '24px',
        fontStyle: 'bold',
        color: '#ffffff',
      },
    );
    this._treasureTimerText.setOrigin(0.5);
    this._treasureTimerText.setDepth(250);
    this._treasureTimerText.setScrollFactor(0);

    // 1-second repeating timer
    this._treasureTimerEvent = this.time.addEvent({
      delay: 1000,
      callback: this._onTreasureTimerTick,
      callbackScope: this,
      loop: true,
    });
  }

  /**
   * Format seconds into MM:SS display string.
   * @private
   * @param {number} seconds
   * @returns {string}
   */
  _formatTreasureTime(seconds) {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  }

  /**
   * Called every second by the treasure timer.
   * Decrements time, updates display, triggers warning effects in the
   * last TREASURE_ROOM_WARNING_SECONDS, and exits when time reaches 0.
   * @private
   */
  _onTreasureTimerTick() {
    this._treasureTimeRemaining -= 1;

    if (this._treasureTimerText) {
      this._treasureTimerText.setText(
        this._formatTreasureTime(this._treasureTimeRemaining),
      );
    }

    // Emit timer update event
    if (this.eventBus) {
      this.eventBus.emit(
        Events.TREASURE_ROOM_TIMER_UPDATE,
        this._treasureTimeRemaining,
      );
    }

    // Warning phase: last N seconds — flash red + tick sound
    if (
      this._treasureTimeRemaining > 0 &&
      this._treasureTimeRemaining <= TREASURE_ROOM_WARNING_SECONDS
    ) {
      // Emit warning event
      if (this.eventBus) {
        this.eventBus.emit(
          Events.TREASURE_ROOM_WARNING,
          this._treasureTimeRemaining,
        );
      }

      // Flash timer text red briefly
      if (this._treasureTimerText) {
        this._treasureTimerText.setColor('#ff0000');
        this.time.delayedCall(500, () => {
          if (this._treasureTimerText) {
            this._treasureTimerText.setColor('#ffffff');
          }
        });
      }

      // Play tick sound
      try {
        this.sound.play(AUDIO_TREASURE_TICK, { volume: getSfxVol(0.5) });
      } catch (_) {}
    }

    // Time's up
    if (this._treasureTimeRemaining <= 0) {
      this._exitTreasureRoom();
    }
  }

  /**
   * Display a brief "VORATH'S VAULT" announcement overlay in the
   * treasure room, similar to the level announcement. Starts the
   * countdown timer after the announcement fades.
   * @private
   */
  _showTreasureAnnouncement() {
    this._transitionLock = true;

    const cam = this.cameras.main;
    const centerX = cam.scrollX + cam.width / 2;
    const centerY = cam.scrollY + cam.height / 2;

    // Dark overlay
    const overlay = this.add.rectangle(
      centerX, centerY,
      cam.width * 2, cam.height * 2,
      0x000000, 0.7,
    );
    overlay.setDepth(199);
    overlay.setAlpha(0);

    // Title text
    const text = this.add.text(centerX, centerY - 20, "VORATH'S VAULT", {
      fontFamily: '"Courier New", Courier, monospace',
      fontSize: '32px',
      fontStyle: 'bold italic',
      color: '#ffd700',
    });
    text.setOrigin(0.5);
    text.setDepth(200);
    text.setAlpha(0);

    // Subtitle with timer info
    const sub = this.add.text(
      centerX,
      centerY + 20,
      `${TREASURE_ROOM_TIMER_SECONDS} seconds — collect all you can`,
      {
        fontFamily: '"Courier New", Courier, monospace',
        fontSize: '14px',
        color: '#cccccc',
      },
    );
    sub.setOrigin(0.5);
    sub.setDepth(200);
    sub.setAlpha(0);

    // Fade in
    this.tweens.add({
      targets: overlay,
      alpha: 0.7,
      duration: 300,
      ease: 'Quad.easeOut',
    });

    this.tweens.add({
      targets: [text, sub],
      alpha: 1,
      duration: 300,
      ease: 'Quad.easeOut',
      onComplete: () => {
        // Hold briefly then fade out
        this.time.delayedCall(1200, () => {
          if (!this.scene) return;
          this.tweens.add({
            targets: [overlay, text, sub],
            alpha: 0,
            duration: 400,
            ease: 'Quad.easeIn',
            onComplete: () => {
              overlay.destroy();
              text.destroy();
              sub.destroy();

              // Start the countdown timer
              this._startTreasureTimer();

              // Unlock input
              this._transitionLock = false;
            },
          });
        });
      },
    });
  }

  /**
   * Exit the treasure room. Stops the timer, plays exit effects,
   * restores the original level, marks the golden exit as spent,
   * and shows the expulsion modal.
   * @private
   */
  _exitTreasureRoom() {
    if (!this._inTreasureRoom) return;

    // Prevent repeated calls
    this._inTreasureRoom = false;
    this._transitionLock = true;

    // Stop timer
    if (this._treasureTimerEvent) {
      this._treasureTimerEvent.remove(false);
      this._treasureTimerEvent = null;
    }

    // Destroy timer text
    if (this._treasureTimerText) {
      this._treasureTimerText.destroy();
      this._treasureTimerText = null;
    }

    // Stop treasure music
    this._stopTreasureMusic();

    // Play exit sound
    try {
      this.sound.play(AUDIO_TREASURE_EXIT, { volume: getSfxVol(0.65) });
    } catch (_) {}

    // Capture collected coins before restoring
    const coinsGathered = this._treasureCoinsCollected;

    // White flash transition
    const cam = this.cameras.main;
    const centerX = cam.scrollX + cam.width / 2;
    const centerY = cam.scrollY + cam.height / 2;
    const flash = this.add.rectangle(
      centerX, centerY,
      cam.width * 2, cam.height * 2,
      0xffffff, 0,
    );
    flash.setDepth(350);

    this.tweens.add({
      targets: flash,
      alpha: 1,
      duration: 400,
      ease: 'Quad.easeIn',
      onComplete: () => {
        // Full cleanup of the treasure room
        this._cleanup();

        // Restore the normal level from snapshot
        this._restoreLevelFromSnapshot();

        // Mark golden exit as spent
        this._goldenExitSpent = true;

        // Rebuild the golden exit marker with spent texture
        this._destroyGoldenExitMarker();
        if (this._goldenExitPos) {
          this._createGoldenExitMarker(this._goldenExitPos);
        }

        // Fade out flash
        this.tweens.add({
          targets: flash,
          alpha: 0,
          duration: 500,
          ease: 'Quad.easeOut',
          onComplete: () => {
            flash.destroy();
            // Show the expulsion modal
            this._showTreasureExpulsionModal(coinsGathered);
          },
        });
      },
    });
  }

  /**
   * Restore the full level state from the snapshot taken before entering
   * the treasure room. Rebuilds the maze, player, all systems, and
   * re-emits HUD events to sync the overlay.
   * @private
   */
  _restoreLevelFromSnapshot() {
    const s = this._preTreasureState;
    if (!s) return;

    // Restore maze
    this.level = s.level;
    this.mazeSeed = s.mazeSeed;
    this.mazeGrid = s.mazeGrid;
    this.mazeGridWidth = s.mazeGridWidth;
    this.mazeGridHeight = s.mazeGridHeight;
    this.startPos = { ...s.startPos };
    this.exitPos = { ...s.exitPos };

    // Restore visited map — _initVisited() creates a blank array as fallback,
    // then we overwrite with the snapshotted state if available.
    this._initVisited();
    if (s.visited) {
      this.visited = s.visited.map((r) => [...r]);
    }

    // Rebuild maze visuals
    this._buildMaze();
    this._createExitMarker();

    // Create player at the position where they entered the golden exit
    this.player = new Player(
      this,
      s.playerCol,
      s.playerRow,
      this.mazeGrid,
      this.eventBus,
    );

    // Restore golden exit state
    this._hasGoldenExit = s.hasGoldenExit;
    this._goldenExitPos = s.goldenExitPos
      ? { ...s.goldenExitPos }
      : null;

    // Restore game state — coins earned in treasure room are KEPT
    // (they were added to coinsCollected as they were picked up)
    this.coinsCollected = s.coinsCollected + this._treasureCoinsCollected;
    this._playerKeys = [...s.playerKeys];
    this._agmuthCount = s.agmuthCount;
    this._hasCompass = s.hasCompass;
    this._hasThreiaTotem = s.hasThreiaTotem;
    this._threiaBlessingUsed = s.threiaBlessingUsed;
    this._hammerCharges = s.hammerCharges;

    // Restore health and lives
    if (this.healthSystem) {
      this.healthSystem.health = s.health;
      this.healthSystem.lives = s.lives;
    }

    // Restore score
    if (this.scoreSystem) {
      this.scoreSystem._score = s.score;
    }

    // Rebuild all systems
    this._initWaterSystem();
    this._initLavaSystem();
    this._initPoisonSystem();
    this._initSpikeSystem();
    this._initFog();
    this._revealFog(this.player.gridCol, this.player.gridRow);

    // Rebuild food spawner
    this.foodGroup = this.physics.add.group();
    const forbiddenCells = this._getCombinedForbiddenCells();
    this.foodSpawner = new FoodSpawner(
      this,
      this.mazeGrid,
      this.foodGroup,
      { col: s.playerCol, row: s.playerRow },
      this.exitPos,
      forbiddenCells,
      this.player,
    );
    this.foodSpawner.start();

    this.physics.add.overlap(
      this.player,
      this.foodGroup,
      this._onFoodCollected,
      null,
      this,
    );

    // Rebuild coin spawner
    this.coinGroup = this.physics.add.group();
    this.coinSpawner = new CoinSpawner(
      this,
      this.mazeGrid,
      this.coinGroup,
      { col: s.playerCol, row: s.playerRow },
      this.exitPos,
      forbiddenCells,
      this.player,
    );
    this.coinSpawner.start();

    this.physics.add.overlap(
      this.player,
      this.coinGroup,
      this._onCoinCollected,
      null,
      this,
    );

    // Rebuild key, chest, manuscript, compass, totem, hammer
    this._initChestSpawner(s.chests);
    this._initKeySpawner();
    this._initManuscript();
    this._initCompass();
    this._initThreiaTotem();
    this._initHammer();
    this._crossLinkSpawners();

    // Rebuild Threia system
    this._threiaSystem = new ThreiaSystem(
      this,
      this.eventBus,
      this.healthSystem,
      this.player,
    );
    this._threiaSystem.setHasTotem(this._hasThreiaTotem);
    this._threiaSystem.setBlessingUsed(this._threiaBlessingUsed);

    // Rebuild bat system
    if (this._batSystem) {
      this._batSystem.destroy();
    }
    this._batSystem = new BatSystem(
      this,
      this.eventBus,
      this.player,
      this.visited,
      this.mazeGridWidth,
      this.mazeGridHeight,
      this.level,
      () => this._hasThreiaTotem,
      this.healthSystem,
    );

    // Rebuild hammer system
    this._hammerSystem = new HammerSystem(this, this.eventBus, this.player);
    this._hammerSystem.setCharges(this._hammerCharges);
    // Restore broken walls from snapshot
    if (s.brokenWalls && s.brokenWalls.length > 0) {
      this._hammerSystem.restoreBrokenWalls(s.brokenWalls, this.mazeGrid);
    }

    // Restore narrator system state from snapshot
    if (s.narratorState) {
      this._narratorSystem = new NarratorSystem({
        monkIndex: s.narratorState.narratorMonkIndex || 0,
        vorathIndex: s.narratorState.narratorVorathIndex || 0,
        monkNextLevel: s.narratorState.narratorMonkNextLevel || 0,
        vorathNextLevel: s.narratorState.narratorVorathNextLevel || 0,
      });
    }

    // Reset level timer for score bonus
    if (this.scoreSystem) {
      this.scoreSystem.resetLevelTimer();
    }

    // Recreate ambient sound system after treasure room exit
    this._ambientSoundSystem = new AmbientSoundSystem({ scene: this, level: this.level });
    this._ambientSoundSystem.start();

    // Set up camera
    this._setupCamera();

    // Re-register keyboard listeners
    this._resubscribeKeyboardListeners();

    // Restart game music
    this._startGameMusic();

    // Reset idle timer
    this._lastMoveTime = Date.now();

    // Re-emit all HUD state so the overlay syncs
    this.eventBus.emit(Events.HEALTH_CHANGED, this.healthSystem.health);
    this.eventBus.emit(Events.LIFE_GAINED, this.healthSystem.lives);
    this.eventBus.emit(Events.COINS_CHANGED, this.coinsCollected);
    this.eventBus.emit(Events.SCORE_CHANGED, this.scoreSystem ? this.scoreSystem.getScore() : 0);
    this.eventBus.emit(Events.LEVEL_CHANGED, this.level);
    this.eventBus.emit(Events.KEYS_CHANGED, this._playerKeys);
    this.eventBus.emit(Events.AGMUTH_CHANGED, this._agmuthCount);

    // Save after restore
    this._autoSave();

    // Clear the snapshot
    this._preTreasureState = null;
  }

  /**
   * Show the Vorath expulsion modal after exiting the treasure room.
   * Uses the Vorath visual style with a random expulsion message and
   * the count of coins collected.
   * @private
   * @param {number} coinsCollected - Coins gathered during the treasure room
   */
  _showTreasureExpulsionModal(coinsCollected) {
    const messages = [
      'Enough! You have stolen from my vault\nfor the last time, worm.',
      'Begone! Every coin you clutch will weigh\nupon your soul.',
      'You dare touch what is mine?\nI cast you back to rot in my corridors!',
      'Thief! My minions will reclaim\nwhat you have taken — and more.',
      'Flee with your trinkets.\nThe labyrinth remembers every debt.',
    ];
    const msg = messages[Math.floor(Math.random() * messages.length)];

    const cam = this.cameras.main;
    const centerX = cam.scrollX + cam.width / 2;
    const centerY = cam.scrollY + cam.height / 2;

    const elements = [];

    // Dark red overlay
    const overlay = this.add.rectangle(
      centerX, centerY,
      cam.width * 2, cam.height * 2,
      COLOR_NARRATOR_VORATH_BG, 0.92,
    );
    overlay.setDepth(400);
    overlay.setAlpha(0);
    elements.push(overlay);

    // Vorath name
    const name = this.add.text(centerX, centerY - 80, 'Vorath', {
      fontFamily: '"Courier New", Courier, monospace',
      fontSize: '22px',
      fontStyle: 'bold italic',
      color: Phaser.Display.Color.IntegerToColor(COLOR_NARRATOR_VORATH_NAME)
        .rgba,
    });
    name.setOrigin(0.5);
    name.setDepth(401);
    name.setAlpha(0);
    elements.push(name);

    // Expulsion message
    const body = this.add.text(centerX, centerY, msg, {
      fontFamily: '"Courier New", Courier, monospace',
      fontSize: '14px',
      fontStyle: 'italic',
      color: Phaser.Display.Color.IntegerToColor(COLOR_NARRATOR_VORATH).rgba,
      align: 'center',
      lineSpacing: 6,
      wordWrap: { width: 500 },
    });
    body.setOrigin(0.5);
    body.setDepth(401);
    body.setAlpha(0);
    elements.push(body);

    // Coins collected summary
    const coinText = this.add.text(
      centerX,
      centerY + 60,
      `Coins plundered: ${coinsCollected}`,
      {
        fontFamily: '"Courier New", Courier, monospace',
        fontSize: '16px',
        fontStyle: 'bold',
        color: '#ffd700',
      },
    );
    coinText.setOrigin(0.5);
    coinText.setDepth(401);
    coinText.setAlpha(0);
    elements.push(coinText);

    // Dismiss hint
    const hint = this.add.text(
      centerX,
      centerY + 110,
      '[Press ENTER or SPACE to continue]',
      {
        fontFamily: '"Courier New", Courier, monospace',
        fontSize: '12px',
        color: '#888888',
      },
    );
    hint.setOrigin(0.5);
    hint.setDepth(401);
    hint.setAlpha(0);
    elements.push(hint);

    // Fade in
    for (const el of elements) {
      this.tweens.add({
        targets: el,
        alpha: el === overlay ? 0.92 : 1,
        duration: 400,
        ease: 'Quad.easeOut',
      });
    }

    // Delayed key binding
    this.time.delayedCall(600, () => {
      if (!this.scene) return;
      const dismiss = () => {
        this.input.keyboard.off('keydown-ENTER', dismiss);
        this.input.keyboard.off('keydown-SPACE', dismiss);
        this.input.keyboard.off('keydown-ESC', dismiss);

        for (const el of elements) {
          this.tweens.add({
            targets: el,
            alpha: 0,
            duration: 300,
            ease: 'Quad.easeIn',
            onComplete: () => el.destroy(),
          });
        }

        this.time.delayedCall(350, () => {
          // Unlock input
          this._transitionLock = false;
        });
      };
      this.input.keyboard.on('keydown-ENTER', dismiss);
      this.input.keyboard.on('keydown-SPACE', dismiss);
      this.input.keyboard.on('keydown-ESC', dismiss);
    });
  }

  // ---------------------------------------------------------------
  // Player
  // ---------------------------------------------------------------

  /** @private */
  _createPlayer() {
    this.player = new Player(
      this,
      this.playerStartCol,
      this.playerStartRow,
      this.mazeGrid,
      this.eventBus,
    );
  }

  // ---------------------------------------------------------------
  // Fog of War
  // ---------------------------------------------------------------

  /** @private Create the fog overlay using a RenderTexture. */
  _initFog() {
    const mazePixelW = this.mazeGridWidth * TILE_SIZE;
    const mazePixelH = this.mazeGridHeight * TILE_SIZE;

    // Dark overlay covering the entire maze
    this.fogGraphics = this.add.graphics();
    this.fogGraphics.setDepth(100);

    this._drawFullFog();
  }

  /**
   * Reveal cells around a given grid position.
   * @private
   * @param {number} col
   * @param {number} row
   */
  _revealFog(col, row) {
    // "Threia's Light" event: expanded fog reveal radius
    let radius = (this._levelEventSystem && this._levelEventSystem.isActive(LevelEvent.THREIAS_LIGHT))
      ? FOG_REVEAL_RADIUS + LEVEL_EVENT_FOG_RADIUS_BONUS
      : FOG_REVEAL_RADIUS;

    for (let dy = -radius; dy <= radius; dy++) {
      for (let dx = -radius; dx <= radius; dx++) {
        const c = col + dx;
        const r = row + dy;

        if (r < 0 || r >= this.mazeGridHeight || c < 0 || c >= this.mazeGridWidth) {
          continue;
        }

        // Circular radius check
        if (dx * dx + dy * dy <= radius * radius) {
          this.visited[r][c] = 1;
        }
      }
    }

    this._drawFullFog();
  }

  /**
   * Count how many cells within the fog reveal radius are currently unseen.
   * Called BEFORE _revealFog() to detect truly new cell reveals (for bat spawning).
   * @private
   * @param {number} col
   * @param {number} row
   * @returns {number} Number of cells that would be newly revealed.
   */
  _countNewFogCells(col, row) {
    const radius = (this._levelEventSystem && this._levelEventSystem.isActive(LevelEvent.THREIAS_LIGHT))
      ? FOG_REVEAL_RADIUS + LEVEL_EVENT_FOG_RADIUS_BONUS
      : FOG_REVEAL_RADIUS;
    let count = 0;

    for (let dy = -radius; dy <= radius; dy++) {
      for (let dx = -radius; dx <= radius; dx++) {
        const c = col + dx;
        const r = row + dy;

        if (r < 0 || r >= this.mazeGridHeight || c < 0 || c >= this.mazeGridWidth) {
          continue;
        }

        if (dx * dx + dy * dy <= radius * radius) {
          if (!this.visited[r][c]) {
            count++;
          }
        }
      }
    }

    return count;
  }

  /**
   * Mark every cell in the maze as visited, fully removing the fog of war.
   * Used by Threia's Guide shop item to reveal the entire labyrinth.
   * @private
   */
  _revealAllFog() {
    for (let r = 0; r < this.mazeGridHeight; r++) {
      for (let c = 0; c < this.mazeGridWidth; c++) {
        this.visited[r][c] = 1;
      }
    }
    this._drawFullFog();
  }

  // ---------------------------------------------------------------
  // Direction Trail
  // ---------------------------------------------------------------

  /**
   * Append the player's current grid position to the trail and redraw.
   * Keeps only the last TRAIL_LENGTH entries.
   * @private
   * @param {number} col
   * @param {number} row
   */
  _updateTrail(col, row) {
    // Don't add duplicate if the player hasn't actually moved to a new cell
    const trail = this._playerTrail;
    if (trail.length > 0) {
      const last = trail[trail.length - 1];
      if (last.col === col && last.row === row) return;
    }

    trail.push({ col, row });
    if (trail.length > TRAIL_LENGTH) {
      trail.shift();
    }

    this._drawTrail();
  }

  /**
   * Redraw the direction trail dots on the game floor.
   * Newest position is brightest, oldest is most faded.
   * @private
   */
  _drawTrail() {
    if (!this._trailGraphics) return;
    this._trailGraphics.clear();

    const trail = this._playerTrail;
    if (!trail || trail.length === 0) return;

    for (let i = 0; i < trail.length; i++) {
      // i=0 is oldest, i=trail.length-1 is newest
      const age = trail.length - 1 - i; // 0 = newest, higher = older
      const alpha = Math.max(TRAIL_BASE_ALPHA - age * TRAIL_FADE_STEP, 0.05);

      const px = trail[i].col * TILE_SIZE + TILE_SIZE / 2;
      const py = trail[i].row * TILE_SIZE + TILE_SIZE / 2;

      this._trailGraphics.fillStyle(TRAIL_COLOR, alpha);
      this._trailGraphics.fillCircle(px, py, TRAIL_DOT_SIZE);
    }
  }

  /**
   * Check if Threia's Guide shop item is active, and if so, roll a random
   * chance to reveal the entire maze for this level (removes fog of war).
   * On a successful reveal, consumes 1 durability charge. If the item
   * breaks (no charges left), emits the ITEM_BROKEN event.
   * @private
   */
  _tryThreiasGuideReveal() {
    if (!isItemActive(SHOP_ITEM_THREIAS_GUIDE)) return;
    if (Math.random() < THREIAS_GUIDE_REVEAL_CHANCE) {
      this._revealAllFog();

      // Consume 1 durability charge
      const result = degradeItem(SHOP_ITEM_THREIAS_GUIDE);
      if (this.eventBus) {
        this.eventBus.emit(Events.ITEM_DURABILITY_CHANGED, SHOP_ITEM_THREIAS_GUIDE, result.remaining);
      }
      if (result.broken) {
        this._onItemBroken(SHOP_ITEM_THREIAS_GUIDE);
      }
    }
  }

  /**
   * Totem of the Voracious: each frame, pull food items within the
   * attraction radius directly toward the player in a straight line,
   * passing through walls.  Consumes 1 durability charge per unique
   * food item that first enters the radius.
   *
   * Radius = TOTEM_VORACIOUS_ATTRACT_RADIUS logical tiles.
   * In the expanded grid each logical tile spans 2 grid cells, so
   * the pixel radius is  radius * 2 * TILE_SIZE.
   *
   * @private
   */
  _updateVoraciousAttraction() {
    if (!isItemActive(SHOP_ITEM_TOTEM_VORACIOUS)) return;
    if (!this.player || !this.foodSpawner) return;

    const items = this.foodSpawner.activeItems;
    if (!items || items.length === 0) return;

    const px = this.player.x;
    const py = this.player.y;
    // Radius in logical tiles (each logical tile = 2 expanded-grid cells)
    const radiusPx = TOTEM_VORACIOUS_ATTRACT_RADIUS * 2 * TILE_SIZE;
    const speed = TOTEM_VORACIOUS_ATTRACT_SPEED;
    const dt = this.game.loop.delta / 1000;

    if (!this._voraciousTrackedIds) this._voraciousTrackedIds = new Set();

    for (const item of items) {
      if (!item || !item.active) continue;

      const dx = px - item.x;
      const dy = py - item.y;
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (dist <= 0 || dist > radiusPx) continue;

      // First time this food enters the radius: consume 1 charge
      // and kill the floating tween so it doesn't fight our movement
      const itemUid = item._voraciousUid ||
        (item._voraciousUid = `food_${Date.now()}_${Math.random()}`);
      if (!this._voraciousTrackedIds.has(itemUid)) {
        this._voraciousTrackedIds.add(itemUid);

        // Stop the floating animation so it won't override our position
        if (item._floatTween) {
          item._floatTween.stop();
          item._floatTween = null;
        }

        const result = degradeItem(SHOP_ITEM_TOTEM_VORACIOUS);
        if (this.eventBus) {
          this.eventBus.emit(
            Events.ITEM_DURABILITY_CHANGED,
            SHOP_ITEM_TOTEM_VORACIOUS,
            result.remaining,
          );
        }
        if (result.broken) {
          this._onItemBroken(SHOP_ITEM_TOTEM_VORACIOUS);
          return;
        }
      }

      // Move in a straight line toward the player (ignores walls)
      const step = Math.min(speed * dt, dist);
      item.x += (dx / dist) * step;
      item.y += (dy / dist) * step;

      // Sync the physics body so the overlap collider can trigger collection
      if (item.body) {
        item.body.reset(item.x, item.y);
      }
    }
  }

  /**
   * Emit RING_ACTIVATED / RING_DEACTIVATED events when the player
   * steps on or off a hazard tile while owning the matching ring.
   * This drives the glow effect on the ring icons in the HUD.
   * @private
   * @param {boolean} onWater
   * @param {boolean} onLava
   * @param {boolean} onPoison
   * @param {boolean} hasWaterRing
   * @param {boolean} hasLavaRing
   * @param {boolean} hasPoisonRing
   */
  _emitRingGlowEvents(onWater, onLava, onPoison, hasWaterRing, hasLavaRing, hasPoisonRing) {
    // Water Ring glow
    const waterActive = !!(onWater && hasWaterRing);
    if (waterActive !== this._waterRingGlowActive) {
      this._waterRingGlowActive = waterActive;
      if (waterActive) {
        this.eventBus.emit(Events.RING_ACTIVATED, SHOP_ITEM_WATER_RING);
      } else {
        this.eventBus.emit(Events.RING_DEACTIVATED, SHOP_ITEM_WATER_RING);
      }
    }

    // Lava Ring glow
    const lavaActive = !!(onLava && hasLavaRing);
    if (lavaActive !== this._lavaRingGlowActive) {
      this._lavaRingGlowActive = lavaActive;
      if (lavaActive) {
        this.eventBus.emit(Events.RING_ACTIVATED, SHOP_ITEM_LAVA_RING);
      } else {
        this.eventBus.emit(Events.RING_DEACTIVATED, SHOP_ITEM_LAVA_RING);
      }
    }

    // Poison Ring glow
    const poisonActive = !!(onPoison && hasPoisonRing);
    if (poisonActive !== this._poisonRingGlowActive) {
      this._poisonRingGlowActive = poisonActive;
      if (poisonActive) {
        this.eventBus.emit(Events.RING_ACTIVATED, SHOP_ITEM_POISON_RING);
      } else {
        this.eventBus.emit(Events.RING_DEACTIVATED, SHOP_ITEM_POISON_RING);
      }
    }

    // Elemental Ring glow — activates on any hazard tile
    const hasElementalRing = isItemActive(SHOP_ITEM_ELEMENTAL_RING);
    const elementalActive = !!(hasElementalRing && (onWater || onLava || onPoison));
    if (elementalActive !== this._elementalRingGlowActive) {
      this._elementalRingGlowActive = elementalActive;
      if (elementalActive) {
        this.eventBus.emit(Events.RING_ACTIVATED, SHOP_ITEM_ELEMENTAL_RING);
      } else {
        this.eventBus.emit(Events.RING_DEACTIVATED, SHOP_ITEM_ELEMENTAL_RING);
      }
    }
  }

  /**
   * Degrade the appropriate ring's durability when a hazard tile is stepped on
   * or an idle drain tick occurs. If the Elemental Ring is active, it takes
   * priority and consumes from its charge pool; otherwise the individual ring
   * is degraded.
   *
   * @private
   * @param {boolean} elementalActive — whether the Elemental Ring is currently active
   * @param {string} individualRingId — the individual ring item ID (e.g. SHOP_ITEM_WATER_RING)
   */
  _degradeRingForHazard(elementalActive, individualRingId) {
    const targetItem = elementalActive ? SHOP_ITEM_ELEMENTAL_RING : individualRingId;
    const result = degradeItem(targetItem);
    if (this.eventBus) {
      this.eventBus.emit(Events.ITEM_DURABILITY_CHANGED, targetItem, result.remaining);
    }
    if (result.broken) {
      this._onItemBroken(targetItem);
    }
  }

  /**
   * Handle an item breaking during gameplay. Plays break sound, emits
   * ITEM_BROKEN event, and cleans up any associated tracked state.
   *
   * @private
   * @param {string} itemId — the shop item that just broke
   */
  _onItemBroken(itemId) {
    // Play break sound
    try {
      this.sound.play(AUDIO_ITEM_BREAK, { volume: getSfxVol(0.5) });
    } catch (_) { /* audio fallback */ }

    // Emit event for HUD to show shatter animation
    if (this.eventBus) {
      this.eventBus.emit(Events.ITEM_BROKEN, itemId);
      // Also emit SHOP_ITEMS_CHANGED so HUD refreshes the item strip
      this.eventBus.emit(Events.SHOP_ITEMS_CHANGED);
    }

    // Clean up totem tracking if the voracious totem broke
    if (itemId === SHOP_ITEM_TOTEM_VORACIOUS) {
      this._voraciousTrackedIds = null;
    }
  }

  /**
   * Redraw the fog overlay based on visited state.
   * Unvisited cells are fully black, visited cells outside the current
   * reveal radius are semi-transparent. Cells at the edge of the reveal
   * radius get a gradient alpha for a softer, less grid-like boundary.
   * @private
   */
  _drawFullFog() {
    const g = this.fogGraphics;
    if (!g) return;
    g.clear();

    const playerCol = this.player ? this.player.gridCol : this.playerStartCol;
    const playerRow = this.player ? this.player.gridRow : this.playerStartRow;
    const radius = FOG_REVEAL_RADIUS;
    // Soft-edge zone: cells within (radius) but beyond (radius - gradient) get partial fog
    const gradientRings = 2;
    const innerRadius = Math.max(0, radius - gradientRings);

    // During zoom-out, visited cells are shown more clearly
    const visitedAlpha = this._isZoomedOut
      ? ZOOM_OUT_FOG_VISITED_ALPHA
      : FOG_VISITED_ALPHA;

    for (let row = 0; row < this.mazeGridHeight; row++) {
      for (let col = 0; col < this.mazeGridWidth; col++) {
        const dx = col - playerCol;
        const dy = row - playerRow;
        const distSq = dx * dx + dy * dy;
        const dist = Math.sqrt(distSq);

        if (dist <= innerRadius) {
          // Fully visible — no fog (inner clear zone)
          continue;
        }

        if (dist <= radius) {
          // Gradient zone: partial fog that increases with distance
          // Lerp from 0 (fully visible) at innerRadius to ~0.7 at radius edge
          const t = (dist - innerRadius) / gradientRings;
          const fogAlpha = t * 0.7;
          g.fillStyle(COLOR_BLACK, fogAlpha);
        } else if (this.visited[row][col]) {
          // Previously visited: dim fog (clearer during zoom-out)
          g.fillStyle(COLOR_BLACK, 1 - visitedAlpha);
        } else {
          // Never visited: full darkness
          g.fillStyle(COLOR_BLACK, 1);
        }

        g.fillRect(
          col * TILE_SIZE,
          row * TILE_SIZE,
          TILE_SIZE,
          TILE_SIZE,
        );
      }
    }
  }

  // ---------------------------------------------------------------
  // Health System
  // ---------------------------------------------------------------

  /** @private */
  _initHealthSystem() {
    this.healthSystem = new HealthSystem(
      this.eventBus,
      this._savedHealth,
      this._savedLives,
    );
  }

  // ---------------------------------------------------------------
  // Food Spawner
  // ---------------------------------------------------------------

  /** @private */
  _initFoodSpawner() {
    this.foodGroup = this.physics.add.group();

    const forbiddenCells = this._getCombinedForbiddenCells();

    this.foodSpawner = new FoodSpawner(
      this,
      this.mazeGrid,
      this.foodGroup,
      { col: this.playerStartCol, row: this.playerStartRow },
      this.exitPos,
      forbiddenCells,
      this.player,
    );

    this.foodSpawner.start();

    // Player <-> food overlap
    this.physics.add.overlap(
      this.player,
      this.foodGroup,
      this._onFoodCollected,
      null,
      this,
    );
  }

  /**
   * Callback when the player overlaps a food item.
   * @private
   * @param {Player} player
   * @param {import('../entities/FoodItem.js').default} food
   */
  _onFoodCollected(player, food) {
    if (!food.active) return;

    food.collect();
    this.healthSystem.heal(food.healAmount || FOOD_HEAL_AMOUNT);
    this.scoreSystem.addFoodPoints();
    this.eventBus.emit(Events.FOOD_COLLECTED);
  }

  // ---------------------------------------------------------------
  // Score System
  // ---------------------------------------------------------------

  /** @private */
  _initScoreSystem() {
    this.scoreSystem = new ScoreSystem(this.eventBus, this.score);
  }

  // ---------------------------------------------------------------
  // Water System
  // ---------------------------------------------------------------

  /**
   * Initialize the water hazard system. Creates water patches on corridors
   * starting from the configured minimum level. Uses the maze seed so
   * water placement is deterministic and reproducible.
   * @private
   */
  _initWaterSystem() {
    this.waterSystem = new WaterSystem(
      this,
      this.mazeGrid,
      this.level,
      this.startPos,
      this.exitPos,
      this.mazeSeed,
    );
  }

  // ---------------------------------------------------------------
  // Lava System
  // ---------------------------------------------------------------

  /**
   * Initialize the lava hazard system. Creates lava patches on corridors
   * starting from the configured minimum level. Uses the maze seed so
   * lava placement is deterministic and reproducible.
   * Must be called AFTER _initWaterSystem() so lava can exclude water cells.
   * @private
   */
  _initLavaSystem() {
    const waterCells = this.waterSystem ? this.waterSystem.waterCells : new Set();
    this.lavaSystem = new LavaSystem(
      this,
      this.mazeGrid,
      this.level,
      this.startPos,
      this.exitPos,
      this.mazeSeed,
      waterCells,
    );
  }

  // ---------------------------------------------------------------
  // Poison System
  // ---------------------------------------------------------------

  /**
   * Initialize the poison hazard system. Creates poison patches on corridors
   * starting from the configured minimum level. Uses the maze seed so
   * poison placement is deterministic and reproducible.
   * Must be called AFTER _initWaterSystem() and _initLavaSystem() so
   * poison can exclude both water and lava cells.
   * @private
   */
  _initPoisonSystem() {
    const waterCells = this.waterSystem ? this.waterSystem.waterCells : new Set();
    const lavaCells = this.lavaSystem ? this.lavaSystem.lavaCells : new Set();
    this.poisonSystem = new PoisonSystem(
      this,
      this.mazeGrid,
      this.level,
      this.startPos,
      this.exitPos,
      this.mazeSeed,
      waterCells,
      lavaCells,
    );
  }

  // ---------------------------------------------------------------
  // Spike System
  // ---------------------------------------------------------------

  /**
   * Initialize the spike hazard system. Creates spike patches on corridors
   * starting from the configured minimum level. Uses the maze seed so
   * spike placement is deterministic and reproducible.
   * Must be called AFTER water, lava, and poison systems so spikes can
   * exclude all their cells.
   * @private
   */
  _initSpikeSystem() {
    const waterCells = this.waterSystem ? this.waterSystem.waterCells : new Set();
    const lavaCells = this.lavaSystem ? this.lavaSystem.lavaCells : new Set();
    const poisonCells = this.poisonSystem ? this.poisonSystem.poisonCells : new Set();
    this.spikeSystem = new SpikeSystem(
      this,
      this.mazeGrid,
      this.level,
      this.startPos,
      this.exitPos,
      this.mazeSeed,
      waterCells,
      lavaCells,
      poisonCells,
    );
  }

  // ---------------------------------------------------------------
  // Coin Spawner
  // ---------------------------------------------------------------

  /** @private */
  _initCoinSpawner() {
    this.coinGroup = this.physics.add.group();

    const forbiddenCells = this._getCombinedForbiddenCells();

    this.coinSpawner = new CoinSpawner(
      this,
      this.mazeGrid,
      this.coinGroup,
      { col: this.playerStartCol, row: this.playerStartRow },
      this.exitPos,
      forbiddenCells,
      this.player,
    );

    this.coinSpawner.start();

    // Player <-> coin overlap
    this.physics.add.overlap(
      this.player,
      this.coinGroup,
      this._onCoinCollected,
      null,
      this,
    );
  }

  /**
   * Cross-link all spawners so that no spawner places an item on a cell
   * already occupied by another spawner's active items, keys, chests,
   * the compass, or the Threia Totem.
   *
   * Must be called after ALL spawners, compass, and totem have been initialized.
   * @private
   */
  _crossLinkSpawners() {
    // Build reference objects that all spawners can read.
    // We use object wrappers so spawners always see the current value
    // (which changes when collected or between levels).
    const compassRef = { item: this._compassItem };
    const totemRef = { item: this._threiaTotemItem };

    if (this.foodSpawner) {
      this.foodSpawner.setSiblings(
        this.coinSpawner ? this.coinSpawner.activeItems : null,
        this.keySpawner ? this.keySpawner.activeItems : null,
        this.chestSpawner ? this.chestSpawner.activeItems : null,
        compassRef,
        totemRef,
      );
    }

    if (this.coinSpawner) {
      this.coinSpawner.setSiblings(
        this.foodSpawner ? this.foodSpawner.activeItems : null,
        this.keySpawner ? this.keySpawner.activeItems : null,
        this.chestSpawner ? this.chestSpawner.activeItems : null,
        compassRef,
        totemRef,
      );
    }

    // Link key spawner with food, coins, chests, compass, and totem
    if (this.keySpawner) {
      this.keySpawner.setSiblings(
        this.foodSpawner ? this.foodSpawner.activeItems : null,
        this.coinSpawner ? this.coinSpawner.activeItems : null,
        this.chestSpawner ? this.chestSpawner.activeItems : null,
        compassRef,
        totemRef,
      );
    }

    // Store refs so we can update .item when items are collected
    this._compassRef = compassRef;
    this._threiaTotemRef = totemRef;
  }

  /**
   * Callback when the player overlaps a coin.
   * @private
   * @param {Player} player
   * @param {Phaser.GameObjects.Sprite} coin
   */
  _onCoinCollected(player, coin) {
    if (!coin.active) return;

    coin.collect();

    // Award score points
    this.scoreSystem.addCoinPoints();

    // Increment coin counter
    this.coinsCollected += 1;
    this.eventBus.emit(Events.COINS_CHANGED, this.coinsCollected);
    this.eventBus.emit(Events.COIN_COLLECTED);

    // Track treasure room coins separately
    if (this._inTreasureRoom) {
      this._treasureCoinsCollected += 1;
    }

    // Check for extra life (every COINS_PER_EXTRA_LIFE coins)
    if (this.coinsCollected % COINS_PER_EXTRA_LIFE === 0) {
      this.healthSystem.addLife();
    }
  }

  // ---------------------------------------------------------------
  // Key Spawner
  // ---------------------------------------------------------------

  /**
   * Initialize the key spawner (only active from KEY_CHEST_MIN_LEVEL).
   * @private
   */
  _initKeySpawner() {
    if (this.level < KEY_CHEST_MIN_LEVEL) {
      this.keySpawner = null;
      this.keyGroup = null;
      return;
    }

    this.keyGroup = this.physics.add.group();
    const forbiddenCells = this._getCombinedForbiddenCells();

    // Add chest cells to forbidden set for key spawning
    if (this.chestSpawner) {
      for (const key of this.chestSpawner.getChestCells()) {
        forbiddenCells.add(key);
      }
    }

    this.keySpawner = new KeySpawner(
      this,
      this.mazeGrid,
      this.keyGroup,
      { col: this.playerStartCol, row: this.playerStartRow },
      this.exitPos,
      forbiddenCells,
      this.player,
      this._playerKeys.length,
    );
    this.keySpawner.start();

    // Player <-> key overlap
    this.physics.add.overlap(
      this.player,
      this.keyGroup,
      this._onKeyCollected,
      null,
      this,
    );
  }

  /**
   * Callback when the player overlaps a key item.
   * @private
   * @param {Player} player
   * @param {import('../entities/KeyItem.js').default} keyItem
   */
  _onKeyCollected(player, keyItem) {
    if (!keyItem.active) return;

    if (this._playerKeys.length >= MAX_PLAYER_KEYS) {
      // Inventory full — try to upgrade the worst key if the new one is better
      if (!this._tryUpgradeKey(keyItem.tier)) return;
    } else {
      this._playerKeys.push(keyItem.tier);
    }

    keyItem.collect();

    // Update key spawner's count
    if (this.keySpawner) {
      this.keySpawner.setPlayerKeyCount(this._playerKeys.length);
    }

    // Notify HUD
    this.eventBus.emit(Events.KEY_COLLECTED, keyItem.tier);
    this.eventBus.emit(Events.KEYS_CHANGED, [...this._playerKeys]);
  }

  /**
   * Attempt to upgrade the worst key in a full inventory.
   * If the new key tier is strictly better than the lowest-tier key
   * currently held, replaces that key with the new one.
   *
   * @private
   * @param {string} newTier - KeyTier value of the incoming key.
   * @returns {boolean} True if a key was replaced, false otherwise.
   */
  _tryUpgradeKey(newTier) {
    const newRank = KEY_TIER_ORDER.indexOf(newTier);
    if (newRank < 0) return false;

    // Find the index of the lowest-ranked key in inventory
    let worstIdx = -1;
    let worstRank = newRank; // must be strictly worse (lower) than the new key
    for (let i = 0; i < this._playerKeys.length; i++) {
      const rank = KEY_TIER_ORDER.indexOf(this._playerKeys[i]);
      if (rank < worstRank) {
        worstRank = rank;
        worstIdx = i;
      }
    }

    if (worstIdx < 0) return false; // no key is worse than the new one

    // Replace the worst key
    this._playerKeys[worstIdx] = newTier;
    return true;
  }

  // ---------------------------------------------------------------
  // Manuscript
  // ---------------------------------------------------------------

  /**
   * Roll for manuscript spawn and, if successful, place a ManuscriptItem
   * on a random walkable path cell. Only uncollected manuscripts can
   * spawn. At most one manuscript per level, 25% chance.
   * Must be called BEFORE _initCompass / _initThreiaTotem so that the
   * manuscript cell can be excluded from their forbidden sets.
   * @private
   */
  _initManuscript() {
    // Clean up any previous manuscript state
    this._manuscriptItem = null;
    this._manuscriptId = null;
    if (this._manuscriptGroup) {
      this._manuscriptGroup.clear(true, true);
      this._manuscriptGroup = null;
    }

    // Roll for manuscript spawn
    if (Math.random() >= MANUSCRIPT_SPAWN_CHANCE) return;

    // Pick a random uncollected manuscript
    const manuscript = pickUncollectedManuscript();
    if (!manuscript) return; // all 30 already collected

    // Pick a random walkable path cell (not start, not exit, not forbidden)
    const forbidden = this._getCombinedForbiddenCells();
    forbidden.add(`${this.startPos.col},${this.startPos.row}`);
    forbidden.add(`${this.exitPos.col},${this.exitPos.row}`);

    const pathCells = MazeGenerator.getPathCells(
      this.mazeGrid,
      this.mazeGridWidth,
      this.mazeGridHeight,
    ).filter((c) => !forbidden.has(`${c.col},${c.row}`));

    if (pathCells.length === 0) return;

    const cell = Phaser.Utils.Array.GetRandom(pathCells);

    // Create physics group + manuscript item
    this._manuscriptId = manuscript.id;
    this._manuscriptGroup = this.physics.add.group();
    this._manuscriptItem = new ManuscriptItem(this, cell.col, cell.row, manuscript.id);
    this._manuscriptGroup.add(this._manuscriptItem);

    // Player <-> manuscript overlap
    this.physics.add.overlap(
      this.player,
      this._manuscriptGroup,
      this._onManuscriptCollected,
      null,
      this,
    );
  }

  /**
   * Callback when the player overlaps the manuscript item.
   * @private
   * @param {Player} _player
   * @param {ManuscriptItem} manuscriptItem
   */
  _onManuscriptCollected(_player, manuscriptItem) {
    if (!manuscriptItem.active) return;

    manuscriptItem.collect();
    this._manuscriptItem = null;

    // Persist collection to localStorage
    collectManuscript(this._manuscriptId);
    this._manuscriptId = null;

    // Notify (HUD or other systems listening)
    this.eventBus.emit(Events.MANUSCRIPT_COLLECTED);
  }

  // ---------------------------------------------------------------
  // Compass
  // ---------------------------------------------------------------

  /**
   * Roll for compass spawn and, if successful, place a CompassItem on a
   * random walkable path cell. If the player already has a compass
   * (restored from save), skip spawning.
   * @private
   */
  _initCompass() {
    // Clean up any previous compass state
    this._compassItem = null;
    if (this._compassGroup) {
      this._compassGroup.clear(true, true);
      this._compassGroup = null;
    }

    // If restored from save and compass was already collected, don't spawn
    if (this._hasCompass) return;

    // Roll for compass spawn
    if (Math.random() >= COMPASS_SPAWN_CHANCE) return;

    // Pick a random walkable path cell (not start, not exit, not forbidden)
    const forbidden = this._getCombinedForbiddenCells();
    forbidden.add(`${this.startPos.col},${this.startPos.row}`);
    forbidden.add(`${this.exitPos.col},${this.exitPos.row}`);

    // Also exclude manuscript item cell if it exists
    if (this._manuscriptItem && this._manuscriptItem.active) {
      forbidden.add(`${this._manuscriptItem.gridCol},${this._manuscriptItem.gridRow}`);
    }

    const pathCells = MazeGenerator.getPathCells(
      this.mazeGrid,
      this.mazeGridWidth,
      this.mazeGridHeight,
    ).filter((c) => !forbidden.has(`${c.col},${c.row}`));

    if (pathCells.length === 0) return;

    const cell = Phaser.Utils.Array.GetRandom(pathCells);

    // Create physics group + compass item
    this._compassGroup = this.physics.add.group();
    this._compassItem = new CompassItem(this, cell.col, cell.row);
    this._compassGroup.add(this._compassItem);

    // Player <-> compass overlap
    this.physics.add.overlap(
      this.player,
      this._compassGroup,
      this._onCompassCollected,
      null,
      this,
    );
  }

  /**
   * Callback when the player overlaps the compass item.
   * @private
   * @param {Player} _player
   * @param {CompassItem} compassItem
   */
  _onCompassCollected(_player, compassItem) {
    if (!compassItem.active) return;

    compassItem.collect();
    this._hasCompass = true;
    this._compassItem = null;

    // Keep spawner compass reference in sync
    if (this._compassRef) {
      this._compassRef.item = null;
    }

    // Notify HUD with exit coordinates
    this.eventBus.emit(Events.COMPASS_COLLECTED, {
      exitCol: this.exitPos.col,
      exitRow: this.exitPos.row,
    });

    this._autoSave();
  }

  // ---------------------------------------------------------------
  // Threia Totem
  // ---------------------------------------------------------------

  /**
   * Roll for Threia Totem spawn and, if successful, place a ThreiaTotemItem
   * on a random walkable path cell. If the player already holds a totem
   * (restored from save or carried from previous level), skip spawning.
   * @private
   */
  _initThreiaTotem() {
    // Clean up any previous totem state
    this._threiaTotemItem = null;
    if (this._threiaTotemGroup) {
      this._threiaTotemGroup.clear(true, true);
      this._threiaTotemGroup = null;
    }

    // If player already has a totem (persisted across levels), don't spawn
    if (this._hasThreiaTotem) return;

    // Roll for totem spawn
    if (Math.random() >= THREIA_TOTEM_SPAWN_CHANCE) return;

    // Pick a random walkable path cell (not start, not exit, not forbidden)
    const forbidden = this._getCombinedForbiddenCells();
    forbidden.add(`${this.startPos.col},${this.startPos.row}`);
    forbidden.add(`${this.exitPos.col},${this.exitPos.row}`);

    // Also exclude compass item cell if it exists
    if (this._compassItem && this._compassItem.active) {
      forbidden.add(`${this._compassItem.gridCol},${this._compassItem.gridRow}`);
    }

    // Also exclude manuscript item cell if it exists
    if (this._manuscriptItem && this._manuscriptItem.active) {
      forbidden.add(`${this._manuscriptItem.gridCol},${this._manuscriptItem.gridRow}`);
    }

    const pathCells = MazeGenerator.getPathCells(
      this.mazeGrid,
      this.mazeGridWidth,
      this.mazeGridHeight,
    ).filter((c) => !forbidden.has(`${c.col},${c.row}`));

    if (pathCells.length === 0) return;

    const cell = Phaser.Utils.Array.GetRandom(pathCells);

    // Create physics group + totem item
    this._threiaTotemGroup = this.physics.add.group();
    this._threiaTotemItem = new ThreiaTotemItem(this, cell.col, cell.row);
    this._threiaTotemGroup.add(this._threiaTotemItem);

    // Player <-> totem overlap
    this.physics.add.overlap(
      this.player,
      this._threiaTotemGroup,
      this._onThreiaTotemCollected,
      null,
      this,
    );
  }

  /**
   * Callback when the player overlaps the Threia Totem item.
   * @private
   * @param {Player} _player
   * @param {ThreiaTotemItem} totemItem
   */
  _onThreiaTotemCollected(_player, totemItem) {
    if (!totemItem.active) return;

    totemItem.collect();
    this._hasThreiaTotem = true;
    this._threiaTotemItem = null;

    // Keep spawner totem reference in sync
    if (this._threiaTotemRef) {
      this._threiaTotemRef.item = null;
    }

    // Notify ThreiaSystem that the totem is held
    if (this._threiaSystem) {
      this._threiaSystem.setHasTotem(true);
    }

    // Notify HUD
    this.eventBus.emit(Events.THREIA_TOTEM_COLLECTED);

    this._autoSave();
  }

  // ---------------------------------------------------------------
  // Hammer of Threia
  // ---------------------------------------------------------------

  /**
   * Roll for Hammer of Threia floor spawn and, if successful, place a
   * HammerItem on a random walkable path cell. If the player already has
   * hammer charges (from shop or previous pickup), still allow a floor spawn
   * — the charges will stack on collection.
   * @private
   */
  _initHammer() {
    // Clean up any previous hammer item state
    this._hammerItem = null;
    if (this._hammerGroup) {
      this._hammerGroup.clear(true, true);
      this._hammerGroup = null;
    }

    // Check for shop-provided hammer charges (stacks with pickup)
    if (isItemActive(SHOP_ITEM_HAMMER_OF_THREIA)) {
      const shopCharges = this._getShopHammerCharges();
      if (shopCharges > 0 && this._hammerCharges === 0) {
        this._hammerCharges = shopCharges;
      }
    }

    // Roll for floor pickup spawn
    if (Math.random() >= HAMMER_SPAWN_CHANCE) return;

    // Pick a random walkable path cell (not start, not exit, not forbidden)
    const forbidden = this._getCombinedForbiddenCells();
    forbidden.add(`${this.startPos.col},${this.startPos.row}`);
    forbidden.add(`${this.exitPos.col},${this.exitPos.row}`);

    // Exclude compass item cell if it exists
    if (this._compassItem && this._compassItem.active) {
      forbidden.add(`${this._compassItem.gridCol},${this._compassItem.gridRow}`);
    }

    // Exclude manuscript item cell if it exists
    if (this._manuscriptItem && this._manuscriptItem.active) {
      forbidden.add(`${this._manuscriptItem.gridCol},${this._manuscriptItem.gridRow}`);
    }

    // Exclude totem item cell if it exists
    if (this._threiaTotemItem && this._threiaTotemItem.active) {
      forbidden.add(`${this._threiaTotemItem.gridCol},${this._threiaTotemItem.gridRow}`);
    }

    const pathCells = MazeGenerator.getPathCells(
      this.mazeGrid,
      this.mazeGridWidth,
      this.mazeGridHeight,
    ).filter((c) => !forbidden.has(`${c.col},${c.row}`));

    if (pathCells.length === 0) return;

    const cell = Phaser.Utils.Array.GetRandom(pathCells);

    // Create physics group + hammer item
    this._hammerGroup = this.physics.add.group();
    this._hammerItem = new HammerItem(this, cell.col, cell.row);
    this._hammerGroup.add(this._hammerItem);

    // Player <-> hammer overlap
    this.physics.add.overlap(
      this.player,
      this._hammerGroup,
      this._onHammerCollected,
      null,
      this,
    );
  }

  /**
   * Get the remaining durability charges from the shop Hammer of Threia.
   * @private
   * @returns {number}
   */
  _getShopHammerCharges() {
    try {
      const durStr = localStorage.getItem('lost-in-labyrinth-durability');
      if (!durStr) return 0;
      const dur = JSON.parse(durStr);
      return dur[SHOP_ITEM_HAMMER_OF_THREIA] || 0;
    } catch (_) {
      return 0;
    }
  }

  /**
   * Callback when the player overlaps the Hammer of Threia floor item.
   * Charges from the pickup stack with any existing charges (from shop or
   * a previous pickup on a different level).
   * @private
   * @param {Player} _player
   * @param {HammerItem} hammerItem
   */
  _onHammerCollected(_player, hammerItem) {
    if (!hammerItem.active) return;

    hammerItem.collect();
    this._hammerItem = null;

    // Stack charges: pickup charges + existing charges
    this._hammerCharges += HAMMER_PICKUP_CHARGES;

    // Update the HammerSystem with the new total
    if (this._hammerSystem) {
      this._hammerSystem.setCharges(this._hammerCharges);
    }

    // Notify HUD
    this.eventBus.emit(Events.HAMMER_COLLECTED, this._hammerCharges);
    this.eventBus.emit(Events.HAMMER_CHARGES_CHANGED, this._hammerCharges);

    this._autoSave();
  }

  // ---------------------------------------------------------------
  // Chest Spawner
  // ---------------------------------------------------------------

  /**
   * Initialize the chest spawner (only active from KEY_CHEST_MIN_LEVEL).
   * Places chests once at maze generation, or restores from save.
   * Also creates the loot physics group for collectible chest loot items.
   * @private
   */
  _initChestSpawner() {
    // Always create the loot group (even if chests aren't active yet,
    // a player may still have uncollected loot from a previous level).
    if (!this.lootGroup) {
      this.lootGroup = this.physics.add.group();

      // Player <-> loot overlap
      this.physics.add.overlap(
        this.player,
        this.lootGroup,
        this._onLootCollected,
        (_player, lootItem) => lootItem.active && lootItem._collectable === true,
        this,
      );
    }

    if (this.level < KEY_CHEST_MIN_LEVEL) {
      this.chestSpawner = null;
      return;
    }

    const forbiddenCells = this._getCombinedForbiddenCells();

    this.chestSpawner = new ChestSpawner(
      this,
      this.mazeGrid,
      this.startPos,
      this.exitPos,
      forbiddenCells,
    );

    // Restore chests from save, or place new ones
    if (this._savedChests) {
      this.chestSpawner.restore(this._savedChests);
      this._savedChests = null;
    } else {
      this.chestSpawner.placeChests();
    }
  }

  /**
   * Try to open a chest in front of the player (O key handler).
   * Checks facing direction, finds adjacent chest, checks for
   * matching key (uses lowest-tier key first), plays open animation,
   * then determines and spawns loot.
   * @private
   */
  async _tryOpenChest() {
    if (!this.player || this._chestOpenInProgress) return;
    if (!this.chestSpawner) return;

    // Find chest adjacent to player in any direction
    const pos = this.player.getGridPosition();
    const chest = this.chestSpawner.findAdjacentChest(pos.col, pos.row);

    if (!chest) return;

    // No keys at all
    if (this._playerKeys.length === 0) {
      try { this.sound.play(AUDIO_CHEST_LOCKED, { volume: getSfxVol(0.5) }); } catch (_) { /* */ }
      return;
    }

    // Find the lowest-tier key that can open this chest
    const keyTier = this._findBestKey(chest.tier);
    if (!keyTier) {
      // Has keys but none match the chest tier
      try { this.sound.play(AUDIO_CHEST_LOCKED, { volume: getSfxVol(0.5) }); } catch (_) { /* */ }
      return;
    }

    this._chestOpenInProgress = true;

    // Consume the key
    const keyIdx = this._playerKeys.indexOf(keyTier);
    if (keyIdx !== -1) {
      this._playerKeys.splice(keyIdx, 1);
    }

    // Update key spawner
    if (this.keySpawner) {
      this.keySpawner.setPlayerKeyCount(this._playerKeys.length);
    }

    // Notify HUD about key usage and updated keys
    this.eventBus.emit(Events.KEY_USED, keyTier);
    this.eventBus.emit(Events.KEYS_CHANGED, [...this._playerKeys]);

    // Play chest open animation
    const isGold = chest.tier === ChestTier.GOLD;
    await chest.open(isGold);

    // Determine loot
    const loot = this._determineLoot(chest.tier);

    // Remove chest from spawner and destroy it
    this.chestSpawner.removeChest(chest);
    chest.destroy();

    // Spawn loot
    await this._spawnLoot(loot, chest.gridCol, chest.gridRow, chest.tier);

    // Emit chest opened event
    this.eventBus.emit(Events.CHEST_OPENED, { tier: chest.tier, loot });

    this._chestOpenInProgress = false;
    this._autoSave();
  }

  /**
   * Find the lowest-tier key that can open the given chest tier.
   * @private
   * @param {string} chestTier - ChestTier value
   * @returns {string|null} KeyTier value, or null if no key can open it
   */
  _findBestKey(chestTier) {
    // Iterate from lowest tier to highest — prefer using the cheapest key
    for (const tier of KEY_TIER_ORDER) {
      if (!KEY_CAN_OPEN[tier]) continue;
      if (!KEY_CAN_OPEN[tier].has(chestTier)) continue;
      if (this._playerKeys.includes(tier)) return tier;
    }
    return null;
  }

  /**
   * Determine the loot type from a chest based on its tier.
   * Uses weighted probability tables from config.
   * @private
   * @param {string} chestTier - ChestTier value
   * @returns {{ type: string, amount?: number }}
   */
  _determineLoot(chestTier) {
    let weights;
    switch (chestTier) {
      case ChestTier.GOLD: weights = CHEST_LOOT_GOLD; break;
      case ChestTier.COPPER: weights = CHEST_LOOT_COPPER; break;
      default: weights = CHEST_LOOT_METALLIC;
    }

    // Build entries, conditionally excluding some
    const entries = [];
    for (const [type, weight] of Object.entries(weights)) {
      // Exclude heart if player has max lives
      if (type === ChestLoot.HEART && this.healthSystem.lives >= INITIAL_LIVES) continue;
      // Exclude agmuth if already at max
      if (type === ChestLoot.AGMUTH && this._agmuthCount >= AGMUTH_MAX_PER_GAME) continue;
      entries.push({ type, weight });
    }

    // Agmuth Pendant: boost agmuth drop probability for copper/gold chests
    if (isItemActive(SHOP_ITEM_AGMUTH_PENDANT) &&
        (chestTier === ChestTier.COPPER || chestTier === ChestTier.GOLD)) {
      const agmuthEntry = entries.find(e => e.type === ChestLoot.AGMUTH);
      if (agmuthEntry) {
        agmuthEntry.weight += AGMUTH_PENDANT_BONUS;
      }
    }

    // Normalize weights
    const totalWeight = entries.reduce((sum, e) => sum + e.weight, 0);
    const r = Math.random() * totalWeight;
    let cumulative = 0;

    for (const entry of entries) {
      cumulative += entry.weight;
      if (r < cumulative) {
        return { type: entry.type };
      }
    }

    // Fallback
    return { type: ChestLoot.EMPTY };
  }

  /**
   * Spawn the loot from an opened chest.
   * @private
   * @param {{ type: string }} loot
   * @param {number} chestCol - Grid column of the chest
   * @param {number} chestRow - Grid row of the chest
   * @param {string} chestTier - ChestTier value (for coin distribution)
   * @returns {Promise<void>}
   */
  async _spawnLoot(loot, chestCol, chestRow, chestTier) {
    const cx = chestCol * TILE_SIZE + TILE_SIZE / 2;
    const cy = chestRow * TILE_SIZE + TILE_SIZE / 2;

    switch (loot.type) {
      case ChestLoot.COIN_BAG:
        this._spawnLootCoinBag(cx, cy);
        break;
      case ChestLoot.HEART:
        this._spawnLootHeart(cx, cy);
        break;
      case ChestLoot.STAR:
        this._spawnLootStar(cx, cy);
        break;
      case ChestLoot.AGMUTH:
        this._spawnLootAgmuth(cx, cy);
        break;
      case ChestLoot.EMPTY:
      default:
        this._showLootLabel(cx, cy, 'Empty...', '#888888');
        break;
    }
  }

  /**
   * Spawn a Bag of Coins loot from a chest at the chest position.
   * The bag stays in place as a collectible item until the player
   * walks over it, awarding CHEST_COIN_BAG_AMOUNT coins at once.
   * @private
   * @param {number} x - World X
   * @param {number} y - World Y
   */
  _spawnLootCoinBag(x, y) {
    const bag = this.add.sprite(x, y, TEX_LOOT_COIN_BAG);
    bag.setDepth(150);
    bag.setScale(0);

    // Tag for identification in the overlap callback
    bag.lootType = ChestLoot.COIN_BAG;

    // Not collectable until pop-in animation finishes
    bag._collectable = false;

    // Register in loot group so player <-> loot overlap works
    if (this.lootGroup) {
      this.lootGroup.add(bag);
      if (bag.body) bag.body.enable = false;
    }

    // Pop-in animation, then settle at idle scale
    this.tweens.add({
      targets: bag,
      scaleX: 1.8,
      scaleY: 1.8,
      duration: 300,
      ease: 'Back.easeOut',
      onComplete: () => {
        if (!bag || !bag.active) return;
        // Settle to normal scale
        this.tweens.add({
          targets: bag,
          scaleX: 1.2,
          scaleY: 1.2,
          duration: 200,
          ease: 'Quad.easeOut',
          onComplete: () => {
            if (!bag || !bag.active) return;
            // Enable collection after animation completes
            if (bag.body) bag.body.enable = true;
            bag._collectable = true;
          },
        });
      },
    });

    // Subtle pulsing glow while waiting to be collected (golden tint)
    this._addLootPulse(bag, 0xffd700);
  }

  /**
   * Spawn a heart loot from a chest at the chest position.
   * The heart stays in place as a collectible item until the player
   * walks over it.
   * @private
   * @param {number} x - World X
   * @param {number} y - World Y
   */
  _spawnLootHeart(x, y) {
    const heart = this.add.sprite(x, y, TEX_LOOT_HEART);
    heart.setDepth(150);
    heart.setScale(0);

    // Tag for identification in the overlap callback
    heart.lootType = ChestLoot.HEART;

    // Not collectable until pop-in animation finishes
    heart._collectable = false;

    // Register in loot group so player <-> loot overlap works
    if (this.lootGroup) {
      this.lootGroup.add(heart);
      if (heart.body) heart.body.enable = false;
    }

    // Pop-in animation, then settle at idle scale
    this.tweens.add({
      targets: heart,
      scaleX: 1.8,
      scaleY: 1.8,
      duration: 300,
      ease: 'Back.easeOut',
      onComplete: () => {
        if (!heart || !heart.active) return;
        // Settle to normal scale
        this.tweens.add({
          targets: heart,
          scaleX: 1.2,
          scaleY: 1.2,
          duration: 200,
          ease: 'Quad.easeOut',
          onComplete: () => {
            if (!heart || !heart.active) return;
            // Enable collection after animation completes
            if (heart.body) heart.body.enable = true;
            heart._collectable = true;
          },
        });
      },
    });

    // Subtle pulsing glow while waiting to be collected
    this._addLootPulse(heart, 0xff4444);
  }

  /**
   * Spawn a Spirit Star loot from a chest at the chest position.
   * The star stays in place as a collectible item until the player
   * walks over it.
   * @private
   * @param {number} x - World X
   * @param {number} y - World Y
   */
  _spawnLootStar(x, y) {
    const star = this.add.sprite(x, y, TEX_LOOT_STAR);
    star.setDepth(150);
    star.setScale(0);

    // Tag for identification in the overlap callback
    star.lootType = ChestLoot.STAR;

    // Not collectable until pop-in animation finishes
    star._collectable = false;

    // Register in loot group so player <-> loot overlap works
    if (this.lootGroup) {
      this.lootGroup.add(star);
      if (star.body) star.body.enable = false;
    }

    // Pop in with spin, then settle
    this.tweens.add({
      targets: star,
      scaleX: 1.8,
      scaleY: 1.8,
      angle: 360,
      duration: 400,
      ease: 'Back.easeOut',
      onComplete: () => {
        if (!star || !star.active) return;
        this.tweens.add({
          targets: star,
          scaleX: 1.2,
          scaleY: 1.2,
          duration: 200,
          ease: 'Quad.easeOut',
          onComplete: () => {
            if (!star || !star.active) return;
            // Enable collection after animation completes
            if (star.body) star.body.enable = true;
            star._collectable = true;
          },
        });
      },
    });

    // Subtle pulsing glow while waiting to be collected
    this._addLootPulse(star, 0xffffff);
  }

  /**
   * Spawn an Agmuth loot from a chest at the chest position.
   * The Agmuth stays in place as a collectible item until the player
   * walks over it.
   * @private
   * @param {number} x - World X
   * @param {number} y - World Y
   */
  _spawnLootAgmuth(x, y) {
    const agmuth = this.add.sprite(x, y, TEX_LOOT_AGMUTH);
    agmuth.setDepth(150);
    agmuth.setScale(0);

    // Tag for identification in the overlap callback
    agmuth.lootType = ChestLoot.AGMUTH;

    // Not collectable until pop-in animation finishes
    agmuth._collectable = false;

    // Register in loot group so player <-> loot overlap works
    if (this.lootGroup) {
      this.lootGroup.add(agmuth);
      if (agmuth.body) agmuth.body.enable = false;
    }

    // Dramatic pop-in, then settle
    this.tweens.add({
      targets: agmuth,
      scaleX: 2.2,
      scaleY: 2.2,
      duration: 500,
      ease: 'Back.easeOut',
      onComplete: () => {
        if (!agmuth || !agmuth.active) return;
        this.tweens.add({
          targets: agmuth,
          scaleX: 1.4,
          scaleY: 1.4,
          duration: 250,
          ease: 'Quad.easeOut',
          onComplete: () => {
            if (!agmuth || !agmuth.active) return;
            // Enable collection after animation completes
            if (agmuth.body) agmuth.body.enable = true;
            agmuth._collectable = true;
          },
        });
      },
    });

    // Subtle pulsing glow while waiting to be collected
    this._addLootPulse(agmuth, 0xffffff);
  }

  /**
   * Add a subtle pulsing glow circle behind a loot sprite to make it
   * visually inviting while sitting on the ground.
   * @private
   * @param {Phaser.GameObjects.Sprite} sprite - The loot sprite.
   * @param {number} tint - Color for the glow (e.g. 0xff4444 for heart).
   */
  _addLootPulse(sprite, tint) {
    if (!sprite || !this.scene) return;

    const glow = this.add.graphics();
    glow.setDepth(sprite.depth - 1);
    glow.fillStyle(tint, 0.15);
    glow.fillCircle(sprite.x, sprite.y, 12);
    glow.fillStyle(tint, 0.08);
    glow.fillCircle(sprite.x, sprite.y, 18);

    // Pulse alpha
    const glowTween = this.tweens.add({
      targets: glow,
      alpha: { from: 1, to: 0.3 },
      duration: 800,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    // Store references on the sprite for cleanup
    sprite._lootGlow = glow;
    sprite._lootGlowTween = glowTween;
  }

  /**
   * Callback when the player overlaps a loot item (heart, star, agmuth, coin bag).
   * Dispatches to the appropriate collection logic based on lootType.
   * @private
   * @param {Player} _player
   * @param {Phaser.GameObjects.Sprite} lootItem
   */
  _onLootCollected(_player, lootItem) {
    if (!lootItem.active) return;

    // Immediately deactivate to prevent double-collect
    lootItem.active = false;

    const lootType = lootItem.lootType;
    const x = lootItem.x;
    const y = lootItem.y;

    // Clean up glow
    if (lootItem._lootGlowTween) {
      lootItem._lootGlowTween.stop();
      lootItem._lootGlowTween = null;
    }
    if (lootItem._lootGlow) {
      lootItem._lootGlow.destroy();
      lootItem._lootGlow = null;
    }

    switch (lootType) {
      case ChestLoot.HEART:
        this._collectLootHeart(lootItem, x, y);
        break;
      case ChestLoot.STAR:
        this._collectLootStar(lootItem, x, y);
        break;
      case ChestLoot.AGMUTH:
        this._collectLootAgmuth(lootItem, x, y);
        break;
      case ChestLoot.COIN_BAG:
        this._collectLootCoinBag(lootItem, x, y);
        break;
      default:
        lootItem.destroy();
        break;
    }
  }

  /**
   * Collect a heart loot item: restore a life, play effects, destroy.
   * @private
   * @param {Phaser.GameObjects.Sprite} heart
   * @param {number} x - World X
   * @param {number} y - World Y
   */
  _collectLootHeart(heart, x, y) {
    // Play collect sound
    try { this.sound.play(AUDIO_HEART_COLLECT, { volume: getSfxVol(0.5) }); } catch (_) {}

    // Show label
    this._showLootLabel(x, y, 'Contained Heart', '#ff4444');

    // Restore a life
    this.healthSystem.addLife();

    // Collect animation: flash + shrink + fade
    this.tweens.add({
      targets: heart,
      scaleX: 2,
      scaleY: 2,
      alpha: 0,
      y: y - 12,
      duration: 400,
      ease: 'Quad.easeOut',
      onComplete: () => heart.destroy(),
    });
  }

  /**
   * Collect a spirit star loot item: full heal, play effects, destroy.
   * @private
   * @param {Phaser.GameObjects.Sprite} star
   * @param {number} x - World X
   * @param {number} y - World Y
   */
  _collectLootStar(star, x, y) {
    // Play collect sound
    try { this.sound.play(AUDIO_STAR_COLLECT, { volume: getSfxVol(0.5) }); } catch (_) {}

    // Show label
    this._showLootLabel(x, y, 'Spirit Star', '#ffffff');

    // Full HP restore
    this.healthSystem.fullHeal();

    // Collect animation: spin + shrink + fade
    this.tweens.add({
      targets: star,
      scaleX: 2,
      scaleY: 2,
      alpha: 0,
      angle: star.angle + 360,
      y: y - 16,
      duration: 400,
      ease: 'Quad.easeOut',
      onComplete: () => star.destroy(),
    });
  }

  /**
   * Collect an Agmuth loot item: increment count, play effects, destroy.
   * @private
   * @param {Phaser.GameObjects.Sprite} agmuth
   * @param {number} x - World X
   * @param {number} y - World Y
   */
  _collectLootAgmuth(agmuth, x, y) {
    // Play collect sound
    try { this.sound.play(AUDIO_AGMUTH_COLLECT, { volume: getSfxVol(0.6) }); } catch (_) {}

    // Show label
    this._showLootLabel(x, y, 'Agmuth found', '#ffffff');

    // Increment count
    this._agmuthCount = Math.min(this._agmuthCount + 1, AGMUTH_MAX_PER_GAME);
    this.eventBus.emit(Events.AGMUTH_COLLECTED);
    this.eventBus.emit(Events.AGMUTH_CHANGED, this._agmuthCount);

    // Subtle glow particles
    try {
      const emitter = this.add.particles(x, y, TEX_GLOW, {
        lifespan: { min: 400, max: 800 },
        speed: { min: 15, max: 40 },
        scale: { start: 0.4, end: 0 },
        alpha: { start: 0.8, end: 0 },
        frequency: -1,
        quantity: 10,
        tint: 0xffffff,
      });
      emitter.setDepth(151);
      emitter.explode(10);
      this.time.delayedCall(900, () => { if (emitter) emitter.destroy(); });
    } catch (_) {}

    // Collect animation: expand + fade
    this.tweens.add({
      targets: agmuth,
      scaleX: 2.5,
      scaleY: 2.5,
      alpha: 0,
      y: y - 14,
      duration: 400,
      ease: 'Quad.easeOut',
      onComplete: () => agmuth.destroy(),
    });

    // Show Brother Malachar's relic message after the collect animation settles
    const relicCount = this._agmuthCount;
    this.time.delayedCall(600, () => {
      this._showAgmuthNarratorModal(relicCount);
    });
  }

  /**
   * Collect a Bag of Coins loot item: award CHEST_COIN_BAG_AMOUNT coins,
   * play effects, destroy.
   * @private
   * @param {Phaser.GameObjects.Sprite} bag
   * @param {number} x - World X
   * @param {number} y - World Y
   */
  _collectLootCoinBag(bag, x, y) {
    // Play collect sound
    try { this.sound.play(AUDIO_COIN_BAG_COLLECT, { volume: getSfxVol(0.5) }); } catch (_) {}

    // Show label
    this._showLootLabel(x, y, `+${CHEST_COIN_BAG_AMOUNT} Coins`, '#ffd700');

    // Award coins
    this.coinsCollected += CHEST_COIN_BAG_AMOUNT;
    this.eventBus.emit(Events.COINS_CHANGED, this.coinsCollected);
    this.eventBus.emit(Events.COIN_COLLECTED);

    // Track treasure room coins separately
    if (this._inTreasureRoom) {
      this._treasureCoinsCollected += CHEST_COIN_BAG_AMOUNT;
    }

    // Award score points per coin
    for (let i = 0; i < CHEST_COIN_BAG_AMOUNT; i++) {
      this.scoreSystem.addCoinPoints();
    }

    // Check for extra lives (every COINS_PER_EXTRA_LIFE coins)
    // Calculate how many thresholds were crossed by this bulk addition
    const prevCoins = this.coinsCollected - CHEST_COIN_BAG_AMOUNT;
    const prevThreshold = Math.floor(prevCoins / COINS_PER_EXTRA_LIFE);
    const newThreshold = Math.floor(this.coinsCollected / COINS_PER_EXTRA_LIFE);
    for (let t = 0; t < newThreshold - prevThreshold; t++) {
      this.healthSystem.addLife();
    }

    // Golden glow particles
    try {
      const emitter = this.add.particles(x, y, TEX_GLOW, {
        lifespan: { min: 300, max: 600 },
        speed: { min: 20, max: 50 },
        scale: { start: 0.3, end: 0 },
        alpha: { start: 0.7, end: 0 },
        frequency: -1,
        quantity: 8,
        tint: 0xffd700,
      });
      emitter.setDepth(151);
      emitter.explode(8);
      this.time.delayedCall(700, () => { if (emitter) emitter.destroy(); });
    } catch (_) {}

    // Collect animation: expand + fade
    this.tweens.add({
      targets: bag,
      scaleX: 2,
      scaleY: 2,
      alpha: 0,
      y: y - 12,
      duration: 400,
      ease: 'Quad.easeOut',
      onComplete: () => bag.destroy(),
    });
  }

  /**
   * Show a floating loot label text above a position.
   * @private
   * @param {number} x - World X
   * @param {number} y - World Y
   * @param {string} text - Label text
   * @param {string} color - CSS color string
   */
  _showLootLabel(x, y, text, color) {
    const label = this.add.text(x, y - 14, text, {
      fontFamily: '"Courier New", Courier, monospace',
      fontSize: '10px',
      fontStyle: 'bold',
      color,
      stroke: '#000000',
      strokeThickness: 2,
    }).setOrigin(0.5).setDepth(160);

    label.setScale(0.5);
    this.tweens.add({
      targets: label,
      scaleX: 1,
      scaleY: 1,
      y: label.y - 16,
      alpha: 0,
      duration: 1200,
      ease: 'Quad.easeOut',
      onComplete: () => label.destroy(),
    });
  }

  // ---------------------------------------------------------------
  // Agmuth Ghost Mode
  // ---------------------------------------------------------------

  /**
   * Try to activate Agmuth ghost mode. Requires at least 1 Agmuth
   * and ghost mode not already active.
   * @private
   */
  _tryActivateAgmuth() {
    // Can't activate if already in ghost mode, or no Agmuths available
    if (this._isGhostMode) return;
    if (this._agmuthCount <= 0) return;
    if (!this.player) return;
    if (this.player.isMoving) return;
    if (this._chestOpenInProgress) return;

    this._isGhostMode = true;

    // Play activation sound (with try/catch for missing audio file)
    try {
      this.sound.play(AUDIO_AGMUTH_USED, { volume: getSfxVol(0.7) });
    } catch (_err) {
      // Graceful fallback — audio file may not exist yet
    }

    // Activate visual effects on the player
    this.player.startGhostMode();

    // Notify HUD to show golden glow on the Agmuth icon
    this.eventBus.emit(Events.AGMUTH_ACTIVATED);
  }

  /**
   * Try to pass through a wall in the given direction while ghost mode is active.
   * The wall cell must have a walkable PATH cell on the other side.
   *
   * In the expanded maze grid, adjacent path cells are 2 units apart with a wall
   * cell in between. So moving from a path cell in direction `dir`:
   *   - 1 cell away = wall cell
   *   - 2 cells away = next path cell (if it exists)
   *
   * @private
   * @param {string} dir - Direction enum value
   * @returns {boolean} Whether the ghost wall-pass move was initiated
   */
  _tryGhostWallPass(dir) {
    if (!this._isGhostMode || !this.player) return false;

    const pos = this.player.getGridPosition();
    const delta = this._directionDelta(dir);
    if (!delta) return false;

    const wallCol = pos.col + delta.dx;
    const wallRow = pos.row + delta.dy;

    // The cell 1 step away must be a wall (if it's a path, normal move would have worked)
    if (!this._isWithinGrid(wallCol, wallRow)) return false;
    if (this.mazeGrid[wallRow][wallCol] !== CellType.WALL) return false;

    // The cell 2 steps away must be a walkable path
    const targetCol = pos.col + delta.dx * 2;
    const targetRow = pos.row + delta.dy * 2;

    if (!MazeGenerator.isWalkable(this.mazeGrid, targetCol, targetRow)) return false;

    // Valid ghost pass! Initiate the wall-phase movement.
    // The player teleports through the wall to the target path cell
    // with a special tween that passes through the wall cell.
    this._executeGhostWallPass(dir, targetCol, targetRow);

    return true;
  }

  /**
   * Execute the ghost wall-pass: tween the player through the wall cell
   * to the target path cell, then deactivate ghost mode and consume the Agmuth.
   * @private
   * @param {string} dir - Direction enum value
   * @param {number} targetCol - Target path cell column
   * @param {number} targetRow - Target path cell row
   */
  _executeGhostWallPass(dir, targetCol, targetRow) {
    if (!this.player) return;

    this.player.isMoving = true;

    const targetX = targetCol * TILE_SIZE + TILE_SIZE / 2;
    const targetY = targetRow * TILE_SIZE + TILE_SIZE / 2;

    // Slightly slower tween to show the phasing effect
    this.tweens.add({
      targets: this.player,
      x: targetX,
      y: targetY,
      duration: this.player.moveDuration * 2,
      ease: 'Sine.easeInOut',
      onComplete: () => {
        if (!this.player) return;

        this.player.gridCol = targetCol;
        this.player.gridRow = targetRow;
        this.player.isMoving = false;

        // Deactivate ghost mode and consume the Agmuth BEFORE emitting
        // PLAYER_MOVED, because PLAYER_MOVED may trigger _onLevelComplete()
        // which starts a scene transition. If we deactivate after, we'd be
        // modifying state on an already-transitioning scene.
        this._deactivateGhostMode();

        // Emit player moved event so fog, hazards, etc. update
        this.player.emitter.emit(Events.PLAYER_MOVED, targetCol, targetRow);
      },
    });
  }

  /**
   * Deactivate ghost mode: restore player visuals, consume one Agmuth,
   * update HUD, and auto-save.
   * @private
   */
  _deactivateGhostMode() {
    if (!this._isGhostMode) return;
    this._isGhostMode = false;

    // Restore player visuals
    if (this.player) {
      this.player.stopGhostMode();
    }

    // Consume one Agmuth
    this._agmuthCount = Math.max(0, this._agmuthCount - 1);

    // Notify HUD
    this.eventBus.emit(Events.AGMUTH_DEACTIVATED, this._agmuthCount);
    this.eventBus.emit(Events.AGMUTH_CHANGED, this._agmuthCount);

    this._autoSave();
  }

  /**
   * Map a Direction enum value to a grid delta.
   * @private
   * @param {string} dir
   * @returns {{ dx: number, dy: number }|null}
   */
  _directionDelta(dir) {
    switch (dir) {
      case Direction.UP: return { dx: 0, dy: -1 };
      case Direction.DOWN: return { dx: 0, dy: 1 };
      case Direction.LEFT: return { dx: -1, dy: 0 };
      case Direction.RIGHT: return { dx: 1, dy: 0 };
      default: return null;
    }
  }

  /**
   * Check if a grid position is within the maze bounds.
   * @private
   * @param {number} col
   * @param {number} row
   * @returns {boolean}
   */
  _isWithinGrid(col, row) {
    return row >= 0 && row < this.mazeGridHeight && col >= 0 && col < this.mazeGridWidth;
  }

  // ---------------------------------------------------------------
  // Input
  // ---------------------------------------------------------------

  /** @private */
  _setupInput() {
    this.cursors = this.input.keyboard.createCursorKeys();

    // Z key for zoom-out map view (polled each frame)
    this._zoomKey = this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.Z);

    // O key for opening chests (polled via keydown event)
    this._openKey = this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.O);
    this._onKeydownO = () => {
      if (!this._transitionLock && !this._isPaused && !this._isZoomedOut && !this._isInfoOpen
        && !(this._finalBattleSystem && this._finalBattleSystem.isActive)) {
        this._tryOpenChest();
      }
    };
    this.input.keyboard.on('keydown-O', this._onKeydownO);

    // A key for activating Agmuth (ghost mode — walk through one wall)
    this._agmuthKey = this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.A);
    this._onKeydownA = () => {
      if (!this._transitionLock && !this._isPaused && !this._isZoomedOut && !this._isInfoOpen
        && !(this._finalBattleSystem && this._finalBattleSystem.isActive)) {
        this._tryActivateAgmuth();
      }
    };
    this.input.keyboard.on('keydown-A', this._onKeydownA);

    // H key for Threia prayer/blessing (must be standing still)
    this._onKeydownH = () => {
      if (!this._transitionLock && !this._isPaused && !this._isZoomedOut && !this._isInfoOpen
        && !(this._finalBattleSystem && this._finalBattleSystem.isActive)) {
        if (this.player && !this.player.isMoving && this._threiaSystem) {
          this._threiaSystem.onHPress();
        }
      }
    };
    this.input.keyboard.on('keydown-H', this._onKeydownH);

    // B key for Hammer of Threia (combo press to break walls)
    this._onKeydownB = () => {
      if (!this._transitionLock && !this._isPaused && !this._isZoomedOut && !this._isInfoOpen
        && !(this._finalBattleSystem && this._finalBattleSystem.isActive)) {
        if (this.player && !this.player.isMoving && this._hammerSystem) {
          this._hammerSystem.onBPress();
        }
      }
    };
    this.input.keyboard.on('keydown-B', this._onKeydownB);

    // I key for info modal (toggle)
    this._onKeydownI = () => {
      if (this._isInfoOpen) {
        this._hideInfoModal();
      } else if (!this._transitionLock && !this._isPaused && !this._isZoomedOut
        && !(this._finalBattleSystem && this._finalBattleSystem.isActive)) {
        this._showInfoModal();
      }
    };
    this.input.keyboard.on('keydown-I', this._onKeydownI);

    // ESC toggles pause menu (or closes info modal first)
    this._onKeydownESC = () => {
      if (this._isInfoOpen) {
        this._hideInfoModal();
        return;
      }
      this._togglePause();
    };
    this.input.keyboard.on('keydown-ESC', this._onKeydownESC);

    // TAB key for toggling mini-map
    this._onKeydownTAB = (event) => {
      // Prevent default browser behavior (tab focus switching)
      event.preventDefault();
      if (!this._transitionLock && !this._isPaused && !this._isInfoOpen) {
        this.eventBus.emit(Events.MINIMAP_TOGGLED);
        this._minimapVisible = !this._minimapVisible;
      }
    };
    this.input.keyboard.on('keydown-TAB', this._onKeydownTAB);
  }

  /**
   * Re-subscribe the keydown event listeners that were removed by _cleanup().
   * Unlike _setupInput(), this does NOT re-create cursor keys or addKey objects
   * — those survive across levels. It only re-attaches the 5 named listeners.
   * @private
   */
  _resubscribeKeyboardListeners() {
    if (!this.input || !this.input.keyboard) return;
    if (this._onKeydownO) this.input.keyboard.on('keydown-O', this._onKeydownO);
    if (this._onKeydownA) this.input.keyboard.on('keydown-A', this._onKeydownA);
    if (this._onKeydownH) this.input.keyboard.on('keydown-H', this._onKeydownH);
    if (this._onKeydownB) this.input.keyboard.on('keydown-B', this._onKeydownB);
    if (this._onKeydownI) this.input.keyboard.on('keydown-I', this._onKeydownI);
    if (this._onKeydownESC) this.input.keyboard.on('keydown-ESC', this._onKeydownESC);
    if (this._onKeydownTAB) this.input.keyboard.on('keydown-TAB', this._onKeydownTAB);
  }

  /** @private Called every frame in update(). */
  _handleInput() {
    if (!this.player || this.player.isMoving) {
      return;
    }

    // Block movement while a chest is being opened / loot is spawning
    if (this._chestOpenInProgress) return;

    let moved = false;
    let dir = null;

    if (this.cursors.up.isDown) {
      dir = Direction.UP;
    } else if (this.cursors.down.isDown) {
      dir = Direction.DOWN;
    } else if (this.cursors.left.isDown) {
      dir = Direction.LEFT;
    } else if (this.cursors.right.isDown) {
      dir = Direction.RIGHT;
    }

    if (dir) {
      const pos = this.player.getGridPosition();
      const delta = this._directionDelta(dir);
      moved = this.player.move(dir);

      // If normal move failed (wall in the way), try secret passage push first
      if (!moved && this._secretPassageSystem && delta) {
        const wallCol = pos.col + delta.dx;
        const wallRow = pos.row + delta.dy;
        if (this._secretPassageSystem.onPushWall(wallCol, wallRow)) {
          // Secret passage opened! Try moving again into the now-open passage
          moved = this.player.move(dir);
        }
      }

      // Ghost mode wall-pass: if normal move failed (wall in the way)
      // and ghost mode is active, try to phase through the wall
      if (!moved && this._isGhostMode) {
        moved = this._tryGhostWallPass(dir);
      }
    }

    // Track the direction the player is facing for chest-opening
    if (moved && dir) {
      this._playerFacing = dir;
    }
  }

  // ---------------------------------------------------------------
  // Pause System
  // ---------------------------------------------------------------

  /**
   * Toggle the pause state. If currently in a level transition or
   * announcement, ignore ESC (don't allow pausing mid-transition).
   * @private
   */
  _togglePause() {
    // Don't allow pausing during level transitions/announcements
    // (unless already paused — ESC should always unpause)
    if (!this._isPaused && this._transitionLock) return;

    this.sound.play(AUDIO_ESC_PRESSED, { volume: getSfxVol(0.5) });

    if (this._isPaused) {
      this._resumeGame();
    } else {
      this._pauseGame();
    }
  }

  /**
   * Enter the paused state: freeze game systems, save, and show the menu.
   * @private
   */
  _pauseGame() {
    this._isPaused = true;
    this._transitionLock = true;
    this._pauseStartTime = Date.now();

    // Cancel zoom-out if active (before the pause overlay renders)
    this._cancelZoomOut();

    // Auto-save before showing pause menu
    if (this.player) {
      this._autoSave();
    }

    // Pause game systems
    this._pauseGameSystems();

    // Show the pause modal
    this._showPauseMenu();
  }

  /**
   * Exit the paused state: resume game systems and destroy the menu.
   * @private
   */
  _resumeGame() {
    // Accumulate paused time for score timer offset
    this._totalPausedMs += Date.now() - this._pauseStartTime;

    // Offset the score system's level start time so pause doesn't penalise
    if (this.scoreSystem) {
      this.scoreSystem.levelStartTime += (Date.now() - this._pauseStartTime);
    }

    this._isPaused = false;
    this._transitionLock = false;

    // Resume game systems
    this._resumeGameSystems();

    // Hide the pause modal
    this._hidePauseMenu();
  }

  /**
   * Freeze spawner timers, physics, and player movement sound.
   * We do NOT call scene.pause() since we need the scene to render
   * the pause modal UI and respond to tweens.
   * @private
   */
  _pauseGameSystems() {
    // Pause food spawner timer
    if (this.foodSpawner && this.foodSpawner.spawnTimer) {
      this.foodSpawner.spawnTimer.paused = true;
    }

    // Pause coin spawner timer
    if (this.coinSpawner && this.coinSpawner.spawnTimer) {
      this.coinSpawner.spawnTimer.paused = true;
    }

    // Pause physics
    this.physics.pause();

    // Stop player movement sound if playing
    if (this.player && this.player._stopMovementSound) {
      this.player._stopMovementSound();
    }

    // Stop water walk sound if playing
    this._stopWaterWalkSound();

    // Stop lava walk sound if playing (keep lava effects — they resume on unpause)
    this._stopLavaWalkSound();

    // Stop spike walk sound if playing
    this._stopSpikeWalkSound();

    // Stop poison walk sound if playing
    this._stopPoisonWalkSound();

    // Stop resting if player was idle-resting
    if (this._isResting) {
      this._stopResting();
    }

    // Pause poison timers if player is poisoned
    if (this._poisonDrainTimer) {
      this._poisonDrainTimer.paused = true;
    }
    if (this._poisonEndTimer) {
      this._poisonEndTimer.paused = true;
    }

    // Pause wounded timers if player is wounded
    if (this._woundedDrainTimer) {
      this._woundedDrainTimer.paused = true;
    }
    if (this._woundedEndTimer) {
      this._woundedEndTimer.paused = true;
    }

    // Pause hazard idle drain timers
    if (this._waterIdleDrainTimer) {
      this._waterIdleDrainTimer.paused = true;
    }
    if (this._lavaIdleDrainTimer) {
      this._lavaIdleDrainTimer.paused = true;
    }

    // Pause final battle system timers (spheres, proximity checks)
    if (this._finalBattleSystem && this._finalBattleSystem.isActive) {
      this._finalBattleSystem.pause();
    }

    // Pause level event system timers (re-darken)
    if (this._levelEventSystem) {
      this._levelEventSystem.pause();
    }

    // Pause ambient sound system
    if (this._ambientSoundSystem) {
      this._ambientSoundSystem.pause();
    }

    // Pause moving wall system (cycle timers)
    if (this._movingWallSystem) {
      this._movingWallSystem.pause();
    }

    // Pause bat nest system (cooldown timers)
    if (this._batNestSystem) {
      this._batNestSystem.pause();
    }
  }

  /**
   * Resume spawner timers, physics, and player systems.
   * @private
   */
  _resumeGameSystems() {
    // Resume food spawner timer
    if (this.foodSpawner && this.foodSpawner.spawnTimer) {
      this.foodSpawner.spawnTimer.paused = false;
    }

    // Resume coin spawner timer
    if (this.coinSpawner && this.coinSpawner.spawnTimer) {
      this.coinSpawner.spawnTimer.paused = false;
    }

    // Resume physics
    this.physics.resume();

    // Resume poison timers if player is poisoned
    if (this._poisonDrainTimer) {
      this._poisonDrainTimer.paused = false;
    }
    if (this._poisonEndTimer) {
      this._poisonEndTimer.paused = false;
    }

    // Resume wounded timers if player is wounded
    if (this._woundedDrainTimer) {
      this._woundedDrainTimer.paused = false;
    }
    if (this._woundedEndTimer) {
      this._woundedEndTimer.paused = false;
    }

    // Resume hazard idle drain timers
    if (this._waterIdleDrainTimer) {
      this._waterIdleDrainTimer.paused = false;
    }
    if (this._lavaIdleDrainTimer) {
      this._lavaIdleDrainTimer.paused = false;
    }

    // Resume final battle system timers
    if (this._finalBattleSystem && this._finalBattleSystem.isActive) {
      this._finalBattleSystem.resume();
    }

    // Resume level event system timers
    if (this._levelEventSystem) {
      this._levelEventSystem.resume();
    }

    // Resume ambient sound system
    if (this._ambientSoundSystem) {
      this._ambientSoundSystem.resume();
    }

    // Resume moving wall system (cycle timers)
    if (this._movingWallSystem) {
      this._movingWallSystem.resume();
    }

    // Resume bat nest system (cooldown timers)
    if (this._batNestSystem) {
      this._batNestSystem.resume();
    }
  }

  /**
   * Create and display the pause menu modal: dark overlay, "PAUSED" title,
   * and a navigable menu with Continue and Quit options.
   * @private
   */
  _showPauseMenu() {
    const cam = this.cameras.main;
    const centerX = cam.scrollX + cam.width / 2;
    const centerY = cam.scrollY + cam.height / 2;

    // Dark overlay covering the entire visible camera area
    this._pauseOverlay = this.add.rectangle(
      centerX, centerY,
      cam.width * 2, cam.height * 2,
      0x000000, 0.75,
    );
    this._pauseOverlay.setDepth(300);
    this._pauseOverlay.setAlpha(0);

    // Fade in overlay
    this.tweens.add({
      targets: this._pauseOverlay,
      alpha: 1,
      duration: 200,
      ease: 'Quad.easeOut',
    });

    // "PAUSED" title
    const titleText = this.add.text(centerX, centerY - 60, 'PAUSED', {
      fontFamily: '"Courier New", Courier, monospace',
      fontSize: '32px',
      fontStyle: 'bold',
      color: '#ffffff',
    });
    titleText.setOrigin(0.5);
    titleText.setDepth(301);
    titleText.setAlpha(0);

    // Decorative line below title
    const lineG = this.add.graphics();
    lineG.setDepth(301);
    lineG.lineStyle(1, 0xffffff, 0.4);
    lineG.lineBetween(centerX - 80, centerY - 35, centerX + 80, centerY - 35);
    lineG.setAlpha(0);

    // Accumulated coins display (wallet + current session)
    const walletCoins = getWallet();
    const sessionCoins = this.coinsCollected || 0;
    const totalCoins = walletCoins + sessionCoins;
    const coinsText = this.add.text(
      centerX, centerY - 18,
      `COINS: ${totalCoins}`,
      {
        fontFamily: '"Courier New", Courier, monospace',
        fontSize: '13px',
        color: '#cccccc',
      },
    );
    coinsText.setOrigin(0.5);
    coinsText.setDepth(301);
    coinsText.setAlpha(0);

    // Breakdown: wallet + session
    const breakdownStr = `(wallet: ${walletCoins}  +  session: ${sessionCoins})`;
    const breakdownText = this.add.text(
      centerX, centerY - 3,
      breakdownStr,
      {
        fontFamily: '"Courier New", Courier, monospace',
        fontSize: '10px',
        color: '#888888',
      },
    );
    breakdownText.setOrigin(0.5);
    breakdownText.setDepth(301);
    breakdownText.setAlpha(0);

    // Menu items
    const menuItems = [
      { label: 'CONTINUE', action: 'continue' },
      { label: 'QUIT', action: 'quit' },
    ];

    const menuStartY = centerY + 35;
    const menuSpacing = 36;

    this._pauseMenuTexts = [];
    this._pauseSelectedIndex = 0;

    menuItems.forEach((item, index) => {
      const text = this.add.text(centerX, menuStartY + index * menuSpacing, item.label, {
        fontFamily: '"Courier New", Courier, monospace',
        fontSize: '18px',
        color: '#ffffff',
      });
      text.setOrigin(0.5);
      text.setDepth(301);
      text.setData('action', item.action);
      text.setAlpha(0);

      // Mouse support: hover to select, click to confirm
      text.setInteractive({ useHandCursor: true });
      text.on('pointerover', () => {
        if (this._pauseSelectedIndex !== index) {
          this._pauseSelectedIndex = index;
          this._updatePauseSelector(true);
          try { this.sound.play(AUDIO_MENU_SELECT, { volume: getSfxVol(0.5) }); } catch (_) {}
        }
      });
      text.on('pointerdown', () => {
        this._pauseSelectedIndex = index;
        this._confirmPauseSelection();
      });

      this._pauseMenuTexts.push(text);
    });

    // Selector arrow
    this._pauseSelectorArrow = this.add.text(0, 0, '>', {
      fontFamily: '"Courier New", Courier, monospace',
      fontSize: '18px',
      color: '#ffffff',
    }).setOrigin(0.5);
    this._pauseSelectorArrow.setDepth(301);
    this._pauseSelectorArrow.setAlpha(0);

    // Footer hint
    const footerText = this.add.text(centerX, centerY + 115, 'ESC to resume', {
      fontFamily: '"Courier New", Courier, monospace',
      fontSize: '11px',
      color: '#888888',
    });
    footerText.setOrigin(0.5);
    footerText.setDepth(301);
    footerText.setAlpha(0);

    // Store references to all pause UI elements for cleanup
    this._pauseUIElements = [titleText, lineG, coinsText, breakdownText, footerText];

    // Fade in all UI elements
    const allTargets = [titleText, lineG, coinsText, breakdownText, footerText, this._pauseSelectorArrow, ...this._pauseMenuTexts];
    this.tweens.add({
      targets: allTargets,
      alpha: 1,
      duration: 200,
      delay: 100,
      ease: 'Quad.easeOut',
    });

    // Update selector position
    this._updatePauseSelector();

    // Set up keyboard listeners for pause menu navigation
    this._setupPauseInput();
  }

  /**
   * Destroy all pause menu game objects with a fade-out animation,
   * then remove keyboard listeners.
   * @private
   */
  _hidePauseMenu() {
    // Remove pause-specific keyboard listeners immediately so they
    // can't fire during the fade-out animation
    this._teardownPauseInput();

    // Collect all UI targets for a single fade-out tween
    const fadeTargets = [];

    if (this._pauseOverlay) fadeTargets.push(this._pauseOverlay);
    if (this._pauseSelectorArrow) fadeTargets.push(this._pauseSelectorArrow);
    for (const text of this._pauseMenuTexts) {
      if (text && text.active) fadeTargets.push(text);
    }
    if (this._pauseUIElements) {
      for (const el of this._pauseUIElements) {
        if (el && el.active !== false) fadeTargets.push(el);
      }
    }

    if (fadeTargets.length === 0) return;

    // Fade out all elements, then destroy them
    this.tweens.add({
      targets: fadeTargets,
      alpha: 0,
      duration: 200,
      ease: 'Quad.easeIn',
      onComplete: () => {
        // Destroy overlay
        if (this._pauseOverlay) {
          this._pauseOverlay.destroy();
          this._pauseOverlay = null;
        }

        // Destroy menu texts
        for (const text of this._pauseMenuTexts) {
          if (text && text.active) text.destroy();
        }
        this._pauseMenuTexts = [];

        // Destroy selector arrow
        if (this._pauseSelectorArrow) {
          this._pauseSelectorArrow.destroy();
          this._pauseSelectorArrow = null;
        }

        // Destroy extra UI elements (title, line, footer)
        if (this._pauseUIElements) {
          for (const el of this._pauseUIElements) {
            if (el && el.destroy) el.destroy();
          }
          this._pauseUIElements = null;
        }
      },
    });
  }

  /**
   * Set up keyboard listeners for navigating the pause menu.
   * These are separate from the game's cursor keys so they work
   * while the game is paused.
   * @private
   */
  _setupPauseInput() {
    const onUp = () => this._navigatePauseMenu(-1);
    const onDown = () => this._navigatePauseMenu(1);
    const onConfirm = () => this._confirmPauseSelection();

    this.input.keyboard.on('keydown-UP', onUp);
    this.input.keyboard.on('keydown-DOWN', onDown);
    this.input.keyboard.on('keydown-ENTER', onConfirm);
    this.input.keyboard.on('keydown-SPACE', onConfirm);

    // Store references for removal
    this._pauseKeyListeners = [
      { event: 'keydown-UP', fn: onUp },
      { event: 'keydown-DOWN', fn: onDown },
      { event: 'keydown-ENTER', fn: onConfirm },
      { event: 'keydown-SPACE', fn: onConfirm },
    ];
  }

  /**
   * Remove the pause menu keyboard listeners.
   * @private
   */
  _teardownPauseInput() {
    for (const listener of this._pauseKeyListeners) {
      this.input.keyboard.off(listener.event, listener.fn);
    }
    this._pauseKeyListeners = [];
  }

  /**
   * Navigate the pause menu up or down.
   * @private
   * @param {number} delta - Direction: -1 for up, +1 for down.
   */
  _navigatePauseMenu(delta) {
    this.sound.play(AUDIO_MENU_SELECT, { volume: getSfxVol(0.5) });

    const count = this._pauseMenuTexts.length;
    this._pauseSelectedIndex = ((this._pauseSelectedIndex + delta) % count + count) % count;
    this._updatePauseSelector(true);
  }

  /**
   * Update the selector arrow position to match the currently selected menu item.
   * When `animate` is true the arrow slides smoothly; otherwise it teleports.
   * @private
   * @param {boolean} [animate=false] - Whether to tween or teleport.
   */
  _updatePauseSelector(animate = false) {
    if (!this._pauseSelectorArrow || this._pauseMenuTexts.length === 0) return;
    const current = this._pauseMenuTexts[this._pauseSelectedIndex];
    const targetX = current.x - current.width / 2 - 20;
    const targetY = current.y;

    if (animate) {
      this.tweens.killTweensOf(this._pauseSelectorArrow);
      this.tweens.add({
        targets: this._pauseSelectorArrow,
        x: targetX,
        y: targetY,
        duration: 120,
        ease: 'Quad.easeOut',
      });
    } else {
      this._pauseSelectorArrow.setPosition(targetX, targetY);
    }
  }

  /**
   * Execute the currently selected pause menu action.
   * @private
   */
  _confirmPauseSelection() {
    this.sound.play(AUDIO_MENU_CLICK, { volume: getSfxVol(0.5) });

    const action = this._pauseMenuTexts[this._pauseSelectedIndex].getData('action');

    switch (action) {
      case 'continue':
        this._resumeGame();
        break;

      case 'quit':
        this._quitToTitle();
        break;
    }
  }

  /**
   * Quit the game and return to the title screen.
   * The game is already saved (auto-save was called when pausing).
   * @private
   */
  _quitToTitle() {
    // Clean up pause UI
    this._hidePauseMenu();

    // Resume physics so cleanup doesn't hang
    this.physics.resume();

    // Bank session coins into persistent wallet (shop items are preserved on quit)
    addToWallet(this.coinsCollected);

    // Stop all game systems and music
    this._cleanup();

    // Stop HUD scene
    this.scene.stop(SCENE_HUD);

    // Fade out, then return to title
    this.cameras.main.fadeOut(400, 0, 0, 0);
    this.cameras.main.once('camerafadeoutcomplete', () => {
      this.scene.start(SCENE_TITLE);
    });
  }

  // ---------------------------------------------------------------
  // Info Modal (I key)
  // ---------------------------------------------------------------

  /**
   * Show the info modal overlay with two sections:
   * 1. Items present in the current level
   * 2. Active shop items the player owns
   * @private
   */
  _showInfoModal() {
    if (this._isInfoOpen || this._transitionLock || this._isPaused) return;

    this._isInfoOpen = true;

    // Clean up any leftover modal objects from a previous fade-out
    if (this._infoOverlay || this._infoContainer || this._infoElements.length > 0) {
      this._destroyInfoModal();
      this._isInfoOpen = true; // _destroyInfoModal resets it
    }

    // Cancel zoom-out if active
    this._cancelZoomOut();

    // Pause game systems (same as pause menu)
    this._pauseGameSystems();

    // Play ESC/menu sound
    try { this.sound.play(AUDIO_ESC_PRESSED, { volume: getSfxVol(0.5) }); } catch (_) {}

    const cam = this.cameras.main;
    const centerX = cam.scrollX + cam.width / 2;
    const centerY = cam.scrollY + cam.height / 2;

    // --- Dark overlay ---
    this._infoOverlay = this.add.rectangle(
      centerX, centerY,
      cam.width * 2, cam.height * 2,
      0x000000, 0.8,
    );
    this._infoOverlay.setDepth(300);
    this._infoOverlay.setAlpha(0);

    // --- Build content lines ---
    const lines = this._buildInfoContent();

    // --- Modal dimensions ---
    const modalW = 540;
    const modalPadX = 24;
    const modalPadY = 20;
    const titleH = 36;
    const footerH = 30;
    const scrollHintH = 16;   // reserved space for the "↑↓ Scroll" hint
    const visibleH = cam.height - 80; // leave margins
    const scrollAreaH = visibleH - titleH - footerH - scrollHintH - modalPadY * 2;

    const modalLeft = centerX - modalW / 2;
    const modalTop = centerY - visibleH / 2;

    // --- Title ---
    const titleText = this.add.text(centerX, modalTop + modalPadY + 12, 'GAME INFO', {
      fontFamily: '"Courier New", Courier, monospace',
      fontSize: '22px',
      fontStyle: 'bold',
      color: '#ffffff',
    }).setOrigin(0.5).setDepth(301).setAlpha(0);

    // Decorative line below title
    const lineG = this.add.graphics().setDepth(301).setAlpha(0);
    lineG.lineStyle(1, 0xffffff, 0.4);
    const lineY = modalTop + modalPadY + titleH;
    lineG.lineBetween(centerX - 200, lineY, centerX + 200, lineY);

    // --- Scrollable content container ---
    const contentTop = modalTop + modalPadY + titleH + 8;
    const contentLeft = modalLeft + modalPadX;
    const contentW = modalW - modalPadX * 2;

    // Create mask for scroll area
    this._infoMaskGraphics = this.make.graphics({ x: 0, y: 0, add: false });
    this._infoMaskGraphics.fillStyle(0xffffff);
    this._infoMaskGraphics.fillRect(contentLeft, contentTop, contentW, scrollAreaH);
    this._infoMask = new Phaser.Display.Masks.GeometryMask(this, this._infoMaskGraphics);

    // Create container
    this._infoContainer = this.add.container(contentLeft, contentTop);
    this._infoContainer.setDepth(301);
    this._infoContainer.setMask(this._infoMask);
    this._infoContainer.setAlpha(0);

    // Render content lines into container
    let cy = 0;
    const lineSpacing = 2;
    const iconSize = 14;       // display size for inline icons
    const iconPad = 6;         // gap between icon and text
    const iconIndent = 8;      // left indent for item rows with icons
    const subExtraIndent = iconSize + iconPad; // sub-rows align with text after icon

    for (const line of lines) {
      const isHeader = line.type === 'header';
      const isSub = line.type === 'sub';
      const isSep = line.type === 'separator';
      const hasIcon = !isHeader && !isSub && !isSep && line.icon;

      if (isSep) {
        // Thin separator line
        const sepG = this.add.graphics();
        sepG.lineStyle(1, 0xffffff, 0.2);
        sepG.lineBetween(0, cy + 6, contentW, cy + 6);
        this._infoContainer.add(sepG);
        cy += 12;
        continue;
      }

      if (hasIcon) {
        // --- Item row with icon ---
        const iconX = iconIndent + iconSize / 2;
        const iconY = cy + iconSize / 2;

        // Subtle circular background behind icon
        const bg = this.add.circle(iconX, iconY, iconSize * 0.6, 0xffffff, 0.07);
        this._infoContainer.add(bg);

        // Icon sprite
        const icon = this.add.image(iconX, iconY, line.icon);
        icon.setDisplaySize(iconSize, iconSize);
        if (line.icon === TEX_FOOD_CHICKEN) icon.setRotation(Math.PI);
        this._infoContainer.add(icon);

        // Item name text (bold, bright white to stand out from description)
        const textX = iconIndent + iconSize + iconPad;
        const txt = this.add.text(textX, cy, line.text, {
          fontFamily: '"Courier New", Courier, monospace',
          fontSize: '11px',
          fontStyle: 'bold',
          color: '#ffffff',
          wordWrap: { width: contentW - textX - 8 },
        });
        this._infoContainer.add(txt);

        const rowH = Math.max(iconSize, txt.height);
        cy += rowH + lineSpacing;
        continue;
      }

      // --- Header, sub, or plain item without icon ---
      const fontSize = isHeader ? '14px' : '11px';
      const color = line.color || (isHeader ? '#ffffff' : isSub ? '#aaaaaa' : '#ffffff');
      const fontStyle = (isHeader || (!isSub)) ? 'bold' : 'normal';

      // Sub-rows indent past the icon area so descriptions align neatly
      const xPos = isHeader ? 0 : isSub ? (iconIndent + subExtraIndent) : 8;

      const txt = this.add.text(xPos, cy, line.text, {
        fontFamily: '"Courier New", Courier, monospace',
        fontSize,
        fontStyle,
        color,
        wordWrap: { width: contentW - xPos - 8 },
      });
      this._infoContainer.add(txt);

      cy += txt.height + lineSpacing;
    }

    const totalContentH = cy;
    this._infoScrollY = 0;
    this._infoMaxScrollY = Math.max(0, totalContentH - scrollAreaH);

    // --- Scroll indicator (only if content overflows) ---
    let scrollHintText = null;
    if (this._infoMaxScrollY > 0) {
      scrollHintText = this.add.text(
        centerX, contentTop + scrollAreaH + scrollHintH / 2,
        '\u2191\u2193 Scroll',
        {
          fontFamily: '"Courier New", Courier, monospace',
          fontSize: '10px',
          color: '#666666',
        },
      ).setOrigin(0.5).setDepth(301).setAlpha(0);
    }

    // --- Footer ---
    const footerText = this.add.text(
      centerX, modalTop + visibleH - modalPadY + 2,
      'I or ESC to close',
      {
        fontFamily: '"Courier New", Courier, monospace',
        fontSize: '11px',
        color: '#888888',
      },
    ).setOrigin(0.5).setDepth(301).setAlpha(0);

    // --- Store all elements for cleanup ---
    this._infoElements = [titleText, lineG, footerText];
    if (scrollHintText) this._infoElements.push(scrollHintText);

    // --- Fade in ---
    const allFadeTargets = [this._infoOverlay, titleText, lineG, footerText, this._infoContainer];
    if (scrollHintText) allFadeTargets.push(scrollHintText);

    this.tweens.add({
      targets: allFadeTargets,
      alpha: 1,
      duration: 200,
      ease: 'Quad.easeOut',
    });

    // --- Set up scroll input ---
    this._setupInfoInput();
  }

  /**
   * Build the text content for the info modal.
   * Returns an array of { type: 'header'|'item'|'sub'|'separator', text: string }.
   * @private
   * @returns {Array<{ type: string, text: string }>}
   */
  _buildInfoContent() {
    const lines = [];

    // ---- Section 1: Level Information ----
    lines.push({ type: 'header', text: 'LEVEL ' + this.level });

    // Active level event
    if (this._levelEventSystem && this._levelEventSystem.hasActiveEvent()) {
      const evtKey = this._levelEventSystem.getActiveEvent();
      const evtName = LEVEL_EVENT_NAMES[evtKey];
      const evtDesc = LEVEL_EVENT_DESCRIPTIONS[evtKey];
      if (evtName) {
        lines.push({ type: 'item', text: 'Event: ' + evtName, color: '#ffd700' });
        if (evtDesc) {
          lines.push({ type: 'sub', text: evtDesc });
        }
      }
    }

    // Current status effects
    const statusEffects = [];
    if (this._isPoisoned) statusEffects.push('Poisoned');
    if (this._isWounded) statusEffects.push('Bleeding');
    if (this._isGhostMode) statusEffects.push('Ghost Mode');
    if (this._isResting) statusEffects.push('Resting');
    if (statusEffects.length > 0) {
      lines.push({ type: 'item', text: 'Status: ' + statusEffects.join(', ') });
    }

    // ---- Section 2: Equipped Items ----
    const equipped = [];

    // Keys in inventory
    if (this._playerKeys.length > 0) {
      const keyCounts = {};
      for (const k of this._playerKeys) {
        keyCounts[k] = (keyCounts[k] || 0) + 1;
      }
      const keyStrs = [];
      if (keyCounts[KeyTier.GOLD]) keyStrs.push(`${keyCounts[KeyTier.GOLD]} gold`);
      if (keyCounts[KeyTier.COPPER]) keyStrs.push(`${keyCounts[KeyTier.COPPER]} copper`);
      if (keyCounts[KeyTier.METALLIC]) keyStrs.push(`${keyCounts[KeyTier.METALLIC]} metallic`);
      // Pick the best key icon
      const bestKeyIcon = keyCounts[KeyTier.GOLD] ? TEX_KEY_GOLD
        : keyCounts[KeyTier.COPPER] ? TEX_KEY_COPPER : TEX_KEY_METALLIC;
      equipped.push({
        text: 'Keys: ' + keyStrs.join(', '),
        icon: bestKeyIcon,
        desc: 'Press O next to a chest to open it with a matching key.',
      });
    }

    // Agmuths
    if (this._agmuthCount > 0) {
      equipped.push({
        text: 'Agmuth relics: ' + this._agmuthCount,
        icon: TEX_LOOT_AGMUTH,
        desc: 'Press A to phase through one wall.',
      });
    }

    // Compass
    if (this._hasCompass) {
      equipped.push({
        text: 'Compass',
        icon: TEX_COMPASS,
        desc: 'Passive. The needle on the HUD points toward the exit.',
      });
    }

    // Threia Totem
    if (this._hasThreiaTotem) {
      equipped.push({
        text: 'Threia Totem',
        icon: TEX_THREIA_TOTEM,
        desc: 'Press H rapidly to pray for Threia\'s Blessing (guaranteed). Also repels bats.',
      });
    }

    // Hammer charges
    if (this._hammerCharges > 0) {
      equipped.push({
        text: `Hammer of Threia [${this._hammerCharges} charges]`,
        icon: TEX_HAMMER_OF_THREIA,
        desc: 'Face a wall and press B rapidly (5 times) to break it.',
      });
    }

    // Active shop items (skip hammer — already shown above with combined charges)
    const activeShopItems = getActiveItemsList();
    for (const item of activeShopItems) {
      if (item.id === SHOP_ITEM_HAMMER_OF_THREIA && this._hammerCharges > 0) continue;
      const name = SHOP_ITEM_NAMES[item.id] || item.id;
      const category = SHOP_ITEM_CATEGORIES[item.id];
      let suffix = '';
      if (category === ItemCategory.DURABILITY && item.durability !== undefined) {
        suffix = ` [${item.durability}/${item.maxDurability}]`;
      } else if (category === ItemCategory.CONSUMABLE && item.count > 1) {
        suffix = ` x${item.count}`;
      }
      const texKey = SHOP_ITEM_TEXTURES[item.id] || null;
      const desc = this._getInfoItemDesc(item.id);
      equipped.push({ text: name + suffix, icon: texKey, desc });
    }

    if (equipped.length > 0) {
      lines.push({ type: 'separator', text: '' });
      lines.push({ type: 'header', text: 'EQUIPPED ITEMS' });
      lines.push({ type: 'separator', text: '' });
      for (const eq of equipped) {
        lines.push({ type: 'item', text: eq.text, icon: eq.icon || null });
        lines.push({ type: 'sub', text: eq.desc });
      }
    }

    lines.push({ type: 'separator', text: '' });

    // ---- Section 3: Items in this level (only those in visited cells) ----
    lines.push({ type: 'header', text: 'ITEMS IN THIS LEVEL' });
    lines.push({ type: 'separator', text: '' });

    let hasItems = false;

    // Exit marker
    if (this.exitMarker && this.exitMarker.active && this._isObjectVisible(this.exitMarker)) {
      lines.push({ type: 'item', text: 'Exit', icon: TEX_EXIT });
      lines.push({ type: 'sub', text: 'Reach the exit to complete the level.' });
      hasItems = true;
    }

    // Active food on the floor (only visible ones)
    if (this.foodGroup) {
      const visibleFood = this.foodGroup.getChildren().filter(f => f.active && this._isObjectVisible(f));
      if (visibleFood.length > 0) {
        lines.push({ type: 'item', text: `Food (${visibleFood.length} visible)`, icon: TEX_FOOD_CHICKEN });
        lines.push({ type: 'sub', text: 'Walk over to collect. Restores health.' });
        hasItems = true;
      }
    }

    // Active coins on the floor (only visible ones)
    if (this.coinGroup) {
      const visibleCoins = this.coinGroup.getChildren().filter(c => c.active && this._isObjectVisible(c));
      if (visibleCoins.length > 0) {
        lines.push({ type: 'item', text: `Coins (${visibleCoins.length} visible)`, icon: TEX_COIN });
        lines.push({ type: 'sub', text: 'Walk over to collect. Every 10 coins = extra life.' });
        hasItems = true;
      }
    }

    // Active keys on the floor (only visible ones)
    if (this.keyGroup) {
      const visibleKeys = this.keyGroup.getChildren().filter(k => k.active && this._isObjectVisible(k));
      if (visibleKeys.length > 0) {
        const tierNames = visibleKeys.map(k => k.tier || 'key');
        lines.push({ type: 'item', text: `Keys on floor (${tierNames.join(', ')})`, icon: TEX_KEY_METALLIC });
        lines.push({ type: 'sub', text: 'Walk over to pick up. Use O near a chest to open.' });
        hasItems = true;
      }
    }

    // Chests in the level (only visible unopened ones)
    if (this.chestSpawner && this.chestSpawner.activeItems) {
      const visibleChests = this.chestSpawner.activeItems.filter(
        c => c && c.active && !c.isOpen && this._isObjectVisible(c),
      );
      if (visibleChests.length > 0) {
        lines.push({ type: 'item', text: `Chests (${visibleChests.length} unopened)`, icon: TEX_CHEST_METALLIC });
        lines.push({ type: 'sub', text: 'Stand next to a chest and press O with a matching key.' });
        hasItems = true;
      }
    }

    // Chest loot on the floor (only visible ones)
    if (this.lootGroup) {
      const visibleLoot = this.lootGroup.getChildren().filter(l => l.active && this._isObjectVisible(l));
      if (visibleLoot.length > 0) {
        lines.push({ type: 'item', text: `Chest loot (${visibleLoot.length} on floor)`, icon: TEX_LOOT_STAR });
        lines.push({ type: 'sub', text: 'Walk over to collect dropped items from chests.' });
        hasItems = true;
      }
    }

    // Compass pickup (only if visible)
    if (this._compassItem && this._compassItem.active && this._isObjectVisible(this._compassItem)) {
      lines.push({ type: 'item', text: 'Compass', icon: TEX_COMPASS });
      lines.push({ type: 'sub', text: 'Walk over to pick up. Shows a needle pointing to exit.' });
      hasItems = true;
    }

    // Threia Totem pickup (only if visible)
    if (this._threiaTotemItem && this._threiaTotemItem.active && this._isObjectVisible(this._threiaTotemItem)) {
      lines.push({ type: 'item', text: 'Threia Totem', icon: TEX_THREIA_TOTEM });
      lines.push({ type: 'sub', text: 'Walk over to pick up. Guarantees Threia\'s Blessing and repels bats.' });
      hasItems = true;
    }

    // Manuscript pickup (only if visible)
    if (this._manuscriptItem && this._manuscriptItem.active && this._isObjectVisible(this._manuscriptItem)) {
      lines.push({ type: 'item', text: 'Manuscript', icon: TEX_MANUSCRIPT });
      lines.push({ type: 'sub', text: 'Walk over to collect. Contains ancient lore.' });
      hasItems = true;
    }

    // Hammer of Threia pickup (only if visible)
    if (this._hammerItem && this._hammerItem.active && this._isObjectVisible(this._hammerItem)) {
      lines.push({ type: 'item', text: 'Hammer of Threia', icon: TEX_HAMMER_OF_THREIA });
      lines.push({ type: 'sub', text: 'Walk over to collect. Break walls with B key.' });
      hasItems = true;
    }

    if (!hasItems) {
      lines.push({ type: 'sub', text: 'No visible items on the floor right now.' });
    }

    // ---- Section 3: Hazards (only those with at least one visited cell) ----
    const hazards = [];
    if (this.waterSystem && this.waterSystem.waterCells && this.waterSystem.waterCells.size > 0) {
      for (const key of this.waterSystem.waterCells) {
        const [c, r] = key.split(',').map(Number);
        if (this._isCellVisited(c, r)) {
          hazards.push({ name: 'Water', desc: 'Blue tiles. Increases health drain when walked on.', icon: TEX_WATER });
          break;
        }
      }
    }
    if (this.lavaSystem && this.lavaSystem.lavaCells && this.lavaSystem.lavaCells.size > 0) {
      for (const key of this.lavaSystem.lavaCells) {
        const [c, r] = key.split(',').map(Number);
        if (this._isCellVisited(c, r)) {
          hazards.push({ name: 'Lava', desc: 'Orange tiles. Heavy health drain when walked on.', icon: TEX_LAVA });
          break;
        }
      }
    }
    if (this.poisonSystem && this.poisonSystem.poisonCells && this.poisonSystem.poisonCells.size > 0) {
      for (const key of this.poisonSystem.poisonCells) {
        const [c, r] = key.split(',').map(Number);
        if (this._isCellVisited(c, r)) {
          hazards.push({ name: 'Poison', desc: 'Green tiles. Causes timed poison status + slowdown.', icon: TEX_POISON });
          break;
        }
      }
    }
    if (this.spikeSystem && this.spikeSystem.spikeCells && this.spikeSystem.spikeCells.size > 0) {
      for (const key of this.spikeSystem.spikeCells) {
        const [c, r] = key.split(',').map(Number);
        if (this._isCellVisited(c, r)) {
          hazards.push({ name: 'Spikes', desc: 'Sharp tiles. Causes timed bleeding + slowdown.', icon: TEX_SPIKE });
          break;
        }
      }
    }

    if (hazards.length > 0) {
      lines.push({ type: 'separator', text: '' });
      lines.push({ type: 'header', text: 'HAZARDS' });
      lines.push({ type: 'separator', text: '' });
      for (const h of hazards) {
        lines.push({ type: 'item', text: h.name, icon: h.icon });
        lines.push({ type: 'sub', text: h.desc });
      }
    }

    // Bats (only those in visited cells)
    if (this._batSystem && this._batSystem._bats) {
      const visibleBats = this._batSystem._bats.filter(b => b.active && this._isObjectVisible(b));
      if (visibleBats.length > 0) {
        lines.push({ type: 'separator', text: '' });
        lines.push({ type: 'header', text: 'CREATURES' });
        lines.push({ type: 'separator', text: '' });
        lines.push({ type: 'item', text: `Bats (${visibleBats.length} visible)`, icon: TEX_BAT_WINGS_UP });
        lines.push({ type: 'sub', text: 'May attack when you pick up items. Threia Totem repels them.' });
      }
    }

    // ---- Controls reminder ----
    lines.push({ type: 'separator', text: '' });
    lines.push({ type: 'header', text: 'CONTROLS' });
    lines.push({ type: 'separator', text: '' });
    lines.push({ type: 'item', text: 'Arrows: Move  |  O: Open chest  |  A: Agmuth' });
    lines.push({ type: 'item', text: 'H: Threia prayer  |  B: Hammer  |  Z: Map' });
    lines.push({ type: 'item', text: 'TAB: Mini-map  |  M: Music  |  I: This screen' });
    lines.push({ type: 'item', text: 'ESC: Pause menu' });

    return lines;
  }

  /**
   * Get a brief in-game description for a shop item (shorter than SHOP_ITEM_DESCRIPTIONS).
   * @private
   * @param {string} itemId
   * @returns {string}
   */
  _getInfoItemDesc(itemId) {
    const shortDescs = {
      'threias-guide': 'May reveal the entire maze at level start.',
      'heart-potion': 'Auto-restores a heart when you die.',
      'totem-voracious': 'Nearby food moves toward you.',
      'agmuth-pendant': '+30% Agmuth drops from chests.',
      'lava-ring': 'Immune to lava damage.',
      'poison-ring': 'Immune to poison effects.',
      'water-ring': 'Immune to water damage.',
      'key-pendant': '+20% key spawn rate.',
      'heart-pendant': '20% less energy drain per step.',
      'elemental-ring': 'Immune to lava, water, and poison.',
      'tear-of-threia': 'Prevents game over once.',
      'hammer-of-threia': 'Face a wall and press B rapidly (5 times) to break it.',
    };
    return shortDescs[itemId] || SHOP_ITEM_DESCRIPTIONS[itemId] || '';
  }

  /**
   * Count unopened chests in the level (fallback if ChestSpawner lacks the method).
   * @private
   * @returns {number}
   */
  _countUnopenedChests() {
    if (!this.chestSpawner || !this.chestSpawner.activeItems) return 0;
    return this.chestSpawner.activeItems.filter(c => c && c.active && !c.isOpen).length;
  }

  /**
   * Check if a grid cell has been visited (seen) by the player.
   * @private
   * @param {number} col  Grid column index
   * @param {number} row  Grid row index
   * @returns {boolean}
   */
  _isCellVisited(col, row) {
    if (!this.visited) return false;
    if (row < 0 || row >= this.mazeGridHeight || col < 0 || col >= this.mazeGridWidth) return false;
    return this.visited[row][col] === 1;
  }

  /**
   * Get the grid column from a world-pixel x coordinate.
   * @private
   * @param {number} x  World x in pixels
   * @returns {number}
   */
  _pixelToCol(x) { return Math.floor(x / TILE_SIZE); }

  /**
   * Get the grid row from a world-pixel y coordinate.
   * @private
   * @param {number} y  World y in pixels
   * @returns {number}
   */
  _pixelToRow(y) { return Math.floor(y / TILE_SIZE); }

  /**
   * Check if a game object sitting on the grid is in a visited cell.
   * Works with entities that have gridCol/gridRow OR pixel x/y.
   * @private
   * @param {Phaser.GameObjects.GameObject} obj
   * @returns {boolean}
   */
  _isObjectVisible(obj) {
    const col = obj.gridCol != null ? obj.gridCol : this._pixelToCol(obj.x);
    const row = obj.gridRow != null ? obj.gridRow : this._pixelToRow(obj.y);
    return this._isCellVisited(col, row);
  }

  /**
   * Set up keyboard and mouse wheel listeners for the info modal.
   * @private
   */
  _setupInfoInput() {
    const onScroll = (delta) => {
      if (this._infoMaxScrollY <= 0) return;
      this._infoScrollY = Phaser.Math.Clamp(
        this._infoScrollY + delta,
        0,
        this._infoMaxScrollY,
      );
      this._updateInfoScroll();
    };

    const onUp = () => onScroll(-30);
    const onDown = () => onScroll(30);

    this.input.keyboard.on('keydown-UP', onUp);
    this.input.keyboard.on('keydown-DOWN', onDown);

    // Mouse wheel support
    const onWheel = (pointer, gameObjects, deltaX, deltaY) => {
      if (this._isInfoOpen) {
        onScroll(deltaY > 0 ? 30 : -30);
      }
    };
    this.input.on('wheel', onWheel);

    this._infoKeyListeners = [
      { event: 'keydown-UP', fn: onUp, target: this.input.keyboard },
      { event: 'keydown-DOWN', fn: onDown, target: this.input.keyboard },
      { event: 'wheel', fn: onWheel, target: this.input },
    ];
  }

  /**
   * Update the scroll position of the info container.
   * @private
   */
  _updateInfoScroll() {
    if (!this._infoContainer) return;
    const cam = this.cameras.main;
    const centerX = cam.scrollX + cam.width / 2;
    const centerY = cam.scrollY + cam.height / 2;
    const modalW = 540;
    const modalPadX = 24;
    const modalPadY = 20;
    const titleH = 36;
    const visibleH = cam.height - 80;
    const modalLeft = centerX - modalW / 2;
    const contentTop = (centerY - visibleH / 2) + modalPadY + titleH + 8;
    const contentLeft = modalLeft + modalPadX;

    this._infoContainer.setPosition(contentLeft, contentTop - this._infoScrollY);
  }

  /**
   * Hide the info modal with a fade-out animation, then destroy all elements.
   * @private
   */
  _hideInfoModal() {
    if (!this._isInfoOpen) return;

    // Reset flag immediately to prevent race conditions on rapid I-key presses
    this._isInfoOpen = false;

    try { this.sound.play(AUDIO_ESC_PRESSED, { volume: getSfxVol(0.5) }); } catch (_) {}

    // Remove input listeners immediately
    this._teardownInfoInput();

    // Collect all fade targets
    const fadeTargets = [];
    if (this._infoOverlay) fadeTargets.push(this._infoOverlay);
    if (this._infoContainer) fadeTargets.push(this._infoContainer);
    for (const el of this._infoElements) {
      if (el && el.active !== false) fadeTargets.push(el);
    }

    if (fadeTargets.length === 0) {
      this._destroyInfoModal();
      this._resumeGameSystems();
      return;
    }

    this.tweens.add({
      targets: fadeTargets,
      alpha: 0,
      duration: 200,
      ease: 'Quad.easeIn',
      onComplete: () => {
        this._destroyInfoModal();
        this._resumeGameSystems();
      },
    });
  }

  /**
   * Destroy all info modal game objects (called after fade-out or during cleanup).
   * @private
   */
  _destroyInfoModal() {
    this._isInfoOpen = false;

    // Destroy overlay
    if (this._infoOverlay) {
      this._infoOverlay.destroy();
      this._infoOverlay = null;
    }

    // Destroy container (also destroys all children)
    if (this._infoContainer) {
      this._infoContainer.setMask(null);
      this._infoContainer.destroy(true);
      this._infoContainer = null;
    }

    // Destroy mask
    if (this._infoMask) {
      this._infoMask.destroy();
      this._infoMask = null;
    }
    if (this._infoMaskGraphics) {
      this._infoMaskGraphics.destroy();
      this._infoMaskGraphics = null;
    }

    // Destroy extra UI elements (title, lines, footer)
    for (const el of this._infoElements) {
      if (el && el.destroy) el.destroy();
    }
    this._infoElements = [];

    // Ensure input listeners are removed
    this._teardownInfoInput();

    this._infoScrollY = 0;
    this._infoMaxScrollY = 0;
  }

  /**
   * Remove info modal keyboard/mouse listeners.
   * @private
   */
  _teardownInfoInput() {
    for (const listener of this._infoKeyListeners) {
      if (listener.target) {
        listener.target.off(listener.event, listener.fn);
      }
    }
    this._infoKeyListeners = [];
  }

  // ---------------------------------------------------------------
  // Events
  // ---------------------------------------------------------------
  /** @private */
  _setupEvents() {
    // Player moved -> drain health, reveal fog, update spawner, check exit
    this.eventBus.on(Events.PLAYER_MOVED, (col, row) => {
      // Guard: cleanup may have already run (e.g. game over triggered by
      // drainOnMove while the movement tween's onComplete is still firing).
      if (!this.player) return;

      // Stop resting if the player was idle-resting
      this._lastMoveTime = Date.now();
      if (this._isResting) {
        this._stopResting();
      }

      // Stop any active hazard idle drain timers (the per-move drain handles this step)
      this._stopWaterIdleDrain();
      this._stopLavaIdleDrain();

      // Count newly revealed fog cells BEFORE revealing (for bat spawning)
      const newCells = this._countNewFogCells(col, row);

      // Reveal fog around the player's new position
      this._revealFog(col, row);

      // Update direction trail (append new position, trim to TRAIL_LENGTH)
      this._updateTrail(col, row);

      // Explorer achievement: check if 100% of maze fog is explored
      if (!isAchievementUnlocked('explorer') && this._isFogFullyExplored()) {
        this._tryUnlockAchievement('explorer');
      }

      // Try to spawn bats based on newly revealed cells (level 2+)
      if (this._batSystem) {
        this._batSystem.trySpawn(col, row, newCells);
      }

      // Update ambient sound system with player position (proximity-based sounds)
      if (this._ambientSoundSystem) {
        this._ambientSoundSystem.updatePlayerPosition(col, row, {
          water: this.waterSystem ? this.waterSystem.waterCells : null,
          lava: this.lavaSystem ? this.lavaSystem.lavaCells : null,
        });
      }

      // Bat nest system: check proximity to nests (may activate and spawn bats)
      if (this._batNestSystem) {
        this._batNestSystem.checkProximity(col, row);

        // Traversal penalty: walking onto a nest cell costs extra damage + coins
        if (this._batNestSystem.hasNestAt(col, row)) {
          const nestDmg = SHADOW_WALKER_DAMAGE * BAT_NEST_TRAVERSE_DAMAGE_MULT;
          this.healthSystem.takeDamage(nestDmg);

          const coinLoss = Math.min(
            SHADOW_WALKER_COIN_STEAL * BAT_NEST_TRAVERSE_COIN_MULT,
            this.coinsCollected,
          );
          if (coinLoss > 0) {
            this.coinsCollected -= coinLoss;
            this.eventBus.emit(Events.COINS_CHANGED, this.coinsCollected);
          }

          this.eventBus.emit(Events.BAT_NEST_TRAVERSED, { col, row, damage: nestDmg, coinLoss });

          // Brief red flash + camera shake feedback
          if (this.cameras && this.cameras.main) {
            this.cameras.main.flash(200, 80, 0, 0, true);
            this.cameras.main.shake(150, 0.005);
          }
        }
      }

      // --- Treasure room / final battle arena: skip ALL health drain, hazards, and status effects ---
      // Note: isInArena (not isActive) — during the early final battle phases
      // (portal materializing, Malachar modal, walking to exit) the player is
      // still in the normal maze and should drain energy normally.
      if (!this._inTreasureRoom && !(this._finalBattleSystem && this._finalBattleSystem.isInArena)) {

      // Water hazard: double drain when walking on water tiles
      const onWater = this.waterSystem && this.waterSystem.isWater(col, row);
      // Lava hazard: much higher drain when walking on lava tiles
      const onLava = this.lavaSystem && this.lavaSystem.isLava(col, row);

      // Check shop ring immunities (Elemental Ring takes priority over individual rings)
      const elementalRingActive = isElementalRingActive();
      const hasWaterImmunity = elementalRingActive || isItemActive(SHOP_ITEM_WATER_RING);
      const hasLavaImmunity = elementalRingActive || isItemActive(SHOP_ITEM_LAVA_RING);
      const hasPoisonImmunity = elementalRingActive || isItemActive(SHOP_ITEM_POISON_RING);

      // Update hazard tile flags (rings don't change detection, only damage)
      this._onWaterTile = !!onWater;
      this._onLavaTile = !!onLava;

      // Determine drain multiplier — lava and water are mutually exclusive
      // Ring immunity nullifies the extra drain (still applies base 1× drain)
      let drainMult = 1;
      if (onLava && !hasLavaImmunity) {
        drainMult = LAVA_DRAIN_MULTIPLIER;
      } else if (onWater && !hasWaterImmunity) {
        drainMult = WATER_DRAIN_MULTIPLIER;
      }

      // Degrade ring durability on hazard steps (1 charge per protected step).
      // When Elemental Ring is active, only consume 1 charge total regardless
      // of how many hazard types overlap on this tile.
      let elementalDegradedThisStep = false;
      if (onWater && hasWaterImmunity) {
        if (!elementalRingActive || !elementalDegradedThisStep) {
          this._degradeRingForHazard(elementalRingActive, SHOP_ITEM_WATER_RING);
          if (elementalRingActive) elementalDegradedThisStep = true;
        }
      }
      if (onLava && hasLavaImmunity) {
        if (!elementalRingActive || !elementalDegradedThisStep) {
          this._degradeRingForHazard(elementalRingActive, SHOP_ITEM_LAVA_RING);
          if (elementalRingActive) elementalDegradedThisStep = true;
        }
      }

      // Heart Pendant: reduce per-step drain (applies to base + hazard drain)
      if (isItemActive(SHOP_ITEM_HEART_PENDANT)) {
        drainMult *= (1 - HEART_PENDANT_DRAIN_REDUCTION);
        // Consume 1 durability charge per step
        const hpResult = degradeItem(SHOP_ITEM_HEART_PENDANT);
        if (this.eventBus) {
          this.eventBus.emit(Events.ITEM_DURABILITY_CHANGED, SHOP_ITEM_HEART_PENDANT, hpResult.remaining);
        }
        if (hpResult.broken) {
          this._onItemBroken(SHOP_ITEM_HEART_PENDANT);
        }
      }

      this.healthSystem.drainOnMove(drainMult);

      // Play or stop water walk sound based on whether we're on water
      if (onWater) {
        this._playWaterWalkSound();
      } else {
        this._stopWaterWalkSound();
      }

      // Play or stop lava walk sound and visual effects based on whether we're on lava
      if (onLava) {
        this._playLavaWalkSound();
        this._startLavaEffects();
      } else {
        this._stopLavaWalkSound();
        this._stopLavaEffects();
      }

      // Poison hazard: apply poison status effect when stepping on poison tiles
      // (Poison Ring grants full immunity — skip status application)
      const onPoison = this.poisonSystem && this.poisonSystem.isPoison(col, row);
      if (onPoison && !this._isPoisoned && !hasPoisonImmunity) {
        this._applyPoison();
      }

      // Degrade poison ring durability on protected poison steps
      if (onPoison && hasPoisonImmunity) {
        if (!elementalRingActive || !elementalDegradedThisStep) {
          this._degradeRingForHazard(elementalRingActive, SHOP_ITEM_POISON_RING);
        }
      }

      // Play or stop poison walk sound based on whether we're on poison
      if (onPoison) {
        this._playPoisonWalkSound();
      } else {
        this._stopPoisonWalkSound();
      }

      // Spike hazard: apply wounded status effect when stepping on spike tiles
      const onSpike = this.spikeSystem && this.spikeSystem.isSpike(col, row);
      if (onSpike && !this._isWounded) {
        this._applyWounded();
      }

      // Play or stop spike walk sound based on whether we're on spikes
      if (onSpike) {
        this._playSpikeWalkSound();
      } else {
        this._stopSpikeWalkSound();
      }

      // drainOnMove may trigger game over synchronously, which calls
      // _cleanup and nullifies everything. Bail out if that happened.
      if (!this.player) return;

      // Start idle drain timers for the tile the player just arrived at
      // (will tick while the player stands still on the hazard)
      // Timers now run regardless of immunity — callbacks handle ring
      // durability degradation if immune, or health drain if not.
      if (onWater) {
        this._startWaterIdleDrain();
      }
      if (onLava) {
        this._startLavaIdleDrain();
      }

      // Ring glow events: notify HUD when a ring is protecting the player
      this._emitRingGlowEvents(onWater, onLava, onPoison, hasWaterImmunity, hasLavaImmunity, hasPoisonImmunity);

      } // end if (!this._inTreasureRoom)

      this._checkExitReached(col, row);

      // _checkExitReached may trigger level transition synchronously,
      // which calls _cleanup and nullifies the player. Bail out if so.
      if (!this.player) return;

      // Notify Threia system that the player moved (cancels prayer if active)
      if (this._threiaSystem) {
        this._threiaSystem.onPlayerMoved();
      }

      this._autoSave();
    });

    // Life lost -> play heart loss sound + player red pulse effect
    this.eventBus.on(Events.LIFE_LOST, () => {
      this._playHeartLossSound();
      if (this.player) {
        this.player.playLifeLostEffect();
      }
    });

    // Game over
    this.eventBus.on(Events.GAME_OVER, () => {
      this._onGameOver();
    });

    // Heart Potion auto-used — prevented a life loss
    this.eventBus.on(Events.HEART_POTION_USED, () => {
      this._onHeartPotionUsed();
    });

    // Tear of Threia resurrection — prevents game over
    this.eventBus.on(Events.TEAR_OF_THREIA_USED, () => {
      this._onTearOfThreiaUsed();
    });

    // Threia Totem consumed by blessing → update persistent flag
    this.eventBus.on(Events.THREIA_TOTEM_CONSUMED, () => {
      this._hasThreiaTotem = false;
      this._autoSave();
    });

    // Threia blessing attempted → mark used so save/restore won't allow re-attempt
    this.eventBus.on(Events.THREIA_BLESSING_START, () => {
      this._threiaBlessingUsed = true;
      this._autoSave();
    });

    // Music toggle from HUD
    this.eventBus.on(Events.MUSIC_TOGGLED, (enabled) => {
      this.musicEnabled = enabled;
      saveMusicPref(enabled);
      if (enabled) {
        this._startGameMusic();
      } else {
        this._stopGameMusic();
      }
    });

    // Hammer charges changed (from HammerSystem after breaking a wall)
    this.eventBus.on(Events.HAMMER_CHARGES_CHANGED, (charges) => {
      const prevCharges = this._hammerCharges;
      this._hammerCharges = charges;

      // If charges decreased, degrade shop durability and update HUD arc
      if (charges < prevCharges && isItemActive(SHOP_ITEM_HAMMER_OF_THREIA)) {
        const result = degradeItem(SHOP_ITEM_HAMMER_OF_THREIA);
        this.eventBus.emit(
          Events.ITEM_DURABILITY_CHANGED,
          SHOP_ITEM_HAMMER_OF_THREIA,
          result.remaining,
        );
        if (result.broken) {
          this._onItemBroken(SHOP_ITEM_HAMMER_OF_THREIA);
        }
      }

      this._autoSave();
    });

    // ---------------------------------------------------------------
    // Achievement tracking listeners
    // ---------------------------------------------------------------

    // Ghost: count Agmuth ghost mode uses this run
    this.eventBus.on(Events.AGMUTH_DEACTIVATED, () => {
      this._achGhostUses += 1;
      if (this._achGhostUses >= ACHIEVEMENT_GHOST_USES) {
        this._tryUnlockAchievement('ghost');
      }
    });

    // Devoted: count Threia blessings (lifetime total)
    this.eventBus.on(Events.THREIA_BLESSING_SUCCESS, () => {
      this._achBlessingsTotal += 1;
      if (this._achBlessingsTotal >= ACHIEVEMENT_PRAY_BLESSINGS) {
        this._tryUnlockAchievement('pray');
      }
    });

    // Destroyer: count walls broken this run
    this.eventBus.on(Events.HAMMER_WALL_BROKEN, () => {
      this._achWallsBroken += 1;
      if (this._achWallsBroken >= ACHIEVEMENT_DESTROYER_WALLS) {
        this._tryUnlockAchievement('destroyer');
      }
    });

    // Treasure Hunter: count chests opened this run
    this.eventBus.on(Events.CHEST_OPENED, () => {
      this._achChestsOpened += 1;
      if (this._achChestsOpened >= ACHIEVEMENT_TREASURE_HUNTER_CHESTS) {
        this._tryUnlockAchievement('treasure_hunter');
      }
    });

    // Hoarder: check coins collected this run
    this.eventBus.on(Events.COINS_CHANGED, () => {
      if (this.coinsCollected >= ACHIEVEMENT_HOARDER_COINS) {
        this._tryUnlockAchievement('hoarder');
      }
    });

    // Scholar: check manuscript count after collection
    this.eventBus.on(Events.MANUSCRIPT_COLLECTED, () => {
      if (getManuscriptCount() >= ACHIEVEMENT_SCHOLAR_MANUSCRIPTS) {
        this._tryUnlockAchievement('scholar');
      }
    });

    // Vorath Defeated: final battle victory
    this.eventBus.on(Events.FINAL_VORATH_DESTROYED, () => {
      this._tryUnlockAchievement('vorath_defeated');
    });

    // ---------------------------------------------------------------
    // Shadow Walker hit: steal coins + deal damage
    // ---------------------------------------------------------------
    this.eventBus.on(Events.SHADOW_WALKER_HIT, () => {
      // Steal coins
      if (this.coinsCollected > 0) {
        const stolen = Math.min(SHADOW_WALKER_COIN_STEAL, this.coinsCollected);
        this.coinsCollected -= stolen;
        this.eventBus.emit(Events.COINS_CHANGED, this.coinsCollected);
      }
      // Damage is already applied by ShadowWalkerSystem via healthSystem
    });
  }

  /**
   * Try to unlock an achievement. If newly unlocked, emits ACHIEVEMENT_UNLOCKED
   * event for HUD notification.
   * @private
   * @param {string} achievementId
   */
  _tryUnlockAchievement(achievementId) {
    const newlyUnlocked = unlockAchievement(achievementId);
    if (newlyUnlocked) {
      this.eventBus.emit(Events.ACHIEVEMENT_UNLOCKED, achievementId);
    }
  }

  /**
   * Check if 100% of the maze fog has been explored (Explorer achievement).
   * @private
   * @returns {boolean}
   */
  _isFogFullyExplored() {
    if (!this.visited || !this.mazeGrid) return false;
    for (let r = 0; r < this.mazeGridHeight; r++) {
      for (let c = 0; c < this.mazeGridWidth; c++) {
        if (this.mazeGrid[r][c] === 0 && !this.visited[r][c]) {
          return false;
        }
      }
    }
    return true;
  }

  /** @private Update the FoodSpawner's, CoinSpawner's, and KeySpawner's reference to the player position. */
  _updateSpawnerPlayerPos(col, row) {
    if (this.foodSpawner) {
      this.foodSpawner.playerPos.col = col;
      this.foodSpawner.playerPos.row = row;
    }
    if (this.coinSpawner) {
      this.coinSpawner.playerPos.col = col;
      this.coinSpawner.playerPos.row = row;
    }
    if (this.keySpawner) {
      this.keySpawner.playerPos.col = col;
      this.keySpawner.playerPos.row = row;
    }
  }

  /**
   * Check if the player reached the exit.
   * @private
   */
  _checkExitReached(col, row) {
    // Skip exit check while the final battle is active — the arena has
    // its own exit logic handled by FinalBattleSystem.
    if (this._finalBattleSystem && this._finalBattleSystem.isActive) return;

    // Normal exit — skip check while inside the treasure room (the
    // generated bonus maze has an exitPos but no exit marker; reaching
    // that cell must NOT trigger level completion).
    if (
      !this._inTreasureRoom &&
      col === this.exitPos.col &&
      row === this.exitPos.row
    ) {
      this._onLevelComplete();
      return;
    }

    // Golden exit (Vorath's Treasure Room)
    if (
      this._hasGoldenExit &&
      this._goldenExitPos &&
      !this._goldenExitSpent &&
      !this._inTreasureRoom &&
      col === this._goldenExitPos.col &&
      row === this._goldenExitPos.row
    ) {
      this._enterTreasureRoom();
    }
  }

  // ---------------------------------------------------------------
  // Level transitions
  // ---------------------------------------------------------------

  /** @private */
  _onLevelComplete() {
    // Award time bonus before advancing
    if (this.scoreSystem) {
      this.scoreSystem.addLevelTimeBonus();
    }

    this.level += 1;
    this.eventBus.emit(Events.LEVEL_COMPLETE, this.level);

    // Survivor achievement: reach level N on Hard difficulty
    if (this.level >= ACHIEVEMENT_SURVIVOR_LEVEL && getDifficulty() === DIFFICULTY_HARD) {
      this._tryUnlockAchievement('survivor');
    }
    // Lock input during the entire transition
    this._transitionLock = true;

    // Cancel zoom-out if active
    this._cancelZoomOut();

    // Stop key spawner (keys in inventory persist across levels)
    if (this.keySpawner) {
      this.keySpawner.stop();
    }

    // Stop game music — the level-complete sound replaces it
    this._stopGameMusic();

    // Begin the level-complete cinematic sequence
    this._playLevelCompleteSequence();
  }

  /**
   * Play the full level-complete cinematic:
   * 1. Play level-complete sound
   * 2. Glow particles emanate from the player ball for the duration of the sound
   * 3. Shortly before the sound ends, intensify glow → white flash on game area
   * 4. After flash, build next level and show announcement
   * @private
   */
  _playLevelCompleteSequence() {
    // --- 1. Play the level-complete sound ---
    const completeSound = this.sound.add(AUDIO_LEVEL_COMPLETE, {
      volume: getSfxVol(0.6),
    });
    completeSound.play();

    // Get the sound duration (in seconds); fallback to 3s if unknown
    const soundDurationMs = (completeSound.duration || 3) * 1000;

    // --- 2. Start glow particles around the player — bright, upward-drifting victory effect ---
    const playerX = this.player.x;
    const playerY = this.player.y;

    const glowEmitter = this.add.particles(playerX, playerY, TEX_GLOW, {
      lifespan: { min: 500, max: 1000 },
      speed: { min: 30, max: 100 },
      angle: { min: 220, max: 320 },  // Upward spread for a celebratory feel
      scale: { start: 0.9, end: 0 },
      alpha: { start: 1.0, end: 0 },
      frequency: 40,
      quantity: 4,
      emitting: true,
    });
    glowEmitter.setDepth(this.player.depth - 1);

    // Gradually intensify glow over time
    const intensifyTime = Math.max(soundDurationMs * 0.5, 500);
    this.time.delayedCall(intensifyTime, () => {
      if (!glowEmitter || !this.scene) return;
      // Increase emission rate and particle count
      glowEmitter.frequency = 20;
      glowEmitter.quantity = 8;
    });

    // --- 3. White flash shortly before sound ends, then transition ---
    const flashLeadTime = 400; // ms before sound ends to trigger flash
    const flashDelay = Math.max(soundDurationMs - flashLeadTime, 500);

    this.time.delayedCall(flashDelay, () => {
      if (!this.scene) return;

      // Stop glow emitter
      glowEmitter.stop();

      // White flash on the game camera only (not HUD)
      this.cameras.main.flash(500, 255, 255, 255);

      // Clean up current level after the flash peaks
      this.time.delayedCall(250, () => {
        if (!this.scene) return;

        // Destroy glow particles
        glowEmitter.destroy();

        // Signal compass to disintegrate before cleanup
        if (this._hasCompass) {
          this.eventBus.emit(Events.COMPASS_DISINTEGRATE);
        }

        // Clean up and build next
        this._cleanup();

        this.time.delayedCall(300, () => {
          if (!this.scene) return;
          this._buildNextLevel();
        });
      });
    });

    // Clean up sound when done
    completeSound.once('complete', () => {
      completeSound.destroy();
    });
  }

  /** @private Build the next maze level. */
  _buildNextLevel() {
    const dims = MazeGenerator.dimensionsForLevel(this.level);
    const gen = new MazeGenerator(dims.cols, dims.rows);
    const result = gen.generate();

    this.mazeGrid = result.grid;
    this.mazeGridWidth = result.gridWidth;
    this.mazeGridHeight = result.gridHeight;
    this.mazeSeed = result.seed;
    this.startPos = result.start;
    this.exitPos = result.exit;
    this.playerStartCol = result.start.col;
    this.playerStartRow = result.start.row;

    this._initVisited();

    // Rebuild everything
    this._buildMaze();
    this._createExitMarker();

    this.player = new Player(
      this,
      result.start.col,
      result.start.row,
      this.mazeGrid,
      this.eventBus,
    );

    this._initWaterSystem();
    this._initLavaSystem();
    this._initPoisonSystem();
    this._initSpikeSystem();
    this._initFog();
    this._revealFog(this.player.gridCol, this.player.gridRow);

    // Reset direction trail for the new level and recreate graphics
    this._playerTrail = [];
    this._trailGraphics = this.add.graphics();
    this._trailGraphics.setDepth(1);

    // Restart food spawner
    this.foodGroup = this.physics.add.group();
    const forbiddenCells = this._getCombinedForbiddenCells();
    this.foodSpawner = new FoodSpawner(
      this,
      this.mazeGrid,
      this.foodGroup,
      { col: result.start.col, row: result.start.row },
      this.exitPos,
      forbiddenCells,
      this.player,
    );
    this.foodSpawner.start();

    this.physics.add.overlap(
      this.player,
      this.foodGroup,
      this._onFoodCollected,
      null,
      this,
    );

    // Restart coin spawner
    this.coinGroup = this.physics.add.group();
    this.coinSpawner = new CoinSpawner(
      this,
      this.mazeGrid,
      this.coinGroup,
      { col: result.start.col, row: result.start.row },
      this.exitPos,
      forbiddenCells,
      this.player,
    );
    this.coinSpawner.start();

    this.physics.add.overlap(
      this.player,
      this.coinGroup,
      this._onCoinCollected,
      null,
      this,
    );

    // Cross-link spawners so no items share a cell
    // Reinitialize key and chest spawners for the new level
    this._initChestSpawner();
    this._initKeySpawner();
    this._initManuscript();
    this._initCompass();
    this._initThreiaTotem();
    this._initHammer();
    this._crossLinkSpawners();

    // Reset health for new level
    this.healthSystem.resetHealth();

    // Reset Threia blessing for the new level (once-per-maze limit)
    this._threiaBlessingUsed = false;
    this._threiaSystem = new ThreiaSystem(this, this.eventBus, this.healthSystem, this.player);
    this._threiaSystem.setHasTotem(this._hasThreiaTotem);
    this._threiaSystem.setBlessingUsed(false);

    // Reset bat system for the new level (fresh spawns, no carry-over)
    if (this._batSystem) {
      this._batSystem.destroy();
    }
    this._batSystem = new BatSystem(
      this,
      this.eventBus,
      this.player,
      this.visited,
      this.mazeGridWidth,
      this.mazeGridHeight,
      this.level,
      () => this._hasThreiaTotem,
      this.healthSystem,
    );

    // Recreate hammer system for the new level (charges carry over, broken walls reset)
    this._hammerSystem = new HammerSystem(this, this.eventBus, this.player);
    this._hammerSystem.setCharges(this._hammerCharges);

    // Reset level timer for score bonus
    if (this.scoreSystem) {
      this.scoreSystem.resetLevelTimer();
    }

    // Roll for a level event (20% chance, level 3+)
    if (this._levelEventSystem) {
      this._levelEventSystem.rollForEvent(this.level);
    }

    // Apply level event effects to spawners
    this._applyLevelEventEffects();

    // Recreate ambient sound system for the new level
    this._ambientSoundSystem = new AmbientSoundSystem({ scene: this, level: this.level });
    this._ambientSoundSystem.start();

    // --- Recreate new systems for the new level ---

    // Moving walls — level 8+
    if (this._movingWallSystem) {
      this._movingWallSystem.destroy();
    }
    this._movingWallSystem = new MovingWallSystem(
      this, this.eventBus, this.mazeGrid, this.level,
      this.startPos, this.exitPos, this.player, this.mazeSeed,
    );

    // Secret passages — level 3+
    if (this._secretPassageSystem) {
      this._secretPassageSystem.destroy();
    }
    this._secretPassageSystem = new SecretPassageSystem(
      this, this.eventBus, this.mazeGrid, this.level,
      this.startPos, this.exitPos, this.mazeSeed,
    );

    // Shadow walkers — level 7+
    if (this._shadowWalkerSystem) {
      this._shadowWalkerSystem.destroy();
    }
    this._shadowWalkerSystem = new ShadowWalkerSystem(
      this, this.eventBus, this.mazeGrid, this.level,
      this.player, this.startPos, this.healthSystem,
      () => this._isGhostMode,
    );

    // Bat nests — level 4+
    if (this._batNestSystem) {
      this._batNestSystem.destroy();
    }
    this._batNestSystem = new BatNestSystem(
      this, this.eventBus, this.mazeGrid, this.player,
      this.visited, this.level, this.startPos, this.exitPos,
      this.healthSystem, () => this._hasThreiaTotem,
    );

    // Notify HUD
    this.eventBus.emit(Events.LEVEL_CHANGED, this.level);

    this._setupCamera();
    this._autoSave();

    // Re-register keyboard listeners removed by _cleanup()
    this._resubscribeKeyboardListeners();

    // Restart game music for the new level
    this._startGameMusic();

    // Reset idle timer so player doesn't rest immediately on new level
    this._lastMoveTime = Date.now();

    // Re-instantiate final battle system (destroyed by _cleanup())
    this._finalBattleSystem = new FinalBattleSystem({
      scene: this,
      eventBus: this.eventBus,
      healthSystem: this.healthSystem
    });

    // Check if this is the final battle level (all 4 Agmuth relics collected)
    if (this._agmuthCount >= AGMUTH_MAX_PER_GAME && this._finalBattleSystem) {
      // Skip normal level announcement, golden exit, and Threia's Guide —
      // start the endgame sequence instead.
      // The level is built normally (maze, player, fog) but the player cannot move.
      this._finalBattleSystem.start();
      return;
    }

    // Threia's Guide: chance to reveal the entire maze this level
    this._tryThreiasGuideReveal();

    // Reset golden exit for the new level (fresh spawn check each eligible level)
    this._hasGoldenExit = false;
    this._goldenExitPos = null;
    this._destroyGoldenExitMarker();
    // Note: _goldenExitSpent persists across levels — resets only on new game
    this._trySpawnGoldenExit();

    // Show level announcement (will unlock input when done)
    this._showLevelAnnouncement(this.level);
  }

  /**
   * Apply effects of the active level event to the relevant systems.
   * Called after spawners are created but before gameplay starts.
   * @private
   */
  _applyLevelEventEffects() {
    if (!this._levelEventSystem || !this._levelEventSystem.hasActiveEvent()) return;

    const evt = this._levelEventSystem.getActiveEvent();

    switch (evt) {
      case LevelEvent.VORATHS_HUNGER:
        // Food lifetime halved
        if (this.foodSpawner) {
          this.foodSpawner.lifetimeMultiplier = LEVEL_EVENT_FOOD_LIFETIME_MULT;
        }
        break;

      case LevelEvent.GOLD_RUSH:
        // 3x coin spawns, no food
        if (this.coinSpawner) {
          this.coinSpawner.maxActiveMultiplier = LEVEL_EVENT_COIN_MULTIPLIER;
        }
        // Stop food spawner entirely
        if (this.foodSpawner) {
          this.foodSpawner.stop();
        }
        break;

      case LevelEvent.SILENT_MAZE:
        // No bats — destroy bat system
        if (this._batSystem) {
          this._batSystem.destroy();
          this._batSystem = null;
        }
        // No bat nests — destroy bat nest system
        if (this._batNestSystem) {
          this._batNestSystem.destroy();
          this._batNestSystem = null;
        }
        // No narrator — skip is handled in _tryShowNarrator check
        break;

      case LevelEvent.LABYRINTH_REMEMBERS:
        // Fog re-darken system starts after transition lock releases
        // (started by startReDarkenIfNeeded, called from _showLevelAnnouncement flow)
        break;

      case LevelEvent.THREIAS_LIGHT:
        // Fog reveal radius bonus is applied dynamically in _revealFog
        // Re-reveal fog around current player position with expanded radius
        if (this.player) {
          this._revealFog(this.player.gridCol, this.player.gridRow);
        }
        break;

      default:
        break;
    }
  }

  /**
   * Display "Level N" centered on the game viewport with a disintegration
   * effect: the text appears over a dark overlay, holds briefly, then
   * breaks apart into scattered particles that drift outward and fade.
   * A dark semi-transparent overlay covers the game area while the
   * announcement is visible, and fades out when disintegration completes.
   * Plays the level-new sound when the announcement appears.
   *
   * @private
   * @param {number} level
   */
  _showLevelAnnouncement(level) {
    this._transitionLock = true;

    // Calculate the center of the camera viewport in world coordinates
    const cam = this.cameras.main;
    const centerX = cam.scrollX + cam.width / 2;
    const centerY = cam.scrollY + cam.height / 2;

    // Dark overlay covering the entire visible camera area
    const overlayW = cam.width * 2;
    const overlayH = cam.height * 2;
    const overlay = this.add.rectangle(
      centerX, centerY,
      overlayW, overlayH,
      0x000000, 0.7,
    );
    overlay.setDepth(199);
    overlay.setAlpha(0);

    // Create the text (displayed in the game scene, affected by the game camera)
    const text = this.add.text(centerX, centerY, `Level ${level}`, {
      fontFamily: '"Courier New", Courier, monospace',
      fontSize: '36px',
      fontStyle: 'bold',
      color: '#ffffff',
    });
    text.setOrigin(0.5);
    text.setDepth(200);
    text.setAlpha(0);

    // Level event subtitle (shown below level text if an event is active)
    let eventNameText = null;
    let eventDescText = null;
    if (this._levelEventSystem && this._levelEventSystem.hasActiveEvent()) {
      const evtKey = this._levelEventSystem.getActiveEvent();
      const evtName = LEVEL_EVENT_NAMES[evtKey];
      const evtDesc = LEVEL_EVENT_DESCRIPTIONS[evtKey];
      if (evtName) {
        // Decorative separator line
        const sepLine = this.add.graphics().setDepth(200).setAlpha(0);
        sepLine.lineStyle(1, 0xffd700, 0.5);
        sepLine.lineBetween(centerX - 100, centerY + 30, centerX + 100, centerY + 30);

        eventNameText = this.add.text(centerX, centerY + 46, `~ ${evtName} ~`, {
          fontFamily: '"Courier New", Courier, monospace',
          fontSize: '18px',
          fontStyle: 'bold italic',
          color: '#ffd700',
        });
        eventNameText.setOrigin(0.5);
        eventNameText.setDepth(200);
        eventNameText.setAlpha(0);

        if (evtDesc) {
          eventDescText = this.add.text(centerX, centerY + 70, evtDesc, {
            fontFamily: '"Courier New", Courier, monospace',
            fontSize: '11px',
            fontStyle: 'italic',
            color: '#cccccc',
            align: 'center',
            wordWrap: { width: 350 },
          });
          eventDescText.setOrigin(0.5);
          eventDescText.setDepth(200);
          eventDescText.setAlpha(0);
        }

        // Store separator for cleanup
        eventNameText._sepLine = sepLine;
      }
    }

    // Fade in both overlay and text, then play sound
    this.tweens.add({
      targets: overlay,
      alpha: 1,
      duration: 300,
      ease: 'Quad.easeOut',
    });

    this.tweens.add({
      targets: text,
      alpha: 1,
      duration: 300,
      ease: 'Quad.easeOut',
      onComplete: () => {
        // Fade in event texts if present (staggered)
        if (eventNameText) {
          // Fade in separator
          if (eventNameText._sepLine) {
            this.tweens.add({
              targets: eventNameText._sepLine,
              alpha: 1,
              duration: 300,
              ease: 'Quad.easeOut',
            });
          }
          // Fade in event name
          this.tweens.add({
            targets: eventNameText,
            alpha: 1,
            duration: 400,
            ease: 'Quad.easeOut',
            onComplete: () => {
              // Then fade in description
              if (eventDescText) {
                this.tweens.add({
                  targets: eventDescText,
                  alpha: 0.85,
                  duration: 350,
                  ease: 'Quad.easeOut',
                });
              }
            },
          });
        }

        // Play level-new sound
        const newLevelSound = this.sound.add(AUDIO_LEVEL_NEW, { volume: getSfxVol(0.5) });
        newLevelSound.play();
        newLevelSound.once('complete', () => newLevelSound.destroy());

        // Hold longer when event text is shown so the player can read it
        this.time.delayedCall(eventNameText ? 2500 : 500, () => {
          if (!this.scene) return;
          // Fade out event texts first
          const eventElements = [eventNameText, eventDescText, eventNameText?._sepLine].filter(Boolean);
          if (eventElements.length > 0) {
            this.tweens.add({
              targets: eventElements,
              alpha: 0,
              duration: 400,
              ease: 'Quad.easeIn',
              onComplete: () => {
                for (const el of eventElements) {
                  if (el && el.active !== false) {
                    try { el.destroy(); } catch (_e) { /* already destroyed */ }
                  }
                }
              },
            });
          }
          this._disintegrateText(text, () => {
            // After level announcement, check if we need to show the tutorial
            if (level === 1 && !this.continuing && !hasTutorialBeenSeen()) {
              // Keep transition lock active — tutorial will release it
              this.tweens.add({
                targets: overlay,
                alpha: 0,
                duration: 300,
                ease: 'Quad.easeIn',
                onComplete: () => {
                  overlay.destroy();
                  this._showTutorialModal();
                },
              });
            } else {
              // Check for narrator event before unlocking
              this.tweens.add({
                targets: overlay,
                alpha: 0,
                duration: 300,
                ease: 'Quad.easeIn',
                onComplete: () => {
                  overlay.destroy();
                  this._tryShowNarrator();
                },
              });
            }
          });
        });
      },
    });
  }

  // ---------------------------------------------------------------
  // First-time tutorial modal
  // ---------------------------------------------------------------

  /**
   * Display a one-time tutorial/mission modal for first-time players.
   * Shown after the Level 1 announcement. Blocks input until dismissed.
   * Once closed, the flag is persisted so it never shows again.
   *
   * @private
   */
  _showTutorialModal() {
    // _transitionLock is already true from _showLevelAnnouncement

    const cam = this.cameras.main;
    const centerX = cam.scrollX + cam.width / 2;
    const centerY = cam.scrollY + cam.height / 2;

    // --- Collect all modal objects for cleanup ---
    const elements = [];

    // --- Dark overlay ---
    const overlay = this.add.rectangle(
      centerX, centerY,
      cam.width * 2, cam.height * 2,
      0x000000, 0.85,
    );
    overlay.setDepth(300);
    overlay.setAlpha(0);
    elements.push(overlay);

    // --- Modal dimensions ---
    const modalW = 480;
    const modalH = 490;
    const modalX = centerX - modalW / 2;
    const modalY = centerY - modalH / 2;

    // --- Modal border (newspaper-style double border) ---
    const border = this.add.graphics().setDepth(301).setAlpha(0);
    border.lineStyle(2, 0xffffff, 0.6);
    border.strokeRect(modalX, modalY, modalW, modalH);
    border.lineStyle(1, 0xffffff, 0.25);
    border.strokeRect(modalX + 4, modalY + 4, modalW - 8, modalH - 8);
    elements.push(border);

    // --- Title ---
    const title = this.add.text(centerX, modalY + 28, 'LOST IN LABYRINTH', {
      fontFamily: '"Courier New", Courier, monospace',
      fontSize: '20px',
      fontStyle: 'bold',
      color: '#ffffff',
    }).setOrigin(0.5).setDepth(301).setAlpha(0);
    elements.push(title);

    // Decorative line below title
    const titleLine = this.add.graphics().setDepth(301).setAlpha(0);
    titleLine.lineStyle(1, 0xffffff, 0.35);
    titleLine.lineBetween(modalX + 30, modalY + 48, modalX + modalW - 30, modalY + 48);
    elements.push(titleLine);

    // --- Lore / mission text ---
    const loreText = 'You are a lost soul trapped in the\n'
      + 'Labyrinth of Limbo. Find the exit,\n'
      + 'but beware — your energy fades as\n'
      + 'you wander through these dark halls.\n\n'
      + 'The evil Vorath feeds on your life\n'
      + 'force. Seek food to survive, and\n'
      + 'pray for the aid of Goddess Threia.';

    const lore = this.add.text(centerX, modalY + 68, loreText, {
      fontFamily: '"Courier New", Courier, monospace',
      fontSize: '12px',
      color: '#cccccc',
      lineSpacing: 4,
      align: 'center',
    }).setOrigin(0.5, 0).setDepth(301).setAlpha(0);
    elements.push(lore);

    // --- Separator ---
    const sepLine = this.add.graphics().setDepth(301).setAlpha(0);
    sepLine.lineStyle(1, 0xffffff, 0.2);
    const sepY = modalY + 228;
    sepLine.lineBetween(modalX + 40, sepY, modalX + modalW - 40, sepY);
    elements.push(sepLine);

    // --- Controls section header ---
    const controlsHeader = this.add.text(centerX, sepY + 14, 'CONTROLS', {
      fontFamily: '"Courier New", Courier, monospace',
      fontSize: '14px',
      fontStyle: 'bold',
      color: '#ffffff',
    }).setOrigin(0.5).setDepth(301).setAlpha(0);
    elements.push(controlsHeader);

    // --- Control entries ---
    const controlsData = [
      { key: '\u2190 \u2191 \u2192 \u2193', desc: 'Move through the labyrinth' },
      { key: 'H (rapid)', desc: 'Pray to Threia to heal' },
      { key: 'Stand still', desc: 'Rest to recover energy (costs score)' },
    ];

    const controlStartY = sepY + 38;
    const keyColX = modalX + 44;
    const descColX = modalX + 180;

    for (let i = 0; i < controlsData.length; i++) {
      const cy = controlStartY + i * 24;

      const keyText = this.add.text(keyColX, cy, controlsData[i].key, {
        fontFamily: '"Courier New", Courier, monospace',
        fontSize: '12px',
        fontStyle: 'bold',
        color: '#ffffff',
      }).setOrigin(0, 0.5).setDepth(301).setAlpha(0);
      elements.push(keyText);

      const descText = this.add.text(descColX, cy, controlsData[i].desc, {
        fontFamily: '"Courier New", Courier, monospace',
        fontSize: '11px',
        color: '#aaaaaa',
      }).setOrigin(0, 0.5).setDepth(301).setAlpha(0);
      elements.push(descText);
    }

    // --- Second separator ---
    const sepLine2 = this.add.graphics().setDepth(301).setAlpha(0);
    sepLine2.lineStyle(1, 0xffffff, 0.2);
    const sep2Y = controlStartY + controlsData.length * 24 + 10;
    sepLine2.lineBetween(modalX + 40, sep2Y, modalX + modalW - 40, sep2Y);
    elements.push(sepLine2);

    // --- Hint text (help & manuscripts) ---
    const hintText = 'Press I during the game for more help.\n'
      + 'Keep an eye out for ancient manuscripts\n'
      + 'scattered through the labyrinth — they\n'
      + 'hold the secrets of Vorath, Threia,\n'
      + 'and the history of the Limbo.';

    const hint = this.add.text(centerX, sep2Y + 14, hintText, {
      fontFamily: '"Courier New", Courier, monospace',
      fontSize: '11px',
      color: '#999999',
      lineSpacing: 3,
      align: 'center',
    }).setOrigin(0.5, 0).setDepth(301).setAlpha(0);
    elements.push(hint);

    // --- Footer (dismiss instruction) ---
    const footer = this.add.text(centerX, modalY + modalH - 28, 'Press ENTER or SPACE to begin', {
      fontFamily: '"Courier New", Courier, monospace',
      fontSize: '11px',
      color: '#666666',
    }).setOrigin(0.5).setDepth(301).setAlpha(0);
    elements.push(footer);

    // Pulsing footer effect
    this.tweens.add({
      targets: footer,
      alpha: { from: 0.4, to: 0.9 },
      duration: 900,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
      delay: 400,
    });

    // --- Fade in all elements ---
    this.tweens.add({
      targets: overlay,
      alpha: 1,
      duration: 400,
      ease: 'Quad.easeOut',
    });
    for (const el of elements) {
      if (el === overlay) continue;
      this.tweens.add({
        targets: el,
        alpha: 1,
        duration: 400,
        delay: 150,
        ease: 'Quad.easeOut',
      });
    }

    // --- Dismiss handler ---
    const dismiss = () => {
      // Remove key listeners
      this.input.keyboard.off('keydown-ENTER', dismiss);
      this.input.keyboard.off('keydown-SPACE', dismiss);
      this.input.keyboard.off('keydown-ESC', dismiss);

      // Mark tutorial as seen (persists forever)
      saveTutorialSeen();

      // Play menu click sound
      try { this.sound.play(AUDIO_MENU_CLICK, { volume: getSfxVol(0.4) }); } catch (_) {}

      // Fade out all elements
      for (const el of elements) {
        this.tweens.add({
          targets: el,
          alpha: 0,
          duration: 300,
          ease: 'Quad.easeIn',
          onComplete: () => {
            if (el && el.destroy) el.destroy();
          },
        });
      }

      // Unlock gameplay after fade — but check for narrator first
      this.time.delayedCall(350, () => {
        this._tryShowNarrator();
      });
    };

    // Delay key binding slightly to prevent accidental dismissal
    this.time.delayedCall(500, () => {
      this.input.keyboard.on('keydown-ENTER', dismiss);
      this.input.keyboard.on('keydown-SPACE', dismiss);
      this.input.keyboard.on('keydown-ESC', dismiss);
    });
  }

  // ---------------------------------------------------------------
  // Narrator System (Monk & Vorath)
  // ---------------------------------------------------------------

  /**
   * Check if a narrator event should trigger on the current level.
   * If so, show the narrator modal; otherwise unlock input.
   *
   * @private
   */
  _tryShowNarrator() {
    if (!this._narratorSystem) {
      this._transitionLock = false;
      return;
    }

    // "The Silent Maze" event: no narrator this level
    if (this._levelEventSystem && this._levelEventSystem.isActive(LevelEvent.SILENT_MAZE)) {
      this._transitionLock = false;
      // Start re-darken if applicable (after announcement flow completes)
      if (this._levelEventSystem) {
        this._levelEventSystem.startReDarkenIfNeeded();
      }
      return;
    }

    const event = this._narratorSystem.check(this.level);
    if (event) {
      this._showNarratorModal(event);
    } else {
      this._transitionLock = false;
      // Start re-darken system if "The Labyrinth Remembers" event is active
      if (this._levelEventSystem) {
        this._levelEventSystem.startReDarkenIfNeeded();
      }
    }
  }

  /**
   * Display a narrator modal with speaker-appropriate visual style.
   * Monk: warm, parchment-like, golden name.
   * Vorath: dark, red-tinted, menacing.
   *
   * @private
   * @param {{ speaker: 'monk'|'vorath', name: string, text: string, gift: string|null }} event
   */
  _showNarratorModal(event) {
    // _transitionLock is already true
    const cam = this.cameras.main;
    const centerX = cam.scrollX + cam.width / 2;
    const centerY = cam.scrollY + cam.height / 2;
    const isMonk = event.speaker === 'monk';

    // --- Collect all modal objects for cleanup ---
    const elements = [];

    // --- Dark overlay ---
    const overlayColor = isMonk ? 0x000000 : COLOR_NARRATOR_VORATH_BG;
    const overlayAlpha = isMonk ? 0.80 : 0.88;
    const overlay = this.add.rectangle(
      centerX, centerY,
      cam.width * 2, cam.height * 2,
      overlayColor, overlayAlpha,
    );
    overlay.setDepth(300);
    overlay.setAlpha(0);
    elements.push(overlay);

    // --- Modal dimensions ---
    const modalW = 380;
    const modalH = event.gift ? 290 : 270;
    const modalX = centerX - modalW / 2;
    const modalY = centerY - modalH / 2;

    // --- Modal border ---
    const border = this.add.graphics().setDepth(301).setAlpha(0);
    if (isMonk) {
      // Warm double border
      border.lineStyle(2, 0xffd700, 0.45);
      border.strokeRect(modalX, modalY, modalW, modalH);
      border.lineStyle(1, 0xfff8dc, 0.2);
      border.strokeRect(modalX + 4, modalY + 4, modalW - 8, modalH - 8);
    } else {
      // Dark red border with distressed feel
      border.lineStyle(2, 0xcc0000, 0.5);
      border.strokeRect(modalX, modalY, modalW, modalH);
      border.lineStyle(1, 0xff0000, 0.15);
      border.strokeRect(modalX + 3, modalY + 3, modalW - 6, modalH - 6);
    }
    elements.push(border);

    // --- Speaker name ---
    const nameColor = isMonk
      ? `#${COLOR_NARRATOR_MONK_NAME.toString(16).padStart(6, '0')}`
      : `#${COLOR_NARRATOR_VORATH_NAME.toString(16).padStart(6, '0')}`;
    const nameText = this.add.text(centerX, modalY + 24, event.name, {
      fontFamily: '"Courier New", Courier, monospace',
      fontSize: '16px',
      fontStyle: 'bold',
      color: nameColor,
    }).setOrigin(0.5).setDepth(301).setAlpha(0);
    elements.push(nameText);

    // --- Decorative line below name ---
    const nameLine = this.add.graphics().setDepth(301).setAlpha(0);
    const lineColor = isMonk ? 0xffd700 : 0xcc0000;
    nameLine.lineStyle(1, lineColor, 0.3);
    nameLine.lineBetween(modalX + 30, modalY + 42, modalX + modalW - 30, modalY + 42);
    elements.push(nameLine);

    // --- Message text ---
    const textColor = isMonk
      ? `#${COLOR_NARRATOR_MONK.toString(16).padStart(6, '0')}`
      : `#${COLOR_NARRATOR_VORATH.toString(16).padStart(6, '0')}`;
    const bodyText = this.add.text(centerX, modalY + 56, event.text, {
      fontFamily: '"Courier New", Courier, monospace',
      fontSize: '12px',
      color: textColor,
      lineSpacing: 5,
      align: 'center',
      fontStyle: isMonk ? '' : 'italic',
    }).setOrigin(0.5, 0).setDepth(301).setAlpha(0);
    elements.push(bodyText);

    // --- Gift notice (monk only) ---
    if (event.gift) {
      const giftLabel = this._narratorGiftLabel(event.gift);
      const giftText = this.add.text(centerX, modalY + modalH - 56, giftLabel, {
        fontFamily: '"Courier New", Courier, monospace',
        fontSize: '11px',
        color: '#ffd700',
        fontStyle: 'bold',
      }).setOrigin(0.5).setDepth(301).setAlpha(0);
      elements.push(giftText);
    }

    // --- Footer ---
    const footerColor = isMonk ? '#888888' : '#661111';
    const footer = this.add.text(
      centerX,
      modalY + modalH - 26,
      'Press ENTER or SPACE to continue',
      {
        fontFamily: '"Courier New", Courier, monospace',
        fontSize: '10px',
        color: footerColor,
      },
    ).setOrigin(0.5).setDepth(301).setAlpha(0);
    elements.push(footer);

    // Pulsing footer
    this.tweens.add({
      targets: footer,
      alpha: { from: 0.3, to: 0.8 },
      duration: 900,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
      delay: 400,
    });

    // --- Vorath: subtle red vignette edges ---
    if (!isMonk) {
      const vignetteTop = this.add.rectangle(
        centerX, modalY + 2, modalW - 8, 6, 0xff0000, 0.06,
      ).setDepth(301).setAlpha(0);
      elements.push(vignetteTop);

      const vignetteBot = this.add.rectangle(
        centerX, modalY + modalH - 2, modalW - 8, 6, 0xff0000, 0.06,
      ).setDepth(301).setAlpha(0);
      elements.push(vignetteBot);
    }

    // --- Play narrator sound ---
    const audioKey = isMonk ? AUDIO_NARRATOR_MONK : AUDIO_NARRATOR_VORATH;
    try { this.sound.play(audioKey, { volume: getSfxVol(0.5) }); } catch (_) {}

    // --- Fade in all elements ---
    this.tweens.add({
      targets: overlay,
      alpha: 1,
      duration: 350,
      ease: 'Quad.easeOut',
    });
    for (const el of elements) {
      if (el === overlay) continue;
      this.tweens.add({
        targets: el,
        alpha: 1,
        duration: 350,
        delay: 150,
        ease: 'Quad.easeOut',
      });
    }

    // --- Dismiss handler ---
    const dismiss = () => {
      this.input.keyboard.off('keydown-ENTER', dismiss);
      this.input.keyboard.off('keydown-SPACE', dismiss);
      this.input.keyboard.off('keydown-ESC', dismiss);

      try { this.sound.play(AUDIO_MENU_CLICK, { volume: getSfxVol(0.4) }); } catch (_) {}

      // Deliver gift if monk
      if (event.gift) {
        this._deliverNarratorGift(event.gift);
      }

      // Advance narrator state + emit event
      this._narratorSystem.advance(event.speaker);
      this.eventBus.emit(Events.NARRATOR_DISMISSED, event.speaker);

      // Auto-save updated narrator state
      this._autoSave();

      // Fade out all elements
      for (const el of elements) {
        this.tweens.add({
          targets: el,
          alpha: 0,
          duration: 250,
          ease: 'Quad.easeIn',
          onComplete: () => {
            if (el && el.destroy) el.destroy();
          },
        });
      }

      // Unlock gameplay after fade
      this.time.delayedCall(300, () => {
        this._transitionLock = false;
        // Start re-darken system if "The Labyrinth Remembers" event is active
        if (this._levelEventSystem) {
          this._levelEventSystem.startReDarkenIfNeeded();
        }
      });
    };

    // Delay key binding slightly to prevent accidental dismissal
    this.time.delayedCall(600, () => {
      this.input.keyboard.on('keydown-ENTER', dismiss);
      this.input.keyboard.on('keydown-SPACE', dismiss);
      this.input.keyboard.on('keydown-ESC', dismiss);
    });
  }

  // ── Agmuth relic narrator messages ──
  // Brother Malachar speaks when the player finds each Agmuth relic,
  // with a unique message depending on how many the player now holds (1–4).

  /**
   * Array of monk messages for each Agmuth relic found (indexed 0–3 for
   * agmuthCount 1–4). Kept as a static-like getter to avoid top-level
   * allocation; the array is tiny and frozen.
   * @private
   * @returns {string[]}
   */
  static get _AGMUTH_RELIC_MESSAGES() {
    return Object.freeze([
      // 1st relic
      'Wanderer! You have found a Relic of Agmuth — a fragment of\n'
      + 'the ancient power that once held the Labyrinth together.\n'
      + 'Very few have ever laid eyes upon one.\n\n'
      + 'Guard it well. Legend says only four remain,\n'
      + 'scattered through the endless corridors of Limbo.\n'
      + 'Resist the temptation to use it lightly, for if the\n'
      + 'four are ever reunited, something will change\n'
      + 'in this labyrinth... forever.',

      // 2nd relic
      'A second Relic of Agmuth! Your perseverance\n'
      + 'is remarkable, wanderer.\n\n'
      + 'Two of four now rest in your hands. The relics\n'
      + 'grow restless — I can feel their hum even from\n'
      + 'my distant sanctuary. Hold firm. Do not spend\n'
      + 'them phasing through walls. The prophecy speaks\n'
      + 'of a convergence when all four are gathered\n'
      + 'by a single soul.',

      // 3rd relic
      'Three relics! Wanderer, you stand on the edge\n'
      + 'of something the Order of Threia only dared\n'
      + 'whisper about in our darkest prayers.\n\n'
      + 'One more remains hidden somewhere in these\n'
      + 'cursed corridors. I beg you — do not yield to\n'
      + 'temptation. Carry all four and the labyrinth\n'
      + 'itself will tremble. You are so very close.',

      // 4th relic — all collected
      'All four Relics of Agmuth... united at last.\n'
      + 'In a thousand years, no soul has achieved this.\n\n'
      + 'I feel the labyrinth shudder, wanderer.\n'
      + 'Vorath feels it too — his prison weakens.\n'
      + 'What happens next, not even the Order foresaw.\n'
      + 'May Threia watch over you... for the Labyrinth\n'
      + 'of Limbo will never be the same.',
    ]);
  }

  /**
   * Show a narrator modal (Brother Malachar) when the player collects an
   * Agmuth relic. The message varies based on the current agmuthCount (1–4).
   * This modal uses the monk visual style but is independent of the
   * NarratorSystem scheduling — it triggers immediately on collection.
   * @private
   * @param {number} count - The player's agmuthCount AFTER collecting (1–4).
   */
  _showAgmuthNarratorModal(count) {
    const idx = Math.min(Math.max(count - 1, 0), 3);
    const messages = GameScene._AGMUTH_RELIC_MESSAGES;
    const text = messages[idx];
    const name = 'Brother Malachar';

    this._transitionLock = true;

    const cam = this.cameras.main;
    const centerX = cam.scrollX + cam.width / 2;
    const centerY = cam.scrollY + cam.height / 2;

    const elements = [];

    // --- Dark overlay ---
    const overlay = this.add.rectangle(
      centerX, centerY,
      cam.width * 2, cam.height * 2,
      0x000000, 0.80,
    );
    overlay.setDepth(300);
    overlay.setAlpha(0);
    elements.push(overlay);

    // --- Modal dimensions ---
    const modalW = 380;
    const modalH = 270;
    const modalX = centerX - modalW / 2;
    const modalY = centerY - modalH / 2;

    // --- Warm double border (monk style) ---
    const border = this.add.graphics().setDepth(301).setAlpha(0);
    border.lineStyle(2, 0xffd700, 0.45);
    border.strokeRect(modalX, modalY, modalW, modalH);
    border.lineStyle(1, 0xfff8dc, 0.2);
    border.strokeRect(modalX + 4, modalY + 4, modalW - 8, modalH - 8);
    elements.push(border);

    // --- Speaker name ---
    const nameColor = `#${COLOR_NARRATOR_MONK_NAME.toString(16).padStart(6, '0')}`;
    const nameText = this.add.text(centerX, modalY + 24, name, {
      fontFamily: '"Courier New", Courier, monospace',
      fontSize: '16px',
      fontStyle: 'bold',
      color: nameColor,
    }).setOrigin(0.5).setDepth(301).setAlpha(0);
    elements.push(nameText);

    // --- Decorative line below name ---
    const nameLine = this.add.graphics().setDepth(301).setAlpha(0);
    nameLine.lineStyle(1, 0xffd700, 0.3);
    nameLine.lineBetween(modalX + 30, modalY + 42, modalX + modalW - 30, modalY + 42);
    elements.push(nameLine);

    // --- Relic count indicator ---
    const relicLabel = `[ Relic ${count} of 4 ]`;
    const relicText = this.add.text(centerX, modalY + 56, relicLabel, {
      fontFamily: '"Courier New", Courier, monospace',
      fontSize: '11px',
      fontStyle: 'bold',
      color: '#ffd700',
    }).setOrigin(0.5).setDepth(301).setAlpha(0);
    elements.push(relicText);

    // --- Message text ---
    const textColor = `#${COLOR_NARRATOR_MONK.toString(16).padStart(6, '0')}`;
    const bodyText = this.add.text(centerX, modalY + 74, text, {
      fontFamily: '"Courier New", Courier, monospace',
      fontSize: '11px',
      color: textColor,
      lineSpacing: 4,
      align: 'center',
      wordWrap: { width: modalW - 50 },
    }).setOrigin(0.5, 0).setDepth(301).setAlpha(0);
    elements.push(bodyText);

    // --- Footer ---
    const footer = this.add.text(
      centerX,
      modalY + modalH - 26,
      'Press ENTER or SPACE to continue',
      {
        fontFamily: '"Courier New", Courier, monospace',
        fontSize: '10px',
        color: '#888888',
      },
    ).setOrigin(0.5).setDepth(301).setAlpha(0);
    elements.push(footer);

    // Pulsing footer
    this.tweens.add({
      targets: footer,
      alpha: { from: 0.3, to: 0.8 },
      duration: 900,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
      delay: 400,
    });

    // --- Play narrator sound ---
    try { this.sound.play(AUDIO_NARRATOR_MONK, { volume: getSfxVol(0.5) }); } catch (_) {}

    // --- Emit event ---
    this.eventBus.emit(Events.NARRATOR_AGMUTH_SPEAK, count);

    // --- Fade in all elements ---
    this.tweens.add({
      targets: overlay,
      alpha: 1,
      duration: 350,
      ease: 'Quad.easeOut',
    });
    for (const el of elements) {
      if (el === overlay) continue;
      this.tweens.add({
        targets: el,
        alpha: 1,
        duration: 350,
        delay: 150,
        ease: 'Quad.easeOut',
      });
    }

    // --- Dismiss handler ---
    const dismiss = () => {
      this.input.keyboard.off('keydown-ENTER', dismiss);
      this.input.keyboard.off('keydown-SPACE', dismiss);
      this.input.keyboard.off('keydown-ESC', dismiss);

      try { this.sound.play(AUDIO_MENU_CLICK, { volume: getSfxVol(0.4) }); } catch (_) {}

      // Fade out all elements
      for (const el of elements) {
        this.tweens.add({
          targets: el,
          alpha: 0,
          duration: 250,
          ease: 'Quad.easeIn',
          onComplete: () => {
            if (el && el.destroy) el.destroy();
          },
        });
      }

      // Unlock gameplay after fade
      this.time.delayedCall(300, () => {
        this._transitionLock = false;
      });
    };

    // Delay key binding to prevent accidental dismissal
    this.time.delayedCall(600, () => {
      this.input.keyboard.on('keydown-ENTER', dismiss);
      this.input.keyboard.on('keydown-SPACE', dismiss);
      this.input.keyboard.on('keydown-ESC', dismiss);
    });
  }

  /**
   * Get a human-readable label for a narrator gift.
   * @private
   * @param {string} gift
   * @returns {string}
   */
  _narratorGiftLabel(gift) {
    switch (gift) {
      case 'key_metallic': return '[ Received: Metallic Key ]';
      case 'key_copper': return '[ Received: Copper Key ]';
      case 'heal_partial': return '[ Received: Partial Heal ]';
      case 'heal_full': return '[ Received: Full Heal ]';
      default: return '';
    }
  }

  /**
   * Deliver a gift from the monk directly to the player's inventory.
   * Keys are added if the player has room; heals are applied immediately.
   *
   * @private
   * @param {string} gift - 'key_metallic', 'key_copper', 'heal_partial', or 'heal_full'
   */
  _deliverNarratorGift(gift) {
    if (gift === 'key_metallic' || gift === 'key_copper') {
      const tier = gift === 'key_metallic' ? KeyTier.METALLIC : KeyTier.COPPER;

      // Only add if player has room, or upgrade worst key
      if (this._playerKeys.length < MAX_PLAYER_KEYS) {
        this._playerKeys.push(tier);
        this.eventBus.emit(Events.KEY_COLLECTED, tier);
        this.eventBus.emit(Events.KEYS_CHANGED, [...this._playerKeys]);
        if (this.keySpawner) {
          this.keySpawner.setPlayerKeyCount(this._playerKeys.length);
        }
      } else if (this._tryUpgradeKey(tier)) {
        this.eventBus.emit(Events.KEY_COLLECTED, tier);
        this.eventBus.emit(Events.KEYS_CHANGED, [...this._playerKeys]);
      }
      // If inventory full and no upgrade possible, gift is silently lost
    } else if (gift === 'heal_partial') {
      if (this.healthSystem) {
        this.healthSystem.heal(40); // Restore 40 HP
      }
    } else if (gift === 'heal_full') {
      if (this.healthSystem) {
        this.healthSystem.fullHeal();
      }
    }

    this.eventBus.emit(Events.NARRATOR_GIFT_RECEIVED, gift);
  }

  /**
   * Break a text object into particles that scatter outward and fade,
   * simulating a disintegration effect.
   *
   * Creates a grid of small squares sampled from the text's position,
   * then tweens them outward with rotation, scale-down, and fade.
   *
   * @private
   * @param {Phaser.GameObjects.Text} text - The text to disintegrate.
   * @param {Function} [onComplete] - Called when the effect finishes.
   */
  _disintegrateText(text, onComplete) {
    const textBounds = text.getBounds();
    const particleSize = 4; // Size of each fragment
    const cols = Math.ceil(textBounds.width / particleSize);
    const rows = Math.ceil(textBounds.height / particleSize);

    const particles = [];

    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const px = textBounds.x + c * particleSize + particleSize / 2;
        const py = textBounds.y + r * particleSize + particleSize / 2;

        const fragment = this.add.rectangle(
          px, py,
          particleSize, particleSize,
          COLOR_WHITE, 1,
        );
        fragment.setDepth(200);

        // Random outward velocity
        const angle = Math.random() * Math.PI * 2;
        const dist = 40 + Math.random() * 120;
        const targetX = px + Math.cos(angle) * dist;
        const targetY = py + Math.sin(angle) * dist;
        const duration = 400 + Math.random() * 400;
        const delay = Math.random() * 100;

        this.tweens.add({
          targets: fragment,
          x: targetX,
          y: targetY,
          alpha: 0,
          scaleX: 0,
          scaleY: 0,
          angle: (Math.random() - 0.5) * 360,
          duration: duration,
          delay: delay,
          ease: 'Quad.easeOut',
          onComplete: () => {
            fragment.destroy();
          },
        });

        particles.push(fragment);
      }
    }

    // Destroy the original text immediately
    text.destroy();

    // Call onComplete after the longest possible tween finishes
    const maxDuration = 100 + 800; // max delay + max duration
    this.time.delayedCall(maxDuration + 50, () => {
      // Clean up any remaining fragments (safety net)
      for (const p of particles) {
        if (p && p.active) p.destroy();
      }
      if (onComplete) onComplete();
    });
  }

  /** @private */
  _onGameOver() {
    // Lock input immediately so the player can't move during the death animation
    this._transitionLock = true;

    // If the final battle is active, this is a definitive game over (no continue)
    if (this._finalBattleSystem && this._finalBattleSystem.isActive) {
      this._isFinalBattleGameOver = true;
      // Stop final battle sounds/timers immediately
      this._finalBattleSystem.cleanup();
      this._finalBattleSystem = null;
    }

    // Cancel zoom-out if active
    this._cancelZoomOut();

    // Stop spawners, music, water sound, lava sound, and rest — but keep the player alive
    // for the animation. We do a partial cleanup here; full cleanup after anim.
    this._stopGameMusic();
    this._stopWaterWalkSound();
    this._stopLavaWalkSound();
    this._stopSpikeWalkSound();
    this._stopPoisonWalkSound();
    this._stopLavaEffects();
    this._stopWaterIdleDrain();
    this._stopLavaIdleDrain();
    if (this._isResting) {
      this._stopResting();
    }
    if (this._isPoisoned) {
      this._removePoison();
    }
    if (this._isWounded) {
      this._removeWounded();
    }
    if (this.foodSpawner) {
      this.foodSpawner.stop();
      this.foodSpawner = null;
    }
    if (this.coinSpawner) {
      this.coinSpawner.stop();
      this.coinSpawner = null;
    }
    if (this.keySpawner) {
      this.keySpawner.stop();
      this.keySpawner = null;
    }
    if (this.player && this.player._stopMovementSound) {
      this.player._stopMovementSound();
    }

    // Cache score/coins/agmuths before cleanup destroys the score system
    const finalScore = this.scoreSystem ? this.scoreSystem.getScore() : 0;
    const finalCoins = this.coinsCollected;
    const finalLevel = this.level;
    const finalAgmuths = this._agmuthCount;
    const finalTimePlayed = this._gameElapsedBeforeSave + (Date.now() - this._gameStartTime) / 1000;

    // --- Explorer mode: save state for potential continue ---
    // For Explorer mode (non-final-battle), persist the current game state so
    // that if the player chooses CONTINUE in GameOverScene, the game can
    // restore from save with reset health/lives. The save is written with the
    // player's death position, full health, and full lives — ready for respawn.
    const isExplorerContinueEligible =
      this._gameMode === GameMode.EXPLORER && !this._isFinalBattleGameOver;

    if (isExplorerContinueEligible) {
      const pos = this.player
        ? this.player.getGridPosition()
        : { col: this.playerStartCol, row: this.playerStartRow };

      saveGame({
        level: this.level,
        mazeSeed: this.mazeSeed,
        mazeCols: this.mazeGridWidth,
        mazeRows: this.mazeGridHeight,
        playerCol: pos.col,
        playerRow: pos.row,
        health: MAX_HEALTH,          // reset to full for respawn
        lives: INITIAL_LIVES,        // reset to full for respawn
        score: finalScore,           // penalties applied in GameOverScene
        coinsCollected: finalCoins,
        visited: this.visited,
        playerKeys: this._playerKeys,
        agmuthCount: this._agmuthCount,
        hasCompass: this._hasCompass,
        hasThreiaTotem: this._hasThreiaTotem,
        threiaBlessingUsed: this._threiaBlessingUsed,
        chests: this.chestSpawner ? this.chestSpawner.serialize() : [],
        hammerCharges: this._hammerCharges,
        brokenWalls: this._hammerSystem ? this._hammerSystem.getBrokenWallsForSave() : [],
        hasGoldenExit: this._hasGoldenExit,
        goldenExitPos: this._goldenExitPos,
        goldenExitSpent: this._goldenExitSpent,
        gameMode: this._gameMode,
        explorerContinuesUsed: this._explorerContinuesUsed,
        ...(this._narratorSystem ? this._narratorSystem.serialize() : {}),
        gameElapsedTime: finalTimePlayed,
        achGhostUses: this._achGhostUses,
        achWallsBroken: this._achWallsBroken,
        achChestsOpened: this._achChestsOpened,
        achBlessingsTotal: this._achBlessingsTotal,
        levelEvent: this._levelEventSystem ? this._levelEventSystem.serialize() : LevelEvent.NONE,
        movingWalls: this._movingWallSystem ? this._movingWallSystem.serialize() : null,
        secretPassages: this._secretPassageSystem ? this._secretPassageSystem.serialize() : null,
        batNests: this._batNestSystem ? this._batNestSystem.serialize() : null,
      });
    }

    // Play death animation, then fade out and transition to game over screen
    this._playDeathAnimation(() => {
      this._cleanup();
      this.scene.stop(SCENE_HUD);

      this.cameras.main.fadeOut(400, 0, 0, 0);
      this.cameras.main.once('camerafadeoutcomplete', () => {
        this.scene.start(SCENE_GAME_OVER, {
          level: finalLevel,
          score: finalScore,
          coins: finalCoins,
          agmuths: finalAgmuths,
          timePlayed: finalTimePlayed,
          isFinalBattle: this._isFinalBattleGameOver,
          // Explorer mode data
          gameMode: this._gameMode,
          explorerContinuesUsed: this._explorerContinuesUsed,
        });
      });
    });
  }

  /**
   * Called when HealthSystem auto-consumes a Heart Potion to prevent a life
   * loss. Plays the potion sound, a green healing flash on the player,
   * the recover-life sound, and emits HEALTH_CHANGED so the health bar
   * visually resets to full.
   *
   * @private
   */
  _onHeartPotionUsed() {
    // 1. Play Heart Potion consumption sound
    try {
      this.sound.play(AUDIO_HEART_POTION_USE, { volume: getSfxVol(0.45) });
    } catch (_) { /* audio fallback */ }

    // 2. Notify HUD that shop items changed (potion consumed)
    this.eventBus.emit(Events.SHOP_ITEMS_CHANGED);

    // 3. Green healing flash around the player
    if (this.player) {
      const px = this.player.x;
      const py = this.player.y;

      const flash = this.add.graphics();
      flash.fillStyle(COLOR_POTION_GREEN, 0.3);
      flash.fillCircle(px, py, 28);
      flash.setDepth(999);

      this.tweens.add({
        targets: flash,
        alpha: { from: 1, to: 0 },
        duration: 600,
        ease: 'Quad.easeOut',
        onComplete: () => {
          flash.destroy();
        },
      });
    }

    // 4. After a short delay, play the recover-life sound for audible feedback
    this.time.delayedCall(250, () => {
      try {
        this.sound.play(AUDIO_RECOVER_LIFE, { volume: getSfxVol(0.3) });
      } catch (_) { /* audio fallback */ }
    });

    // 5. Emit HEALTH_CHANGED so health bar visually shows full
    this.eventBus.emit(Events.HEALTH_CHANGED, MAX_HEALTH, MAX_HEALTH);
  }

  /**
   * Handle Tear of Threia resurrection: plays a celestial animation sequence
   * where hearts restore one by one with golden glow effects, then the player
   * resumes normal gameplay.
   *
   * Called when HealthSystem detects Tear of Threia ownership at the moment
   * the player would have gotten a game over (lives reach 0).
   *
   * @private
   */
  _onTearOfThreiaUsed() {
    // Lock input during the resurrection sequence
    this._transitionLock = true;

    // Play celestial resurrection sound
    try {
      this.sound.play(AUDIO_TEAR_OF_THREIA_USE, { volume: getSfxVol(0.6) });
    } catch (_) { /* audio fallback */ }

    // Emit shop items changed so HUD strip updates (tear consumed)
    this.eventBus.emit(Events.SHOP_ITEMS_CHANGED);

    // --- Visual resurrection sequence ---
    const px = this.player ? this.player.x : GAME_WIDTH / 2;
    const py = this.player ? this.player.y : GAME_HEIGHT / 2;

    // 1. Golden celestial flash over the player
    const flash = this.add.graphics();
    flash.fillStyle(COLOR_TEAR_THREIA_GOLD, 0.35);
    flash.fillCircle(px, py, 60);
    flash.setDepth(999);

    this.tweens.add({
      targets: flash,
      alpha: { from: 0, to: 1 },
      duration: 300,
      yoyo: true,
      hold: 200,
      onComplete: () => {
        flash.destroy();
      },
    });

    // 2. Golden glow particles emanating from the player
    if (this.textures.exists(TEX_GLOW)) {
      const emitter = this.add.particles(px, py, TEX_GLOW, {
        speed: { min: 20, max: 60 },
        scale: { start: 0.4, end: 0 },
        alpha: { start: 0.7, end: 0 },
        tint: COLOR_TEAR_THREIA_GOLD,
        lifespan: 1200,
        quantity: 3,
        frequency: 80,
        duration: 1800,
      });
      emitter.setDepth(998);
      this.time.delayedCall(2200, () => {
        emitter.destroy();
      });
    }

    // 3. "Threia's grace!" floating text
    const graceText = this.add.text(px, py - 30, "Threia's grace!", {
      fontFamily: 'monospace',
      fontSize: '13px',
      color: '#ffd700',
      stroke: '#886600',
      strokeThickness: 2,
    });
    graceText.setOrigin(0.5, 0.5);
    graceText.setDepth(1000);

    this.tweens.add({
      targets: graceText,
      alpha: { from: 1, to: 0 },
      y: py - 60,
      duration: 2200,
      ease: 'Quad.easeOut',
      onComplete: () => {
        graceText.destroy();
      },
    });

    // 4. Restore hearts one by one with timed delays
    // HealthSystem already set lives = INITIAL_LIVES and reset health.
    // We emit LIFE_GAINED events one at a time so HUD animates each heart.
    const totalLives = INITIAL_LIVES;
    for (let i = 1; i <= totalLives; i++) {
      this.time.delayedCall(400 + i * 350, () => {
        // Emit LIFE_GAINED so HudScene animates the heart appearing
        this.eventBus.emit(Events.LIFE_GAINED, i);
        // Play recover-life sound for each heart
        try {
          this.sound.play(AUDIO_RECOVER_LIFE, { volume: getSfxVol(0.3) });
        } catch (_) { /* audio fallback */ }
      });
    }

    // 5. Emit HEALTH_CHANGED so health bar shows full
    this.time.delayedCall(500, () => {
      this.eventBus.emit(Events.HEALTH_CHANGED, MAX_HEALTH, MAX_HEALTH);
    });

    // 6. Unlock input after the full sequence completes
    const totalDuration = 400 + (totalLives + 1) * 350 + 200;
    this.time.delayedCall(totalDuration, () => {
      this._transitionLock = false;
    });
  }

  /**
   * Play the player death cinematic:
   * 1. Glow particles emanate from the player ball (1.5 seconds)
   * 2. Player sprite disintegrates into scattered fragments that fade out
   * 3. game-end-1.mp3 sound plays during the whole sequence
   *
   * @private
   * @param {Function} onComplete - Called when the full animation finishes.
   */
  _playDeathAnimation(onComplete) {
    if (!this.player) {
      onComplete();
      return;
    }

    const playerX = this.player.x;
    const playerY = this.player.y;

    // --- Play game-end sound ---
    const endSound = this.sound.add(AUDIO_GAME_END, { volume: getSfxVol(0.6) });
    endSound.play();
    endSound.once('complete', () => endSound.destroy());

    // --- 1. Glow particles around the player — dim, ominous death effect ---
    const glowEmitter = this.add.particles(playerX, playerY, TEX_GLOW, {
      follow: this.player,
      lifespan: { min: 500, max: 900 },
      speed: { min: 10, max: 40 },
      angle: { min: 40, max: 140 },  // Downward spread for a sinking feel
      scale: { start: 0.5, end: 0 },
      alpha: { start: 0.6, end: 0 },
      frequency: 50,
      quantity: 2,
      tint: 0xaaaaaa,               // Dimmer gray tint for death (vs bright white for victory)
      emitting: true,
    });
    glowEmitter.setDepth(this.player.depth - 1);

    // Intensify glow at 750ms — more particles but still muted
    this.time.delayedCall(750, () => {
      if (!glowEmitter || !this.scene) return;
      glowEmitter.frequency = 25;
      glowEmitter.quantity = 4;
    });

    // --- 2. At 1500ms: stop glow, hide player, disintegrate ---
    this.time.delayedCall(1500, () => {
      if (!this.scene) return;

      // Stop glow
      glowEmitter.stop();

      // Hide the player sprite
      if (this.player) {
        this.player.setVisible(false);
      }

      // Disintegrate: spawn fragments in a grid matching the player sprite
      const size = PLAYER_SIZE;
      const fragSize = 2;
      const cols = Math.ceil(size / fragSize);
      const rows = Math.ceil(size / fragSize);
      const startX = playerX - size / 2;
      const startY = playerY - size / 2;
      const centerR = size / 2;

      const fragments = [];

      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          // Only create fragments within the circular shape of the player
          const fx = c * fragSize + fragSize / 2;
          const fy = r * fragSize + fragSize / 2;
          const dx = fx - centerR;
          const dy = fy - centerR;
          if (dx * dx + dy * dy > centerR * centerR) continue;

          const px = startX + fx;
          const py = startY + fy;

          const fragment = this.add.rectangle(
            px, py,
            fragSize, fragSize,
            COLOR_WHITE, 1,
          );
          fragment.setDepth(200);

          // Scatter outward with random velocity
          const angle = Math.atan2(dy, dx) + (Math.random() - 0.5) * 1.2;
          const dist = 30 + Math.random() * 80;
          const targetX = px + Math.cos(angle) * dist;
          const targetY = py + Math.sin(angle) * dist;
          const duration = 500 + Math.random() * 500;
          const delay = Math.random() * 150;

          this.tweens.add({
            targets: fragment,
            x: targetX,
            y: targetY,
            alpha: 0,
            scaleX: 0,
            scaleY: 0,
            angle: (Math.random() - 0.5) * 360,
            duration: duration,
            delay: delay,
            ease: 'Quad.easeOut',
            onComplete: () => {
              fragment.destroy();
            },
          });

          fragments.push(fragment);
        }
      }

      // Wait for disintegration to finish, then clean up and call onComplete
      const maxAnimTime = 150 + 1000; // max delay + max duration
      this.time.delayedCall(maxAnimTime + 100, () => {
        // Safety: destroy any remaining fragments
        for (const f of fragments) {
          if (f && f.active) f.destroy();
        }

        // Destroy glow emitter
        if (glowEmitter && glowEmitter.active) {
          glowEmitter.destroy();
        }

        if (onComplete) onComplete();
      });
    });
  }

  // ---------------------------------------------------------------
  // Camera
  // ---------------------------------------------------------------

  /** @private Center camera and set bounds to maze dimensions. */
  _setupCamera() {
    const mazePixelW = this.mazeGridWidth * TILE_SIZE;
    const mazePixelH = this.mazeGridHeight * TILE_SIZE;

    // Viewport inside the decorative border and below the HUD
    const vpX = BORDER_MARGIN;
    const vpY = BORDER_MARGIN + HUD_HEIGHT;
    const vpW = GAME_WIDTH - BORDER_MARGIN * 2;
    const vpH = GAME_HEIGHT - BORDER_MARGIN * 2 - HUD_HEIGHT;

    this.cameras.main.setViewport(vpX, vpY, vpW, vpH);
    this.cameras.main.setBounds(0, 0, mazePixelW, mazePixelH);
    this.cameras.main.startFollow(this.player, true, 0.1, 0.1);
    // Snap camera to player immediately so there is no lerp lag after
    // level transitions, treasure-room entry/exit, or save restores.
    this.cameras.main.centerOn(this.player.x, this.player.y);
  }

  // ---------------------------------------------------------------
  // Zoom-Out Map View (Z key)
  // ---------------------------------------------------------------

  /**
   * Calculate the zoom level needed to fit the entire maze within
   * the camera viewport.
   * @private
   * @returns {number} Target zoom factor (< 1 means zoomed out).
   */
  _calculateMapZoom() {
    const mazePixelW = this.mazeGridWidth * TILE_SIZE;
    const mazePixelH = this.mazeGridHeight * TILE_SIZE;
    const vpW = GAME_WIDTH - BORDER_MARGIN * 2;
    const vpH = GAME_HEIGHT - BORDER_MARGIN * 2 - HUD_HEIGHT;

    // Fit the entire maze in the viewport with a small margin
    const zoomX = vpW / mazePixelW;
    const zoomY = vpH / mazePixelH;
    return Math.min(zoomX, zoomY) * 0.95; // 5% padding
  }

  /**
   * Begin the zoom-out transition: smoothly zoom the camera to show
   * the entire explored maze. Movement is blocked during zoom-out.
   * @private
   */
  _startZoomOut() {
    if (this._isZoomedOut || this._isZoomTransitioning) return;

    this._isZoomedOut = true;
    this._isZoomTransitioning = true;

    // Play zoom-out sound effect
    try { this.sound.play(AUDIO_ZOOM_OUT, { volume: getSfxVol(0.8) }); } catch (_) { /* audio optional */ }

    const cam = this.cameras.main;
    const targetZoom = this._calculateMapZoom();

    // Expand camera bounds so the zoomed-out view can show the full maze
    // (at lower zoom, the camera "sees" more, so bounds must accommodate that)
    const mazePixelW = this.mazeGridWidth * TILE_SIZE;
    const mazePixelH = this.mazeGridHeight * TILE_SIZE;
    const vpW = GAME_WIDTH - BORDER_MARGIN * 2;
    const vpH = GAME_HEIGHT - BORDER_MARGIN * 2 - HUD_HEIGHT;
    const visibleW = vpW / targetZoom;
    const visibleH = vpH / targetZoom;
    const padX = Math.max(0, (visibleW - mazePixelW) / 2);
    const padY = Math.max(0, (visibleH - mazePixelH) / 2);
    cam.setBounds(-padX, -padY, mazePixelW + padX * 2, mazePixelH + padY * 2);

    // Stop following the player so the camera centres on the maze
    cam.stopFollow();
    const centerX = mazePixelW / 2;
    const centerY = mazePixelH / 2;

    // Kill any existing zoom tween
    if (this._zoomTween) {
      this._zoomTween.stop();
      this._zoomTween = null;
    }

    // Redraw fog with enhanced visited-cell visibility
    this._drawFullFog();

    // Smooth zoom-out tween
    this._zoomTween = this.tweens.add({
      targets: cam,
      zoom: targetZoom,
      scrollX: centerX - (vpW / targetZoom) / 2,
      scrollY: centerY - (vpH / targetZoom) / 2,
      duration: ZOOM_OUT_DURATION,
      ease: 'Sine.easeInOut',
      onComplete: () => {
        this._isZoomTransitioning = false;
        this._zoomTween = null;
      },
    });
  }

  /**
   * End the zoom-out transition: smoothly zoom back to normal (1.0)
   * and resume following the player.
   * @private
   */
  _stopZoomOut() {
    if (!this._isZoomedOut) return;

    this._isZoomedOut = false;
    this._isZoomTransitioning = true;

    const cam = this.cameras.main;

    // Kill any existing zoom tween
    if (this._zoomTween) {
      this._zoomTween.stop();
      this._zoomTween = null;
    }

    // Target scroll: centre on the player
    const playerX = this.player ? this.player.x : this.playerStartCol * TILE_SIZE;
    const playerY = this.player ? this.player.y : this.playerStartRow * TILE_SIZE;
    const vpW = GAME_WIDTH - BORDER_MARGIN * 2;
    const vpH = GAME_HEIGHT - BORDER_MARGIN * 2 - HUD_HEIGHT;

    // Smooth zoom-in tween
    this._zoomTween = this.tweens.add({
      targets: cam,
      zoom: 1,
      scrollX: playerX - vpW / 2,
      scrollY: playerY - vpH / 2,
      duration: ZOOM_IN_DURATION,
      ease: 'Sine.easeInOut',
      onComplete: () => {
        this._isZoomTransitioning = false;
        this._zoomTween = null;

        // Restore normal camera bounds and follow
        const mazePixelW = this.mazeGridWidth * TILE_SIZE;
        const mazePixelH = this.mazeGridHeight * TILE_SIZE;
        cam.setBounds(0, 0, mazePixelW, mazePixelH);
        if (this.player) {
          cam.startFollow(this.player, true, 0.1, 0.1);
        }

        // Redraw fog with normal visited-cell alpha
        this._drawFullFog();
      },
    });
  }

  /**
   * Immediately cancel any zoom-out state without animation.
   * Used during pause, game over, level transition, and cleanup.
   * @private
   */
  _cancelZoomOut() {
    if (!this._isZoomedOut && !this._isZoomTransitioning) return;

    // Stop any running zoom tween
    if (this._zoomTween) {
      this._zoomTween.stop();
      this._zoomTween = null;
    }

    this._isZoomedOut = false;
    this._isZoomTransitioning = false;

    const cam = this.cameras.main;
    cam.setZoom(1);

    // Restore normal camera bounds and follow
    const mazePixelW = this.mazeGridWidth * TILE_SIZE;
    const mazePixelH = this.mazeGridHeight * TILE_SIZE;
    cam.setBounds(0, 0, mazePixelW, mazePixelH);
    if (this.player) {
      cam.startFollow(this.player, true, 0.1, 0.1);
    }

    // Redraw fog with normal visited-cell alpha
    this._drawFullFog();
  }

  // ---------------------------------------------------------------
  // Game Background Music
  // ---------------------------------------------------------------

  /**
   * Start the game background music (looped).
   * Respects the user's music preference.
   * Rotates to a new random track every 2 levels.
   * @private
   */
  _startGameMusic() {
    if (!this.musicEnabled) return;

    // Stop any existing instance first
    this._stopGameMusic();

    // Rotate to a new random track every 2 levels
    if (this.level - this._gameMusicLastChangeLevel >= 2) {
      let next;
      do {
        next = Math.floor(Math.random() * AUDIO_GAME_BG_COUNT) + 1;
      } while (next === this._gameMusicTrack && AUDIO_GAME_BG_COUNT > 1);
      this._gameMusicTrack = next;
      this._gameMusicLastChangeLevel = this.level;
    }

    const trackKey = `${AUDIO_GAME_BG}-${this._gameMusicTrack}`;
    this.gameMusic = this.sound.add(trackKey, {
      loop: true,
      volume: getMusicVol(0.35),
    });
    this.gameMusic.play();
  }

  /**
   * Stop the game background music immediately.
   * @private
   */
  _stopGameMusic() {
    if (this.gameMusic) {
      if (this.gameMusic.isPlaying) {
        this.gameMusic.stop();
      }
      this.gameMusic.destroy();
      this.gameMusic = null;
    }
  }

  // ---------------------------------------------------------------
  // Sound Effects (heart loss, water walk)
  // ---------------------------------------------------------------

  /**
   * Play the heart-loss sound effect once (when the player loses a life).
   * @private
   */
  _playHeartLossSound() {
    if (!this.sound) return;
    const sfx = this.sound.add(AUDIO_HEART_LOSS, { volume: getSfxVol(0.55) });
    sfx.play();
    sfx.once('complete', () => sfx.destroy());
  }

  /**
   * Play the water-walk sound effect if not already playing.
   * Called each time the player steps on a water tile; the sound
   * continues looping until _stopWaterWalkSound() is called.
   * @private
   */
  _playWaterWalkSound() {
    // Already playing — nothing to do
    if (this._waterWalkSound && this._waterWalkSound.isPlaying) return;

    if (!this.sound) return;

    this._waterWalkSound = this.sound.add(AUDIO_WATER_WALK, {
      loop: true,
      volume: getSfxVol(0.4),
    });
    this._waterWalkSound.play();
  }

  /**
   * Stop the water-walk sound immediately.
   * @private
   */
  _stopWaterWalkSound() {
    if (this._waterWalkSound) {
      if (this._waterWalkSound.isPlaying) {
        this._waterWalkSound.stop();
      }
      this._waterWalkSound.destroy();
      this._waterWalkSound = null;
    }
  }

  // ---------------------------------------------------------------
  // Lava Walk Sound & Visual Effects
  // ---------------------------------------------------------------

  /**
   * Play the lava-walk sound effect if not already playing.
   * Called each time the player steps on a lava tile; the sound
   * continues looping until _stopLavaWalkSound() is called.
   * @private
   */
  _playLavaWalkSound() {
    // Already playing — nothing to do
    if (this._lavaWalkSound && this._lavaWalkSound.isPlaying) return;
    if (!this.sound) return;

    this._lavaWalkSound = this.sound.add(AUDIO_LAVA_WALK, {
      loop: true,
      volume: getSfxVol(0.35),
    });
    this._lavaWalkSound.play();
  }

  /**
   * Stop the lava-walk sound immediately.
   * @private
   */
  _stopLavaWalkSound() {
    if (this._lavaWalkSound) {
      if (this._lavaWalkSound.isPlaying) {
        this._lavaWalkSound.stop();
      }
      this._lavaWalkSound.destroy();
      this._lavaWalkSound = null;
    }
  }

  // ---------------------------------------------------------------
  // Spike Walk Sound
  // ---------------------------------------------------------------

  /**
   * Play the spike-walk sound effect if not already playing.
   * Called each time the player steps on a spike tile; the sound
   * continues looping until _stopSpikeWalkSound() is called.
   * @private
   */
  _playSpikeWalkSound() {
    // Already playing — nothing to do
    if (this._spikeWalkSound && this._spikeWalkSound.isPlaying) return;
    if (!this.sound) return;

    this._spikeWalkSound = this.sound.add(AUDIO_SPIKE_WALK, {
      loop: true,
      volume: getSfxVol(0.35),
    });
    this._spikeWalkSound.play();
  }

  /**
   * Stop the spike-walk sound immediately.
   * @private
   */
  _stopSpikeWalkSound() {
    if (this._spikeWalkSound) {
      if (this._spikeWalkSound.isPlaying) {
        this._spikeWalkSound.stop();
      }
      this._spikeWalkSound.destroy();
      this._spikeWalkSound = null;
    }
  }

  /**
   * Play the poison-walk sound effect if not already playing.
   * Called each time the player steps on a poison tile; the sound
   * continues looping until _stopPoisonWalkSound() is called.
   * @private
   */
  _playPoisonWalkSound() {
    // Already playing — nothing to do
    if (this._poisonWalkSound && this._poisonWalkSound.isPlaying) return;
    if (!this.sound) return;

    this._poisonWalkSound = this.sound.add(AUDIO_POISON_WALK, {
      loop: true,
      volume: getSfxVol(0.35),
    });
    this._poisonWalkSound.play();
  }

  /**
   * Stop the poison-walk sound immediately.
   * @private
   */
  _stopPoisonWalkSound() {
    if (this._poisonWalkSound) {
      if (this._poisonWalkSound.isPlaying) {
        this._poisonWalkSound.stop();
      }
      this._poisonWalkSound.destroy();
      this._poisonWalkSound = null;
    }
  }

  /**
   * Start lava visual effects on the player: a subtle reddish glow
   * and smoke particles trailing behind.
   * Only creates emitters if they don't already exist (idempotent).
   * @private
   */
  _startLavaEffects() {
    if (!this.player) return;

    // --- Burning rim sprite (red/orange ring around the player) ---
    if (!this._lavaRimSprite) {
      this._lavaRimSprite = this.add.sprite(this.player.x, this.player.y, TEX_LAVA_RIM);
      this._lavaRimSprite.setDepth(this.player.depth - 1); // behind the player
      this._lavaRimSprite.setAlpha(0.9);

      // Gentle scale pulse — never touches alpha so the player stays fully opaque
      this._lavaRimTween = this.tweens.add({
        targets: this._lavaRimSprite,
        scaleX: { from: 1.0, to: 1.1 },
        scaleY: { from: 1.0, to: 1.1 },
        duration: 500,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    }

    // --- Smoke emitter (gray particles drifting upward behind the player) ---
    if (!this._lavaSmokeEmitter) {
      this._lavaSmokeEmitter = this.add.particles(this.player.x, this.player.y, TEX_SMOKE, {
        follow: this.player,
        followOffset: { x: 0, y: 2 },
        lifespan: { min: 400, max: 800 },
        speed: { min: 10, max: 30 },
        angle: { min: 250, max: 290 }, // Drift upward (270 = straight up, +/- 20 for spread)
        scale: { start: 0.6, end: 0.1 },
        alpha: { start: 0.5, end: 0 },
        frequency: 60,
        quantity: 1,
        emitting: true,
      });
      this._lavaSmokeEmitter.setDepth(this.player.depth + 2);
    }
  }

  /**
   * Stop and destroy lava visual effects (glow and smoke emitters).
   * @private
   */
  _stopLavaEffects() {
    if (this._lavaRimTween) {
      this._lavaRimTween.stop();
      this._lavaRimTween = null;
    }

    if (this._lavaRimSprite) {
      this._lavaRimSprite.destroy();
      this._lavaRimSprite = null;
    }

    if (this._lavaSmokeEmitter) {
      this._lavaSmokeEmitter.stop();
      this._lavaSmokeEmitter.destroy();
      this._lavaSmokeEmitter = null;
    }
  }

  // ---------------------------------------------------------------
  // Hazard Idle Drain (passive damage while standing on water/lava)
  // ---------------------------------------------------------------

  /**
   * Start a repeating timer that drains health while the player
   * stands still on a water tile.
   * @private
   */
  _startWaterIdleDrain() {
    if (this._waterIdleDrainTimer) return; // already running
    this._waterIdleDrainTimer = this.time.addEvent({
      delay: WATER_IDLE_DRAIN_INTERVAL,
      loop: true,
      callback: () => {
        if (!this.healthSystem || !this.player) return;
        // Re-check immunity each tick (ring may have broken since timer started)
        const stillImmune = isElementalRingActive() || isItemActive(SHOP_ITEM_WATER_RING);
        if (stillImmune) {
          // Ring is protecting — degrade durability instead of draining health
          this._degradeRingForHazard(isElementalRingActive(), SHOP_ITEM_WATER_RING);
          return;
        }
        for (let i = 0; i < WATER_IDLE_DRAIN_AMOUNT; i++) {
          this.healthSystem.drainOnMove(1);
          if (!this.player) return; // game over may have fired
        }
      },
    });
  }

  /**
   * Stop the water idle drain timer.
   * @private
   */
  _stopWaterIdleDrain() {
    if (this._waterIdleDrainTimer) {
      this._waterIdleDrainTimer.remove(false);
      this._waterIdleDrainTimer = null;
    }
    this._onWaterTile = false;
  }

  /**
   * Start a repeating timer that drains health while the player
   * stands still on a lava tile.
   * @private
   */
  _startLavaIdleDrain() {
    if (this._lavaIdleDrainTimer) return; // already running
    this._lavaIdleDrainTimer = this.time.addEvent({
      delay: LAVA_IDLE_DRAIN_INTERVAL,
      loop: true,
      callback: () => {
        if (!this.healthSystem || !this.player) return;
        // Re-check immunity each tick (ring may have broken since timer started)
        const stillImmune = isElementalRingActive() || isItemActive(SHOP_ITEM_LAVA_RING);
        if (stillImmune) {
          // Ring is protecting — degrade durability instead of draining health
          this._degradeRingForHazard(isElementalRingActive(), SHOP_ITEM_LAVA_RING);
          return;
        }
        for (let i = 0; i < LAVA_IDLE_DRAIN_AMOUNT; i++) {
          this.healthSystem.drainOnMove(1);
          if (!this.player) return; // game over may have fired
        }
      },
    });
  }

  /**
   * Stop the lava idle drain timer.
   * @private
   */
  _stopLavaIdleDrain() {
    if (this._lavaIdleDrainTimer) {
      this._lavaIdleDrainTimer.remove(false);
      this._lavaIdleDrainTimer = null;
    }
    this._onLavaTile = false;
  }

  // ---------------------------------------------------------------
  // Poison Status Effect
  // ---------------------------------------------------------------

  /**
   * Apply the poison status effect to the player. Shows a floating
   * "Poisoned!" label, slows movement, starts passive health drain,
   * and schedules automatic removal after POISON_DURATION ms.
   * @private
   */
  _applyPoison() {
    if (this._isPoisoned) return;
    this._isPoisoned = true;

    // Emit event so HUD can show poison glow on health bar
    this.eventBus.emit(Events.PLAYER_POISONED);

    // Slow the player: double the move duration
    if (this.player) {
      this.player.moveDuration = PLAYER_MOVE_DURATION * POISON_SPEED_MULTIPLIER;
    }

    // Show floating "Poisoned!" text at the player's position
    this._showPoisonedLabel();

    // Start green rim visual effect around the player
    this._startPoisonEffects();

    // Start passive drain timer — drains POISON_DRAIN_AMOUNT every POISON_DRAIN_INTERVAL ms
    this._poisonDrainTimer = this.time.addEvent({
      delay: POISON_DRAIN_INTERVAL,
      loop: true,
      callback: () => {
        if (!this.healthSystem) return;
        for (let i = 0; i < POISON_DRAIN_AMOUNT; i++) {
          this.healthSystem.drainOnMove(1);
          // drainOnMove may trigger game over, bail if cleanup happened
          if (!this.player) return;
        }
      },
    });

    // Schedule poison removal after POISON_DURATION ms
    this._poisonEndTimer = this.time.delayedCall(POISON_DURATION, () => {
      this._removePoison();
    });
  }

  /**
   * Remove the poison status effect: restore normal speed, stop
   * passive drain, and notify the HUD.
   * @private
   */
  _removePoison() {
    if (!this._isPoisoned) return;
    this._isPoisoned = false;

    // Restore movement speed — if still wounded, keep wounded speed; otherwise normal
    if (this.player) {
      if (this._isWounded) {
        this.player.moveDuration = PLAYER_MOVE_DURATION * SPIKE_SPEED_MULTIPLIER;
      } else {
        this.player.moveDuration = PLAYER_MOVE_DURATION;
      }
    }

    // Stop passive drain timer
    if (this._poisonDrainTimer) {
      this._poisonDrainTimer.remove(false);
      this._poisonDrainTimer = null;
    }

    // Cancel the end timer (if called early, e.g. from cleanup)
    if (this._poisonEndTimer) {
      this._poisonEndTimer.remove(false);
      this._poisonEndTimer = null;
    }

    // Stop green rim visual effect
    this._stopPoisonEffects();

    // Emit event so HUD removes poison glow
    this.eventBus.emit(Events.PLAYER_POISON_ENDED);
  }

  /**
   * Show a floating "You are poisoned" text at the player's current position.
   * The label appears at full size, holds briefly, then drifts upward and
   * fades out. Uses a greenish color and stroke to match the poison theme.
   * @private
   */
  _showPoisonedLabel() {
    if (!this.player) return;

    const px = this.player.x;
    const py = this.player.y;

    const label = this.add.text(px, py, 'You are poisoned', {
      fontFamily: '"Courier New", Courier, monospace',
      fontSize: '10px',
      color: '#55cc55',
      stroke: '#1a3a1a',
      strokeThickness: 3,
      fontStyle: 'bold',
    });
    label.setOrigin(0.5, 0.5);
    label.setDepth(150);
    label.setAlpha(1);

    // Hold visible briefly, then float up and fade out (no zoom)
    this.tweens.add({
      targets: label,
      y: label.y - TILE_SIZE * 1.2,
      alpha: 0,
      duration: 1200,
      delay: 400,
      ease: 'Quad.easeIn',
      onComplete: () => {
        if (label.active) label.destroy();
      },
    });
  }

  /**
   * Start poison visual effects on the player: a greenish glowing ring
   * that pulses around the player for the duration of the poison effect.
   * Only creates the rim sprite if it doesn't already exist (idempotent).
   * @private
   */
  _startPoisonEffects() {
    if (!this.player) return;

    if (!this._poisonRimSprite) {
      this._poisonRimSprite = this.add.sprite(this.player.x, this.player.y, TEX_POISON_RIM);
      this._poisonRimSprite.setDepth(this.player.depth - 1); // behind the player
      this._poisonRimSprite.setAlpha(0.9);

      // Gentle scale pulse — never touches alpha so the player stays fully opaque
      this._poisonRimTween = this.tweens.add({
        targets: this._poisonRimSprite,
        scaleX: { from: 1.0, to: 1.1 },
        scaleY: { from: 1.0, to: 1.1 },
        duration: 700,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    }
  }

  /**
   * Stop and destroy poison visual effects (green rim sprite and tween).
   * @private
   */
  _stopPoisonEffects() {
    if (this._poisonRimTween) {
      this._poisonRimTween.stop();
      this._poisonRimTween = null;
    }

    if (this._poisonRimSprite) {
      this._poisonRimSprite.destroy();
      this._poisonRimSprite = null;
    }
  }

  // ---------------------------------------------------------------
  // Wounded Status Effect (Spike Hazard)
  // ---------------------------------------------------------------

  /**
   * Apply the wounded status effect to the player. Shows a floating
   * "Wounded!" label, slows movement, starts passive health drain,
   * starts blood-red rim + blood trail effects, and schedules automatic
   * removal after SPIKE_WOUND_DURATION ms.
   * @private
   */
  _applyWounded() {
    if (this._isWounded) return;
    this._isWounded = true;

    // Emit event so HUD can show wounded glow on health bar
    this.eventBus.emit(Events.PLAYER_WOUNDED);

    // Slow the player: apply spike speed multiplier.
    // If also poisoned, the slower of the two wins (higher multiplier = slower).
    if (this.player) {
      const currentMult = this._isPoisoned ? POISON_SPEED_MULTIPLIER : 1;
      const newMult = Math.max(currentMult, SPIKE_SPEED_MULTIPLIER);
      this.player.moveDuration = PLAYER_MOVE_DURATION * newMult;
    }

    // Show floating "Wounded!" text at the player's position
    this._showWoundedLabel();

    // Start blood-red rim visual effect + blood trail around the player
    this._startWoundedEffects();

    // Start passive drain timer — drains SPIKE_DRAIN_AMOUNT every SPIKE_DRAIN_INTERVAL ms
    this._woundedDrainTimer = this.time.addEvent({
      delay: SPIKE_DRAIN_INTERVAL,
      loop: true,
      callback: () => {
        if (!this.healthSystem) return;
        for (let i = 0; i < SPIKE_DRAIN_AMOUNT; i++) {
          this.healthSystem.drainOnMove(1);
          // drainOnMove may trigger game over, bail if cleanup happened
          if (!this.player) return;
        }
      },
    });

    // Schedule wounded removal after SPIKE_WOUND_DURATION ms
    this._woundedEndTimer = this.time.delayedCall(SPIKE_WOUND_DURATION, () => {
      this._removeWounded();
    });
  }

  /**
   * Remove the wounded status effect: restore appropriate speed (accounting
   * for whether poison is still active), stop passive drain, and notify the HUD.
   * @private
   */
  _removeWounded() {
    if (!this._isWounded) return;
    this._isWounded = false;

    // Restore movement speed — if still poisoned, keep poison speed; otherwise normal
    if (this.player) {
      if (this._isPoisoned) {
        this.player.moveDuration = PLAYER_MOVE_DURATION * POISON_SPEED_MULTIPLIER;
      } else {
        this.player.moveDuration = PLAYER_MOVE_DURATION;
      }
    }

    // Stop passive drain timer
    if (this._woundedDrainTimer) {
      this._woundedDrainTimer.remove(false);
      this._woundedDrainTimer = null;
    }

    // Cancel the end timer (if called early, e.g. from cleanup)
    if (this._woundedEndTimer) {
      this._woundedEndTimer.remove(false);
      this._woundedEndTimer = null;
    }

    // Stop blood-red rim visual effect + blood trail
    this._stopWoundedEffects();

    // Emit event so HUD removes wounded glow
    this.eventBus.emit(Events.PLAYER_WOUND_ENDED);
  }

  /**
   * Show a floating "You are bleeding" text at the player's current position.
   * The label appears at full size, holds briefly, then drifts upward and
   * fades out. Uses a blood-red color and stroke to match the wound theme.
   * @private
   */
  _showWoundedLabel() {
    if (!this.player) return;

    const px = this.player.x;
    const py = this.player.y;

    const label = this.add.text(px, py, 'You are bleeding', {
      fontFamily: '"Courier New", Courier, monospace',
      fontSize: '10px',
      color: '#cc4444',
      stroke: '#3a1010',
      strokeThickness: 3,
      fontStyle: 'bold',
    });
    label.setOrigin(0.5, 0.5);
    label.setDepth(150);
    label.setAlpha(1);

    // Hold visible briefly, then float up and fade out (no zoom)
    this.tweens.add({
      targets: label,
      y: label.y - TILE_SIZE * 1.2,
      alpha: 0,
      duration: 1200,
      delay: 400,
      ease: 'Quad.easeIn',
      onComplete: () => {
        if (label.active) label.destroy();
      },
    });
  }

  /**
   * Start wounded visual effects on the player: a blood-red glowing ring
   * that pulses around the player, and a blood trail particle emitter that
   * drops small red droplets while the player is moving.
   * Only creates the rim sprite if it doesn't already exist (idempotent).
   * @private
   */
  _startWoundedEffects() {
    if (!this.player) return;

    // Blood-red rim sprite (similar to poison rim but red)
    if (!this._spikeRimSprite) {
      this._spikeRimSprite = this.add.sprite(this.player.x, this.player.y, TEX_SPIKE_RIM);
      this._spikeRimSprite.setDepth(this.player.depth - 1); // behind the player
      this._spikeRimSprite.setAlpha(0.9);

      // Gentle scale pulse — never touches alpha so the player stays fully opaque
      this._spikeRimTween = this.tweens.add({
        targets: this._spikeRimSprite,
        scaleX: { from: 1.0, to: 1.1 },
        scaleY: { from: 1.0, to: 1.1 },
        duration: 600,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    }

    // Blood trail emitter — drops small red particles while moving
    if (!this._bloodTrailEmitter) {
      this._bloodTrailEmitter = this.add.particles(
        this.player.x, this.player.y, TEX_BLOOD, {
          lifespan: { min: 2000, max: 3000 },
          speed: { min: 2, max: 8 },
          angle: { min: 0, max: 360 },
          scale: { start: 0.8, end: 0.3 },
          alpha: { start: 0.9, end: 0 },
          frequency: 180,
          quantity: 1,
          emitting: false, // controlled by update() based on player movement
        },
      );
      this._bloodTrailEmitter.setDepth(this.player.depth - 1);
    }
  }

  /**
   * Stop and destroy wounded visual effects (spike rim sprite, tween,
   * and blood trail emitter).
   * @private
   */
  _stopWoundedEffects() {
    if (this._spikeRimTween) {
      this._spikeRimTween.stop();
      this._spikeRimTween = null;
    }

    if (this._spikeRimSprite) {
      this._spikeRimSprite.destroy();
      this._spikeRimSprite = null;
    }

    if (this._bloodTrailEmitter) {
      this._bloodTrailEmitter.stop();
      this._bloodTrailEmitter.destroy();
      this._bloodTrailEmitter = null;
    }
  }

  /**
   * Build a combined Set of forbidden cell positions where food and coins
   * must not spawn. Includes water, lava, and poison tiles.
   * @private
   * @returns {Set<string>}
   */
  _getCombinedForbiddenCells() {
    const forbidden = new Set();

    if (this.waterSystem) {
      for (const key of this.waterSystem.waterCells) {
        forbidden.add(key);
      }
    }

    if (this.lavaSystem) {
      for (const key of this.lavaSystem.lavaCells) {
        forbidden.add(key);
      }
    }

    if (this.poisonSystem) {
      for (const key of this.poisonSystem.poisonCells) {
        forbidden.add(key);
      }
    }

    if (this.spikeSystem) {
      for (const key of this.spikeSystem.spikeCells) {
        forbidden.add(key);
      }
    }

    // Chest cells are also forbidden (items should not spawn on chests)
    if (this.chestSpawner) {
      for (const key of this.chestSpawner.getChestCells()) {
        forbidden.add(key);
      }
    }

    return forbidden;
  }

  // ---------------------------------------------------------------
  // Idle Rest / Sleep System
  // ---------------------------------------------------------------

  /**
   * Enter the resting state: floating Z letters and slow health recovery.
   * Called when the player has been idle for REST_IDLE_DELAY ms,
   * health is below maximum, and the player has score points to spend.
   * Each heal tick costs REST_SCORE_COST_PER_HEAL points; resting stops
   * automatically when health is full or score runs out.
   * @private
   */
  _startResting() {
    if (this._isResting) return;
    this._isResting = true;

    // Notify HUD to start health bar glow
    this.eventBus.emit(Events.PLAYER_RESTING);

    // Start recover-life sound — plays once every second while resting
    this._restHealSound = this.sound.add(AUDIO_RECOVER_LIFE, { volume: getSfxVol(0.25) });
    this._restHealSound.play();
    this._restSoundTimer = this.time.addEvent({
      delay: 1000,
      loop: true,
      callback: () => {
        if (this._restHealSound && this._isResting) {
          this._restHealSound.play();
        }
      },
    });

    // Start heal timer — slowly restore health each tick, consuming score
    this._restHealTimer = this.time.addEvent({
      delay: REST_HEAL_INTERVAL,
      loop: true,
      callback: () => {
        if (!this.healthSystem || !this.scoreSystem) return;

        // Deduct score cost; if score is already 0, stop resting
        const deducted = this.scoreSystem.deductPoints(REST_SCORE_COST_PER_HEAL);
        if (!deducted) {
          this._stopResting();
          return;
        }

        this.healthSystem.heal(REST_HEAL_AMOUNT);

        // If health is now full, stop resting
        if (this.healthSystem.health >= this.healthSystem.maxHealth) {
          this._stopResting();
        }
      },
    });

    // Start Z letter spawner — spawn floating Z/z letters periodically
    this._restZTimer = this.time.addEvent({
      delay: REST_Z_SPAWN_INTERVAL,
      loop: true,
      callback: () => {
        this._spawnZLetter();
      },
    });

    // Spawn the first Z immediately
    this._spawnZLetter();
  }

  /**
   * Exit the resting state: destroy timers and all floating Z letters,
   * notify HUD to stop the health bar glow.
   * @private
   */
  _stopResting() {
    if (!this._isResting) return;
    this._isResting = false;

    // Stop recover-life sound timer and instance
    if (this._restSoundTimer) {
      this._restSoundTimer.remove(false);
      this._restSoundTimer = null;
    }
    if (this._restHealSound) {
      if (this._restHealSound.isPlaying) {
        this._restHealSound.stop();
      }
      this._restHealSound.destroy();
      this._restHealSound = null;
    }

    // Destroy heal timer
    if (this._restHealTimer) {
      this._restHealTimer.remove(false);
      this._restHealTimer = null;
    }

    // Destroy Z spawn timer
    if (this._restZTimer) {
      this._restZTimer.remove(false);
      this._restZTimer = null;
    }

    // Destroy all floating Z texts immediately
    for (const zObj of this._restZTexts) {
      if (zObj && zObj.text && zObj.text.active) {
        // Stop any running tween on this text
        this.tweens.killTweensOf(zObj.text);
        zObj.text.destroy();
      }
    }
    this._restZTexts = [];

    // Notify HUD to stop health bar glow
    this.eventBus.emit(Events.PLAYER_REST_STOPPED);
  }

  /**
   * Spawn a single floating "z" or "Z" letter at the player's position.
   * The letter drifts upward with slight horizontal sway, fades in then out.
   * Enforces the REST_Z_MAX_VISIBLE limit by destroying the oldest letter
   * when the cap would be exceeded.
   * @private
   */
  _spawnZLetter() {
    if (!this.player || !this._isResting) return;

    // Enforce max visible limit — remove oldest if at cap
    while (this._restZTexts.length >= REST_Z_MAX_VISIBLE) {
      const oldest = this._restZTexts.shift();
      if (oldest && oldest.text && oldest.text.active) {
        this.tweens.killTweensOf(oldest.text);
        oldest.text.destroy();
      }
    }

    // Alternate between small "z" and large "Z" for comic-style sleep effect
    const isUpperCase = this._restZTexts.length % 2 === 0;
    const letter = isUpperCase ? 'Z' : 'z';
    const fontSize = isUpperCase ? '16px' : '12px';

    // Spawn at the player's world position, offset slightly above
    const px = this.player.x + (Math.random() - 0.5) * 10;
    const py = this.player.y - 10;

    const zText = this.add.text(px, py, letter, {
      fontFamily: '"Courier New", Courier, monospace',
      fontSize: fontSize,
      fontStyle: 'bold',
      color: '#ffffff',
      stroke: '#000000',
      strokeThickness: 3,
    });
    zText.setOrigin(0.5);
    zText.setDepth(110); // Above fog (~depth 100) but below collection labels (~depth 150)
    zText.setAlpha(0);

    // Random horizontal drift
    const driftX = (Math.random() - 0.5) * 24;
    const driftY = -(30 + Math.random() * 20); // Float upward 30-50px

    // Track this Z
    const zEntry = { text: zText };
    this._restZTexts.push(zEntry);

    // Animation: fade in quickly, drift up + sideways, then fade out
    // Phase 1: Fade in (200ms)
    this.tweens.add({
      targets: zText,
      alpha: 0.9,
      duration: 200,
      ease: 'Quad.easeOut',
      onComplete: () => {
        if (!zText.active) return;

        // Phase 2: Drift upward + fade out (1300ms)
        this.tweens.add({
          targets: zText,
          x: px + driftX,
          y: py + driftY,
          alpha: 0,
          duration: 1300,
          ease: 'Quad.easeIn',
          onComplete: () => {
            // Remove from tracking array
            const idx = this._restZTexts.indexOf(zEntry);
            if (idx !== -1) {
              this._restZTexts.splice(idx, 1);
            }
            if (zText.active) {
              zText.destroy();
            }
          },
        });
      },
    });
  }

  // ---------------------------------------------------------------
  // Save / Cleanup
  // ---------------------------------------------------------------

  /** @private Persist current state to localStorage. */
  _autoSave() {
    // Never save while inside the treasure room — if the player quits
    // mid-bonus, Continue should restore the normal level state instead.
    if (this._inTreasureRoom) return;

    // Never save during the final battle — the endgame is a one-shot event.
    if (this._finalBattleSystem && this._finalBattleSystem.isActive) return;

    const pos = this.player.getGridPosition();
    saveGame({
      level: this.level,
      mazeSeed: this.mazeSeed,
      mazeCols: this.mazeGridWidth,
      mazeRows: this.mazeGridHeight,
      playerCol: pos.col,
      playerRow: pos.row,
      health: this.healthSystem.health,
      lives: this.healthSystem.lives,
      score: this.scoreSystem ? this.scoreSystem.getScore() : 0,
      coinsCollected: this.coinsCollected,
      visited: this.visited,
      playerKeys: this._playerKeys,
      agmuthCount: this._agmuthCount,
      hasCompass: this._hasCompass,
      hasThreiaTotem: this._hasThreiaTotem,
      threiaBlessingUsed: this._threiaBlessingUsed,
      chests: this.chestSpawner ? this.chestSpawner.serialize() : [],
      hammerCharges: this._hammerCharges,
      brokenWalls: this._hammerSystem ? this._hammerSystem.getBrokenWallsForSave() : [],
      hasGoldenExit: this._hasGoldenExit,
      goldenExitPos: this._goldenExitPos,
      goldenExitSpent: this._goldenExitSpent,
      gameMode: this._gameMode,
      explorerContinuesUsed: this._explorerContinuesUsed,
      ...(this._narratorSystem ? this._narratorSystem.serialize() : {}),
      levelEvent: this._levelEventSystem ? this._levelEventSystem.serialize() : LevelEvent.NONE,
      gameElapsedTime: this._gameElapsedBeforeSave + (Date.now() - this._gameStartTime) / 1000,
      achGhostUses: this._achGhostUses,
      achWallsBroken: this._achWallsBroken,
      achChestsOpened: this._achChestsOpened,
      achBlessingsTotal: this._achBlessingsTotal,
      movingWalls: this._movingWallSystem ? this._movingWallSystem.serialize() : null,
      secretPassages: this._secretPassageSystem ? this._secretPassageSystem.serialize() : null,
      batNests: this._batNestSystem ? this._batNestSystem.serialize() : null,
      minimapVisible: this._minimapVisible,
      playerTrail: this._playerTrail,
    });
  }

  /** @private Clean up game objects before level transition or game over. */
  _cleanup() {
    // Cancel zoom-out if active
    this._cancelZoomOut();

    // --- Treasure room cleanup ---
    this._destroyGoldenExitMarker();
    this._stopTreasureMusic();
    if (this._treasureTimerEvent) {
      this._treasureTimerEvent.remove(false);
      this._treasureTimerEvent = null;
    }
    if (this._treasureTimerText) {
      this._treasureTimerText.destroy();
      this._treasureTimerText = null;
    }

    // Remove keydown listeners to prevent accumulation on scene restart
    if (this.input && this.input.keyboard) {
      if (this._onKeydownO) this.input.keyboard.off('keydown-O', this._onKeydownO);
      if (this._onKeydownA) this.input.keyboard.off('keydown-A', this._onKeydownA);
      if (this._onKeydownH) this.input.keyboard.off('keydown-H', this._onKeydownH);
      if (this._onKeydownB) this.input.keyboard.off('keydown-B', this._onKeydownB);
      if (this._onKeydownI) this.input.keyboard.off('keydown-I', this._onKeydownI);
      if (this._onKeydownESC) this.input.keyboard.off('keydown-ESC', this._onKeydownESC);
      if (this._onKeydownTAB) this.input.keyboard.off('keydown-TAB', this._onKeydownTAB);
    }

    // Clean up info modal if it's showing
    if (this._isInfoOpen) {
      this._destroyInfoModal();
    }

    // Deactivate ghost mode if active
    if (this._isGhostMode) {
      this._isGhostMode = false;
      if (this.player) {
        this.player.stopGhostMode();
      }
    }

    // Reset voracious totem tracking set to prevent unbounded growth
    this._voraciousTrackedIds = null;

    // Clean up pause UI if it's showing
    if (this._isPaused) {
      this._hidePauseMenu();
      this._isPaused = false;
    }

    if (this.foodSpawner) {
      this.foodSpawner.stop();
      this.foodSpawner = null;
    }

    if (this.foodGroup) {
      try { this.foodGroup.clear(true, true); } catch (_) { /* physics world may be gone */ }
      this.foodGroup = null;
    }

    if (this.coinSpawner) {
      this.coinSpawner.stop();
      this.coinSpawner = null;
    }

    if (this.coinGroup) {
      try { this.coinGroup.clear(true, true); } catch (_) { /* physics world may be gone */ }
      this.coinGroup = null;
    }

    if (this.keySpawner) {
      this.keySpawner.stop();
      this.keySpawner = null;
    }

    if (this.keyGroup) {
      try { this.keyGroup.clear(true, true); } catch (_) { /* physics world may be gone */ }
      this.keyGroup = null;
    }

    if (this.chestSpawner) {
      this.chestSpawner.destroy();
      this.chestSpawner = null;
    }

    if (this.lootGroup) {
      try { this.lootGroup.clear(true, true); } catch (_) { /* physics world may be gone */ }
      this.lootGroup = null;
    }

    // Clean up manuscript item and group
    if (this._manuscriptItem) {
      this._manuscriptItem.destroy();
      this._manuscriptItem = null;
    }
    if (this._manuscriptGroup) {
      try { this._manuscriptGroup.clear(true, true); } catch (_) { /* physics world may be gone */ }
      this._manuscriptGroup = null;
    }
    this._manuscriptId = null;

    // Clean up compass item and group
    if (this._compassItem) {
      this._compassItem.destroy();
      this._compassItem = null;
    }
    if (this._compassRef) {
      this._compassRef.item = null;
      this._compassRef = null;
    }
    if (this._compassGroup) {
      try { this._compassGroup.clear(true, true); } catch (_) { /* physics world may be gone */ }
      this._compassGroup = null;
    }
    this._hasCompass = false;

    // Clean up Threia Totem item and group
    if (this._threiaTotemItem) {
      this._threiaTotemItem.destroy();
      this._threiaTotemItem = null;
    }
    if (this._threiaTotemRef) {
      this._threiaTotemRef.item = null;
      this._threiaTotemRef = null;
    }
    if (this._threiaTotemGroup) {
      try { this._threiaTotemGroup.clear(true, true); } catch (_) { /* physics world may be gone */ }
      this._threiaTotemGroup = null;
    }
    // Note: _hasThreiaTotem persists across levels, only reset on new game

    // Clean up Threia prayer system
    if (this._threiaSystem) {
      this._threiaSystem.destroy();
      this._threiaSystem = null;
    }

    // Clean up bat system
    if (this._batSystem) {
      this._batSystem.destroy();
      this._batSystem = null;
    }

    // Clean up level event system timers (preserves event state)
    if (this._levelEventSystem) {
      this._levelEventSystem.cleanup();
    }

    // Clean up ambient sound system
    if (this._ambientSoundSystem) {
      this._ambientSoundSystem.destroy();
      this._ambientSoundSystem = null;
    }

    // Clean up moving wall system
    if (this._movingWallSystem) {
      this._movingWallSystem.destroy();
      this._movingWallSystem = null;
    }

    // Clean up secret passage system
    if (this._secretPassageSystem) {
      this._secretPassageSystem.destroy();
      this._secretPassageSystem = null;
    }

    // Clean up shadow walker system
    if (this._shadowWalkerSystem) {
      this._shadowWalkerSystem.destroy();
      this._shadowWalkerSystem = null;
    }

    // Clean up bat nest system
    if (this._batNestSystem) {
      this._batNestSystem.destroy();
      this._batNestSystem = null;
    }

    // Clean up final battle system — but ONLY if it's not actively running.
    // During the final battle, _cleanup() is called by _cleanupOldLevel() to
    // tear down the normal maze. If we destroy the FinalBattleSystem here, all
    // its tracked elements (overlays, tweens, timers) are killed mid-transition,
    // which is why Phase 3 and Phase 5 effects were invisible at runtime.
    if (this._finalBattleSystem && !this._finalBattleSystem.isActive) {
      this._finalBattleSystem.cleanup();
      this._finalBattleSystem = null;
    }

    // Clean up hammer item and group
    if (this._hammerItem) {
      this._hammerItem.destroy();
      this._hammerItem = null;
    }
    if (this._hammerGroup) {
      try { this._hammerGroup.clear(true, true); } catch (_) { /* physics world may be gone */ }
      this._hammerGroup = null;
    }

    // Clean up hammer system (cancels regen timers, destroys debris)
    if (this._hammerSystem) {
      this._hammerSystem.destroy();
      this._hammerSystem = null;
    }

    if (this.waterSystem) {
      this.waterSystem.destroy();
      this.waterSystem = null;
    }

    if (this.lavaSystem) {
      this.lavaSystem.destroy();
      this.lavaSystem = null;
    }

    if (this.poisonSystem) {
      this.poisonSystem.destroy();
      this.poisonSystem = null;
    }

    if (this.spikeSystem) {
      this.spikeSystem.destroy();
      this.spikeSystem = null;
    }

    // Remove poison status effect if active
    if (this._isPoisoned) {
      this._removePoison();
    }

    // Remove wounded status effect if active
    if (this._isWounded) {
      this._removeWounded();
    }

    // Stop resting if player was idle-resting (before player destruction)
    if (this._isResting) {
      this._stopResting();
    }

    if (this.wallGroup) {
      try { this.wallGroup.clear(true, true); } catch (_) { /* physics world may be gone */ }
      this.wallGroup = null;
    }

    if (this.player) {
      this.player.destroy();
      this.player = null;
    }

    if (this.exitMarker) {
      this.exitMarker.destroy();
      this.exitMarker = null;
    }

    if (this.fogGraphics) {
      this.fogGraphics.destroy();
      this.fogGraphics = null;
    }

    // Clean up direction trail graphics
    if (this._trailGraphics) {
      this._trailGraphics.destroy();
      this._trailGraphics = null;
    }

    // Stop game background music
    this._stopGameMusic();

    // Stop water walk sound
    this._stopWaterWalkSound();

    // Stop lava walk sound and effects
    this._stopLavaWalkSound();
    this._stopLavaEffects();

    // Stop spike walk sound
    this._stopSpikeWalkSound();

    // Stop poison walk sound
    this._stopPoisonWalkSound();

    // Stop poison visual effects
    this._stopPoisonEffects();

    // Stop wounded visual effects (spike rim + blood trail)
    this._stopWoundedEffects();

    // Stop hazard idle drain timers
    this._stopWaterIdleDrain();
    this._stopLavaIdleDrain();
  }

  /** Called when the scene shuts down (via Phaser lifecycle). */
  _onShutdown() {
    // Force-cleanup the final battle system on full shutdown (scene is going
    // away), since _cleanup() skips it while it's active.
    if (this._finalBattleSystem) {
      this._finalBattleSystem.cleanup();
      this._finalBattleSystem = null;
    }
    this._cleanup();
    if (this.eventBus) {
      this.eventBus.removeAllListeners();
    }
  }
}
